import { DESKTOP_PASTE_AS_TEXT_EVENT } from "../../lib/desktopPasteAsText";
import { useEnvironmentMachine, usePrimaryEnvironmentId } from "../../state/environments";
import { runtimeModeConfig, runtimeModeOptions } from "./runtimeModeConfig";
import { useRightPanelStore } from "~/rightPanelStore";
import { AttachmentFilePreview } from "../files/AttachmentFilePreview";
import { Dialog, DialogPopup, DialogTitle } from "../ui/dialog";
import { filterComposerPullRequestMatches } from "@afar-research/shared/composerPullRequestMatches";
import { importPastedComposerText, readPastedComposerContext } from "../composerInlineTokenPaste";
import { elementContextToPreviewAnnotation } from "../../lib/elementContext";
import { RefreshIcon } from "~/components/ui/refresh-icon";
import {
  questionAttachmentDraftId,
  countQuestionAttachments,
  useQuestionAttachmentPreparation,
  changeQuestionAttachmentPreparation,
} from "../../questionAttachments";
import type {
  ApprovalRequestId,
  DesignDeleteTarget,
  DesignSystemMention,
  DesignSystemMentionTarget,
  KeybindingCommand,
  AssistantCitation,
  ChatFileAttachment,
  EnvironmentId,
  ModelSelection,
  ProjectId,
  PullRequestListInput,
  PreviewAnnotationPayload,
  ProviderApprovalDecision,
  ProviderInteractionMode,
  ResolvedKeybindingsConfig,
  RuntimeMode,
  ScopedThreadRef,
  ServerProvider,
  ThreadId,
  SnapShotSource,
  ThreadActionSelection,
} from "@afar-research/contracts";
import {
  ProviderDriverKind,
  ProviderInstanceId,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
  PROVIDER_SEND_TURN_MAX_INPUT_CHARS,
} from "@afar-research/contracts";
import type { EnvironmentConnectionPresentation } from "@afar-research/client-runtime/state/presentation";
import {
  isPasteAsTextShortcut,
  nextPastedTextFileName,
  pastedTextDisposition,
  wouldTextPasteExceedLimit,
} from "@afar-research/client-runtime/text-paste";
import { serializeComposerFileLink } from "@afar-research/shared/composerTrigger";
import { createModelSelection, normalizeModelSlug } from "@afar-research/shared/model";
import {
  Fragment,
  memo,
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal, flushSync } from "react-dom";
import {
  clampCollapsedComposerCursor,
  type ComposerSubmissionIntent,
  type ComposerTrigger,
  collapseExpandedComposerCursor,
  composerSubmissionIntentForEnter,
  detectComposerTrigger,
  expandCollapsedComposerCursor,
  formatAssistantCitationForComposer,
  replaceTextRange,
} from "../../composer-logic";
import { DISCONNECTED_COMPOSER_PLACEHOLDER } from "../../composerPlaceholder";
import { listContinuationForEnter, listIndentForTab } from "../../composer-list-continuation";
import {
  deriveComposerSendState,
  readFileAsDataUrl,
  resolveComposerInteractionMode,
  resolveComposerProviderSelection,
  threadShellHasStarted,
} from "../ChatView.logic";
import { dataTransferIsComposerDrop, makeComposerDropHandlers } from "./composerMentionDrag";
import {
  composerFloatingLayerProps,
  useComposerMenuProps,
  isInsideCollapsedComposerControls,
  isInsideRestingComposerControlScope,
} from "./composerEventScope";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  type DraftId,
  type PersistedComposerFileAttachment,
  type PersistedComposerImageAttachment,
  composerFileDedupKey,
  composerFileMatchesReattachMarker,
  composerFileNeedsReattach,
  composerFileUploadsBytes,
  composerTargetKey,
  hydrateImagesFromPersisted,
  useComposerDraftStore,
  useComposerThreadDraft,
  useEffectiveComposerModelState,
} from "../../composerDraftStore";
import {
  MAX_STASH_ENTRIES,
  partitionStashAttachments,
  usePromptStashStore,
  type PromptStashEntry,
} from "../../promptStashStore";
import { ComposerStashBadge } from "./ComposerStashBadge";
import { ComposerStashMenu } from "./ComposerStashMenu";
import { useComposerMenuState } from "./useComposerMenuState";
import { useComposerFocusState } from "./useComposerFocusState";
import { useComposerMultilinePrompt } from "./useComposerMultilinePrompt";
import {
  ComposerTasksBadge,
  ComposerTasksContent,
  ComposerTasksDrawer,
  type ComposerTaskStep,
  type ComposerTasksProgress,
} from "./ComposerTasksBadge";
import { ComposerActivityRow } from "./ComposerActivityStatus";
import {
  reconcileAttachmentContextReferences,
  type RetainedAttachmentContextPayloads,
} from "./composerContextUndo";
import type { ThreadSyncPhase } from "../../threadSync";
import {
  EMPTY_BRAIN_COMPOSER_COLLECTION,
  EMPTY_DESIGN_COMPOSER_COLLECTION,
  brainActionOf,
  brainAwareComposerCanSend,
  brainActionCarriesComposerInput,
  brainDeleteTargetPath,
  composerAttachmentLimit,
  composerTakesSourcesAsIs,
  describeBrainAction,
  designOperationOf,
  type BrainComposerCollection,
  type DesignComposerCollection,
  type ThreadActionContext,
  countDesignComposerCollected,
  countDesignComposerEvidence,
  describeDesignComposerOperation,
  designComposerOperationCanSend,
  designComposerOperationCollectsEvidenceUrls,
  designComposerOperationTakesAttachments,
  designComposerPlaceholder,
  designDeleteSendLabel,
  designDeleteTargetKey,
  designDeleteTargetLabel,
  resolveDesignSystemMention,
  describeDesignComposerTargetPicker,
  searchDesignSystemMentionTargets,
  selectDesignComposerMentions,
} from "@afar-research/afar";
import { DesignAddEvidenceUrlDialog } from "./DesignAddEvidenceUrlDialog";
import { DesignDeleteTargetsDialog } from "./DesignDeleteTargetsDialog";
import { DesignTargetPicker } from "./DesignTargetPicker";
import { BrainAddTextDialog } from "./BrainAddTextDialog";
import { BrainComposerChipRow } from "./BrainComposerChips";
import { findAction } from "@afar-research/agent-content/actions";
import { ComposerActionMenu } from "./ComposerActionMenu";
import {
  ComposerActionChip,
  ComposerActionDescription,
  NextActionSuggestion,
} from "./ComposerActionChip";
import { AttachIngestDialog } from "./AttachIngestDialog";
import { decideAttachIngest, INGEST_ACTION_SELECTION } from "./attachIngest.logic";
import {
  composerActionChip,
  initialActionSelection,
  listComposerActionEntries,
  matchComposerActionEntries,
  type ComposerCustomActionSkill,
} from "./composerActions.logic";
import { BrainDeleteRawsDialog } from "./BrainDeleteRawsDialog";
import { ComposerEnvironmentFilesDialog } from "./ComposerEnvironmentFilesDialog";
import { ComposerBanner } from "./ComposerBanner";
import { ComposerSurface } from "./ComposerSurface";
import {
  ComposerBannerStack,
  type ComposerBannerStackContent,
  type ComposerBannerStackItem,
} from "./ComposerBannerStack";
import { compressImageForStash, prepareImageForAttachment } from "../../lib/imageCompression";
import {
  fileAttachmentTooLargeMessage,
  formatAttachmentSize,
} from "@afar-research/client-runtime/state/attachments";
import {
  attachmentsToReleaseOnUploadCapabilityLoss,
  composerAttachmentsAwaitingUpload,
  composerOtherFilesForPresentation,
  classifyComposerAttachmentFile,
  fileAttachmentCapabilityBlockReason,
  fileAttachmentStagingLimit,
  isPreviewableComposerVideo,
  normalizeComposerImageFileMimeType,
  shouldHandleComposerAttachmentPaste,
} from "./composerAttachmentFiles";
import {
  readAttachmentUpload,
  releaseAttachmentUpload,
  releaseDraftAttachment,
  releasePersistedAttachmentUpload,
  retryAttachmentUpload,
  startAttachmentUpload,
  useAttachmentUploadStore,
} from "../../lib/attachmentUploadQueue";
import {
  attachmentUploadBlockReason,
  formatAttachmentUploadProgress,
} from "../../lib/attachmentUploadState";
import { isCommandPaletteOpen } from "../../commandPaletteContext";
import { getTerminalFocusOwner } from "../../lib/terminalFocus";
import type { AssistantCitationSourceAnchor } from "~/lib/assistantTextSelection";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../../keybindings";
import {
  type TerminalContextDraft,
  type TerminalContextSelection,
} from "../../lib/terminalContext";
import { useComposerPathSearch } from "../../lib/composerPathSearchState";
import { useDesignSystemMentionDiscovery } from "../../state/queries";
import { replaceComposerContextReferences } from "@afar-research/shared/composerContextReferences";
import {
  COMPOSER_FOOTER_COMPACT_BREAKPOINT_PX,
  COMPOSER_FOOTER_WIDE_ACTIONS_COMPACT_BREAKPOINT_PX,
  getRestingComposerImagePreviewCounts,
  resolveRestingComposerControlsLayout,
  shouldAnimateComposerRestingTransition,
  shouldUseCompactComposerPrimaryActions,
  shouldUseCompactComposerFooter,
  shouldUseRestingComposerLayout,
} from "../composerFooterLayout";
import { measureRestingComposerControls } from "./restingComposerControlsMeasurement";
import { observeResponsiveBreakpointFade, usePanelAnimationSettings } from "../../panelAnimations";
import { type ComposerPromptEditorHandle, ComposerPromptEditor } from "../ComposerPromptEditor";
import {
  ComposerContextActionsContext,
  composerContextRecordsFromDraft,
  uploadedContextRecordFromDraft,
} from "../composerContextPresentation";
import { useOpenPrLink } from "~/lib/openPullRequestLink";
import {
  collectInlineContextIds,
  type ComposerContextReference,
  ensureInlineContextReferences,
  formatInlineContextReference,
  insertInlineContextReference,
  inlineContextReferenceReplacement,
  toKindScopedComposerContextId,
} from "~/lib/composerContextReferences";
import {
  asKnownContextRecord,
  composerContextImportLookupIds,
  isSameComposerContextPayload,
  uploadedAttachmentContextRecord,
  fileContextReference,
  imageContextReference,
  previewAnnotationContextId,
  previewAnnotationContextRecord,
  previewAnnotationFromRecord,
  reviewCommentContextId,
  reviewCommentContextReference,
  reviewCommentContextRecord,
  reviewCommentFromRecord,
  terminalContextDraftFromRecord,
  terminalContextReference,
  terminalContextRecord,
} from "~/lib/composerContextRecords";
import { requestConfirmDialog } from "~/confirmDialog";
import { encodeComposerContextFragment } from "@afar-research/shared/composerContextClipboard";
import type {
  ComposerContextClipboardFragment,
  ComposerContextRecord,
} from "@afar-research/contracts";
import { resolveAssetUrl } from "~/assets/assetUrls";
import { assetEnvironment } from "~/state/assets";
import { readPreparedConnection } from "~/state/session";
import { useAtomQueryRunner } from "~/state/use-atom-query-runner";
import { usePullRequestList, type EnvironmentQueryTarget } from "~/state/pullRequests";
import { useDebouncedValue } from "~/state/queries";
import { ProviderModelPicker } from "./ProviderModelPicker";
import { type ComposerCommandItem, ComposerCommandMenu } from "./ComposerCommandMenu";
import { ComposerPendingApprovalActions } from "./ComposerPendingApprovalActions";
import { CompactComposerControlsMenu } from "./CompactComposerControlsMenu";
import { ComposerImageThumbnail } from "./ComposerImageThumbnail";
import { ComposerPrimaryActions } from "./ComposerPrimaryActions";
import { ComposerPendingApprovalPanel } from "./ComposerPendingApprovalPanel";
import { ComposerPendingUserInputPanel } from "./ComposerPendingUserInputPanel";
import { ComposerPlanFollowUpBanner } from "./ComposerPlanFollowUpBanner";
import {
  ComposerControl,
  ComposerControlIcon,
  ComposerControlSeparator,
  ComposerSelectControl,
} from "./ComposerControl";
import { resolveComposerMenuActiveItemId } from "./composerMenuHighlight";
import {
  buildPullRequestReferenceContext,
  matchesPullRequestQuery,
  rankPullRequestMatches,
} from "./composerPullRequestReference";
import {
  searchSlashCommandItems,
  slashCommandItemsForPromptPosition,
} from "./composerSlashCommandSearch";
import {
  getComposerPromptInjectionState,
  getComposerProviderState,
  renderProviderTraitsMenuContent,
  renderProviderTraitsPicker,
} from "./composerProviderState";
import { ContextWindowMeter, ContextWindowMeterPlaceholder } from "./ContextWindowMeter";
import {
  providerSupportsManualCompaction,
  resolveContextWindowModelDisplayName,
  shouldReserveContextWindowMeter,
} from "./ContextWindowMeter.logic";
import {
  attachVideoThumbnail,
  buildExpandedImagePreview,
  type ExpandedImagePreview,
} from "./ExpandedImagePreview";
import {
  SNAP_SHOT_ATTACHMENT_FRAME_CLASS,
  SnapShotAttachmentDetails,
} from "./SnapShotAttachmentDetails";
import {
  getPendingSnapShotAnimations,
  pendingSnapShotAnimationIdsForTarget,
  scheduleSnapShotAnimationDestination,
  setSnapShotAnimationDestination,
  shouldAnimateSnapShotArrival,
  subscribeToPendingSnapShotAnimations,
} from "../../lib/snapShotAnimation";
import { resizeSnapShotSource } from "../../lib/snapShotSource";
import { basenameOfPath } from "../../pierre-icons";
import { cn, isMacPlatform, randomUUID } from "~/lib/utils";
import {
  browserFolderPickSupported,
  composerAttachmentsFromDrop,
  composerFileFromPathAttachment,
  composerFileSizeLabel,
  pickBrowserFolderAttachment,
  type ComposerPathAttachment,
} from "../../lib/composerAttachments";
import {
  openRuntimeComposerFilesAndFolders,
  resolveRuntimeComposerFilesAndFoldersOpenStrategy,
} from "../../lib/openComposerFilesAndFolders";
import {
  getComposerPromptLengthValidationMessage,
  getComposerSubmissionValidationMessage,
  submitComposerDraft,
} from "./composerSubmission";
import { ComposerPromptLengthValidation } from "./ComposerPromptLengthValidation";
import { PierreEntryIcon } from "./PierreEntryIcon";
import { pendingDraftWork } from "./pendingDraftWork";
import { isTimelineScrollTarget } from "./timelineScrollTarget";
import {
  createComposerScrollGestureState,
  recordComposerScrollGestureEvent,
  shouldCollapseComposerForScrollKey,
  resetComposerScrollGesture,
  suppressActiveComposerScrollGesture,
} from "./composerScrollGesture";
import { prepareVideoFirstFrame } from "../../lib/videoFirstFrame";

function ComposerVideoThumbnail({ file }: { file: File }) {
  const setVideo = useCallback(
    (video: HTMLVideoElement | null) => {
      if (!video) return;
      return attachVideoThumbnail(video, file);
    },
    [file],
  );

  return (
    <video
      ref={setVideo}
      muted
      playsInline
      preload="metadata"
      aria-hidden="true"
      onLoadedMetadata={(event) => prepareVideoFirstFrame(event.currentTarget)}
      className="pointer-events-none absolute inset-0 size-full object-cover"
    />
  );
}

type ComposerCommandMenuPosition = {
  bottom: number;
  left: number;
  maxHeight: number;
  width: number;
};

function SnapShotAttachmentFrame({
  animationId,
  animationSource,
  arrival,
  animateArrival,
  className,
  ...props
}: ComponentProps<"div"> & {
  readonly animationId?: string | undefined;
  readonly animationSource?: SnapShotSource | undefined;
  readonly arrival?: boolean | undefined;
  readonly animateArrival?: boolean | undefined;
}) {
  const frameRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame || (!animationId && !arrival)) return;
    frame.scrollIntoView({ block: "nearest", inline: "nearest" });
    if (!animationId) return;

    return scheduleSnapShotAnimationDestination(animationId, () =>
      setSnapShotAnimationDestination(animationId, frame, animationSource),
    );
  }, [animationId, animationSource, arrival]);

  return (
    <div
      ref={frameRef}
      className={cn(
        animateArrival &&
          !animationId &&
          "origin-center transition-[opacity,scale] duration-300 ease-[cubic-bezier(.2,.8,.2,1)] starting:scale-95 starting:opacity-0 motion-reduce:transition-none motion-reduce:starting:scale-100 motion-reduce:starting:opacity-100",
        className,
      )}
      {...props}
    />
  );
}

const COMPOSER_PULL_REQUEST_LIST_LIMIT = 99;
const COMPOSER_PULL_REQUEST_RESULT_LIMIT = 12;
/** A stable empty list: the file row is measured, and a fresh array each render would re-measure for nothing. */
const EMPTY_COMPOSER_FILES: ComposerFileAttachment[] = [];
const EMPTY_IMAGES: ComposerImageAttachment[] = [];

const EMPTY_PULL_REQUEST_LIST_TARGETS: ReadonlyArray<EnvironmentQueryTarget<PullRequestListInput>> =
  [];

const COMPOSER_SCROLL_COLLAPSE_THRESHOLD_PX = 24;
const COMPOSER_SCROLL_GESTURE_RESET_MS = 120;
const COMPOSER_RESTING_TRANSITION_DURATION_MS = 280;
const COMPOSER_RESTING_TRANSITION_CLEANUP_BUFFER_MS = 50;
const COMPOSER_RESTING_TRANSITION_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";
const COMPOSER_RESTING_CONTROLS_ARRIVAL_DRIFT_PX = 4;

function useComposerRestingTransition(
  isCollapsed: boolean,
  isResting: boolean,
  restingControlsRef: React.RefObject<HTMLDivElement | null>,
  onOverlayHeightChange: (height: number) => void,
) {
  const elementRef = useRef<HTMLDivElement>(null);
  const isCollapsedRef = useRef(isCollapsed);
  const previousCollapsedRef = useRef(isCollapsed);
  const previousRestingRef = useRef(isResting);
  const previousHeightRef = useRef<number | null>(null);
  const previousContentOffsetsRef = useRef<{
    promptFromTop: number | null;
    promptHeight: number | null;
    actionFromBottom: number | null;
    controlsFromBottom: number | null;
    controlsFromLeft: number | null;
  }>({
    promptFromTop: null,
    promptHeight: null,
    actionFromBottom: null,
    controlsFromBottom: null,
    controlsFromLeft: null,
  });
  const animationRef = useRef<Animation | null>(null);
  const animationTargetHeightRef = useRef<number | null>(null);
  const contentAnimationsRef = useRef<Animation[]>([]);
  const stateChangeAnimationsRef = useRef<Animation[]>([]);
  const pinnedOverlayRef = useRef<HTMLElement | null>(null);
  const transitionCleanupTimeoutRef = useRef<number | null>(null);
  const transitionLayoutRequestRef = useRef(0);
  const hasCompletedInitialLayoutRef = useRef(false);

  const clearOverlayPin = useCallback(() => {
    // The overlay belongs to the chat view and outlives this composer, so it
    // is remembered from pin time rather than re-resolved through a ref that
    // React may already have detached during unmount.
    const overlay = pinnedOverlayRef.current;
    pinnedOverlayRef.current = null;
    overlay?.style.removeProperty("height");
    overlay?.style.removeProperty("display");
    overlay?.style.removeProperty("flex-direction");
    overlay?.style.removeProperty("justify-content");
  }, []);

  const clearTransitionStyles = useCallback(() => {
    const element = elementRef.current;
    const footer = element?.querySelector<HTMLElement>('[data-chat-composer-footer="true"]');
    element?.style.removeProperty("overflow");
    element
      ?.querySelector<HTMLElement>('[data-chat-composer-surface="true"]')
      ?.style.removeProperty("height");
    footer?.style.removeProperty("position");
    footer?.style.removeProperty("top");
    footer?.style.removeProperty("bottom");
    footer?.style.removeProperty("left");
    footer?.style.removeProperty("right");
    footer?.style.removeProperty("height");
    clearOverlayPin();
  }, [clearOverlayPin]);

  isCollapsedRef.current = isCollapsed;

  const transitionToCurrentGeometry = useCallback(
    (stateChanged: boolean) => {
      const element = elementRef.current;
      const surface = element?.querySelector<HTMLElement>('[data-chat-composer-surface="true"]');
      if (!element || !surface) return;

      const nextIsCollapsed = isCollapsedRef.current;

      const visibleTransitionElement = (selector: string) =>
        Array.from(element.querySelectorAll<HTMLElement>(selector)).find(
          (candidate) => candidate.getClientRects().length > 0,
        ) ?? null;
      const prompt = visibleTransitionElement(
        '[data-testid="composer-editor"], [data-chat-composer-transition-prompt="true"]',
      );
      const action = visibleTransitionElement('[data-chat-composer-transition-actions="true"]');
      const footer = element.querySelector<HTMLElement>('[data-chat-composer-footer="true"]');
      // Inline resting controls stay inside the surface, so they slide between
      // the footer and their resting row instead of fading in from elsewhere.
      const continuousControls = element.dataset.inlineRestingControls === "true";
      const controls = nextIsCollapsed
        ? restingControlsRef.current
        : element.querySelector<HTMLElement>('[data-chat-composer-controls="left"]');
      const interruptedAnimation = animationRef.current;
      const interruptedPromptTop = interruptedAnimation
        ? (prompt?.getBoundingClientRect().top ?? null)
        : null;
      const interruptedActionTop = interruptedAnimation
        ? (action?.getBoundingClientRect().top ?? null)
        : null;
      const interruptedControlsLeft = interruptedAnimation
        ? (controls?.getBoundingClientRect().left ?? null)
        : null;
      const interruptedControlsTop = interruptedAnimation
        ? (controls?.getBoundingClientRect().top ?? null)
        : null;
      const interruptedHeight = interruptedAnimation
        ? element.getBoundingClientRect().height
        : null;
      const interruptedTargetHeight = animationTargetHeightRef.current;
      const interruptedCurrentTime =
        typeof interruptedAnimation?.currentTime === "number"
          ? interruptedAnimation.currentTime
          : null;
      const interruptedDuration = interruptedAnimation?.effect?.getComputedTiming().duration;
      if (transitionCleanupTimeoutRef.current !== null) {
        window.clearTimeout(transitionCleanupTimeoutRef.current);
        transitionCleanupTimeoutRef.current = null;
      }
      interruptedAnimation?.cancel();
      animationRef.current = null;
      for (const animation of contentAnimationsRef.current) animation.cancel();
      contentAnimationsRef.current = [];
      // The reveal and fade animations keep their own schedule across the
      // body-resize re-entries that retarget the geometry mid-flight (every
      // transition with a draft triggers one); cancelling them there would
      // pop their subjects to full visibility at the start of the tween.
      if (stateChanged) {
        for (const animation of stateChangeAnimationsRef.current) animation.cancel();
        stateChangeAnimationsRef.current = [];
      }
      clearTransitionStyles();

      const nextRect = element.getBoundingClientRect();
      const nextHeight = nextRect.height;
      // The chat view resize-observes the overlay to place the timeline
      // inset, the scroll-to-end pill, and the mini player. Publishing the
      // destination height here turns that feedback into one update instead
      // of a ChatView re-render on every animation frame.
      const overlay = element.closest<HTMLElement>('[data-chat-composer-overlay="true"]');
      const overlayHeight = overlay?.getBoundingClientRect().height ?? null;
      if (overlayHeight !== null) {
        onOverlayHeightChange(overlayHeight);
      }
      const nextPromptRect = prompt?.getBoundingClientRect() ?? null;
      const nextPromptTop = nextPromptRect?.top ?? null;
      const nextActionTop = action?.getBoundingClientRect().top ?? null;
      const nextControlsRect = controls?.getBoundingClientRect() ?? null;
      const nextControlsTop = nextControlsRect?.top ?? null;
      const footerBottom = footer ? nextRect.bottom - footer.getBoundingClientRect().bottom : 1;
      const previousHeight = interruptedHeight ?? previousHeightRef.current;
      const targetChanged =
        interruptedTargetHeight === null || Math.abs(interruptedTargetHeight - nextHeight) >= 0.5;
      const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      const shouldAnimate = shouldAnimateComposerRestingTransition({
        hasCompletedInitialLayout: hasCompletedInitialLayoutRef.current,
        stateChanged,
        hasInterruptedAnimation: interruptedHeight !== null,
      });

      if (
        shouldAnimate &&
        !prefersReducedMotion &&
        previousHeight !== null &&
        Math.abs(previousHeight - nextHeight) >= 0.5
      ) {
        const remainingDuration =
          typeof interruptedDuration === "number" && interruptedCurrentTime !== null
            ? Math.max(1, interruptedDuration - interruptedCurrentTime)
            : COMPOSER_RESTING_TRANSITION_DURATION_MS;
        const duration =
          interruptedHeight !== null && !targetChanged
            ? remainingDuration
            : COMPOSER_RESTING_TRANSITION_DURATION_MS;
        element.style.overflow = "clip";
        surface.style.height = "100%";

        // Pinning the overlay at the destination height keeps the resize
        // observer quiet for the tween; bottom alignment keeps the animating
        // surface glued to the overlay's stable bottom edge. The pin lasts
        // only for the tween so later attachment, thread, font, and viewport
        // changes remain natural.
        if (overlay && overlayHeight !== null) {
          overlay.style.height = `${String(overlayHeight)}px`;
          overlay.style.display = "flex";
          overlay.style.flexDirection = "column";
          overlay.style.justifyContent = "flex-end";
          pinnedOverlayRef.current = overlay;
        }

        // Keep the footer attached to the stable bottom edge while the outer
        // height changes. Its resting absolute layout otherwise spans the old
        // height on collapse, while its expanded flow layout falls below the
        // clipped surface on expansion.
        if (footer) {
          footer.style.position = "absolute";
          footer.style.top = "auto";
          footer.style.bottom = `${String(footerBottom)}px`;
          footer.style.height = "3rem";
          if (nextIsCollapsed) {
            footer.style.left = "auto";
            footer.style.right = "1px";
          } else {
            footer.style.left = "1px";
            footer.style.right = "1px";
          }
        }

        const animation = element.animate(
          [{ height: `${previousHeight}px` }, { height: `${nextHeight}px` }],
          {
            duration,
            easing: COMPOSER_RESTING_TRANSITION_EASING,
          },
        );
        animationRef.current = animation;
        animationTargetHeightRef.current = nextHeight;

        const animatedRect = element.getBoundingClientRect();
        const previousPromptTop =
          interruptedPromptTop ??
          (previousContentOffsetsRef.current.promptFromTop === null
            ? null
            : animatedRect.top + previousContentOffsetsRef.current.promptFromTop);
        const previousActionTop =
          interruptedActionTop ??
          (previousContentOffsetsRef.current.actionFromBottom === null
            ? null
            : animatedRect.bottom - previousContentOffsetsRef.current.actionFromBottom);
        const contentAnimations: Animation[] = [];
        const animateContentPosition = (
          content: HTMLElement | null,
          previousTop: number | null,
          previousLeft: number | null = null,
        ) => {
          if (!content || previousTop === null) return;
          const rect = content.getBoundingClientRect();
          const offset = previousTop - rect.top;
          const offsetX = previousLeft === null ? 0 : previousLeft - rect.left;
          if (Math.abs(offset) < 0.5 && Math.abs(offsetX) < 0.5) return;
          contentAnimations.push(
            content.animate(
              [
                { transform: `translate(${String(offsetX)}px, ${String(offset)}px)` },
                { transform: "none" },
              ],
              {
                duration,
                easing: COMPOSER_RESTING_TRANSITION_EASING,
              },
            ),
          );
        };
        animateContentPosition(prompt, previousPromptTop);
        animateContentPosition(action, previousActionTop);
        if (continuousControls) {
          const previousControlsTop =
            interruptedControlsTop ??
            (previousContentOffsetsRef.current.controlsFromBottom === null
              ? null
              : animatedRect.bottom - previousContentOffsetsRef.current.controlsFromBottom);
          const previousControlsLeft =
            interruptedControlsLeft ??
            (previousContentOffsetsRef.current.controlsFromLeft === null
              ? null
              : animatedRect.left + previousContentOffsetsRef.current.controlsFromLeft);
          animateContentPosition(controls, previousControlsTop, previousControlsLeft);
        }
        contentAnimationsRef.current = contentAnimations;

        if (stateChanged) {
          const stateChangeAnimations: Animation[] = [];

          // A prompt that gains lines on expansion would otherwise slide up
          // from under the footer band as one block. Opening a bottom clip in
          // step with the tween instead unfurls the extra lines beneath the
          // rising first line, so no text crosses the returning controls.
          const previousPromptHeight = previousContentOffsetsRef.current.promptHeight;
          if (
            !nextIsCollapsed &&
            prompt &&
            nextPromptRect &&
            previousPromptHeight !== null &&
            nextPromptRect.height - previousPromptHeight >= 0.5
          ) {
            const hiddenHeight = nextPromptRect.height - previousPromptHeight;
            stateChangeAnimations.push(
              prompt.animate(
                [
                  { clipPath: `inset(0 0 ${String(hiddenHeight)}px 0)` },
                  { clipPath: "inset(0 0 0 0)" },
                ],
                {
                  duration,
                  easing: COMPOSER_RESTING_TRANSITION_EASING,
                },
              ),
            );
          }

          // The footer controls teleport between the composer footer and the
          // context strip below it in a single commit. Fading the arriving
          // cluster in along its direction of travel reads as one continuous
          // move instead of a pop. Collapsing controls land in empty strip
          // space and can appear immediately, but expanding controls return
          // to the bottom row the prompt still occupies while the surface is
          // short, so they stay hidden through the first half of the tween
          // and fade in once the geometry has mostly settled.
          const arrivingControls = nextIsCollapsed
            ? restingControlsRef.current
            : element.querySelector<HTMLElement>('[data-chat-composer-controls="left"]');
          if (arrivingControls && !continuousControls) {
            const drift = nextIsCollapsed
              ? -COMPOSER_RESTING_CONTROLS_ARRIVAL_DRIFT_PX
              : COMPOSER_RESTING_CONTROLS_ARRIVAL_DRIFT_PX;
            stateChangeAnimations.push(
              arrivingControls.animate(
                [
                  { opacity: 0, transform: `translateY(${String(drift)}px)` },
                  { opacity: 1, transform: "none" },
                ],
                {
                  duration: nextIsCollapsed ? duration : duration / 2,
                  delay: nextIsCollapsed ? 0 : duration / 2,
                  fill: "backwards",
                  easing: COMPOSER_RESTING_TRANSITION_EASING,
                },
              ),
            );
          }

          const arrivingImagePreviews = nextIsCollapsed
            ? Array.from(
                element.querySelectorAll<HTMLElement>('[data-chat-composer-resting-images="true"]'),
              )
            : Array.from(
                element.querySelectorAll<HTMLElement>('[data-chat-composer-expanded-image="true"]'),
              );
          for (const imagePreview of arrivingImagePreviews) {
            stateChangeAnimations.push(
              imagePreview.animate([{ opacity: 0 }, { opacity: 1 }], {
                duration: nextIsCollapsed ? duration : duration / 2,
                delay: nextIsCollapsed ? 0 : duration / 2,
                fill: "backwards",
                easing: COMPOSER_RESTING_TRANSITION_EASING,
              }),
            );
          }
          stateChangeAnimationsRef.current = stateChangeAnimations;
        }

        const finishTransition = (cancelAnimations: boolean) => {
          if (animationRef.current !== animation) return;
          if (transitionCleanupTimeoutRef.current !== null) {
            window.clearTimeout(transitionCleanupTimeoutRef.current);
            transitionCleanupTimeoutRef.current = null;
          }
          if (cancelAnimations) {
            animation.cancel();
            for (const contentAnimation of contentAnimationsRef.current) {
              contentAnimation.cancel();
            }
            for (const stateChangeAnimation of stateChangeAnimationsRef.current) {
              stateChangeAnimation.cancel();
            }
          }
          animationRef.current = null;
          animationTargetHeightRef.current = null;
          contentAnimationsRef.current = [];
          stateChangeAnimationsRef.current = [];
          clearTransitionStyles();
        };
        void animation.finished.catch(() => undefined).then(() => finishTransition(false));
        // A suspended document timeline can leave `finished` pending while
        // these measurement styles remain active. Wall-clock cleanup makes
        // the natural layout the eventual source of truth in that case.
        transitionCleanupTimeoutRef.current = window.setTimeout(
          () => finishTransition(true),
          duration + COMPOSER_RESTING_TRANSITION_CLEANUP_BUFFER_MS,
        );
      } else {
        animationTargetHeightRef.current = null;
      }

      previousCollapsedRef.current = nextIsCollapsed;
      previousHeightRef.current = nextHeight;
      previousContentOffsetsRef.current = {
        promptFromTop: nextPromptTop === null ? null : nextPromptTop - nextRect.top,
        promptHeight: nextPromptRect?.height ?? null,
        actionFromBottom: nextActionTop === null ? null : nextRect.bottom - nextActionTop,
        controlsFromBottom: nextControlsTop === null ? null : nextRect.bottom - nextControlsTop,
        controlsFromLeft: nextControlsRect === null ? null : nextControlsRect.left - nextRect.left,
      };
    },
    [clearTransitionStyles, onOverlayHeightChange, restingControlsRef],
  );

  useLayoutEffect(() => {
    const requestId = transitionLayoutRequestRef.current + 1;
    transitionLayoutRequestRef.current = requestId;
    const stateChanged = previousCollapsedRef.current !== isCollapsed;
    // A non-Git context strip enters or leaves flow through ChatView state in
    // an earlier layout effect. Let React flush that parent update before the
    // FLIP reads its destination geometry, while still running before paint.
    queueMicrotask(() => {
      if (transitionLayoutRequestRef.current !== requestId) return;
      transitionToCurrentGeometry(stateChanged);
    });
    return () => {
      if (transitionLayoutRequestRef.current === requestId) {
        transitionLayoutRequestRef.current += 1;
      }
    };
  }, [isCollapsed, transitionToCurrentGeometry]);

  // The resting flag can change while the collapsed layout stays the same,
  // for example when an unfocused thread crosses the phone breakpoint. The
  // chat view pairs overlay heights with that flag, so republish the natural
  // height for the new flag. A transition in flight publishes its own.
  useLayoutEffect(() => {
    if (previousRestingRef.current === isResting) return;
    previousRestingRef.current = isResting;
    if (animationRef.current) return;
    const overlay = elementRef.current?.closest<HTMLElement>('[data-chat-composer-overlay="true"]');
    if (overlay) onOverlayHeightChange(overlay.getBoundingClientRect().height);
  }, [isResting, onOverlayHeightChange]);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;

    const body = element.querySelector<HTMLElement>('[data-chat-composer-body="true"]');
    const observer = new ResizeObserver((entries) => {
      if (animationRef.current) {
        if (body && entries.some((entry) => entry.target === body)) {
          transitionToCurrentGeometry(false);
        }
        return;
      }
      const elementRect = element.getBoundingClientRect();
      const visibleTransitionElement = (selector: string) =>
        Array.from(element.querySelectorAll<HTMLElement>(selector)).find(
          (candidate) => candidate.getClientRects().length > 0,
        ) ?? null;
      const promptRect = visibleTransitionElement(
        '[data-testid="composer-editor"], [data-chat-composer-transition-prompt="true"]',
      )?.getBoundingClientRect();
      const actionTop = visibleTransitionElement(
        '[data-chat-composer-transition-actions="true"]',
      )?.getBoundingClientRect().top;
      const controlsRect = (
        isCollapsedRef.current
          ? restingControlsRef.current
          : visibleTransitionElement('[data-chat-composer-controls="left"]')
      )?.getBoundingClientRect();
      previousHeightRef.current = elementRect.height;
      previousContentOffsetsRef.current = {
        promptFromTop: promptRect === undefined ? null : promptRect.top - elementRect.top,
        promptHeight: promptRect?.height ?? null,
        actionFromBottom: actionTop === undefined ? null : elementRect.bottom - actionTop,
        controlsFromBottom:
          controlsRect === undefined ? null : elementRect.bottom - controlsRect.top,
        controlsFromLeft: controlsRect === undefined ? null : controlsRect.left - elementRect.left,
      };
    });
    observer.observe(element);
    if (body) observer.observe(body);
    return () => observer.disconnect();
  }, [restingControlsRef, transitionToCurrentGeometry]);

  useEffect(() => {
    // Host discovery and width measurement settle through layout updates on
    // mount. Treat that bootstrap as initial geometry so an existing thread
    // paints at rest instead of visibly collapsing from the expanded height.
    hasCompletedInitialLayoutRef.current = true;
    return () => {
      if (transitionCleanupTimeoutRef.current !== null) {
        window.clearTimeout(transitionCleanupTimeoutRef.current);
        transitionCleanupTimeoutRef.current = null;
      }
      animationRef.current?.cancel();
      animationRef.current = null;
      animationTargetHeightRef.current = null;
      for (const animation of contentAnimationsRef.current) animation.cancel();
      contentAnimationsRef.current = [];
      for (const animation of stateChangeAnimationsRef.current) animation.cancel();
      stateChangeAnimationsRef.current = [];
      clearTransitionStyles();
    };
  }, [clearTransitionStyles]);

  return elementRef;
}

function composerCommandMenuPositionsEqual(
  a: ComposerCommandMenuPosition,
  b: ComposerCommandMenuPosition,
): boolean {
  return (
    a.bottom === b.bottom && a.left === b.left && a.maxHeight === b.maxHeight && a.width === b.width
  );
}

function ComposerCommandMenuLayer(props: { anchor: HTMLElement | null; children: ReactNode }) {
  const [position, setPosition] = useState<ComposerCommandMenuPosition | null>(null);

  useLayoutEffect(() => {
    const anchor = props.anchor;
    if (!anchor) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const form = anchor.closest<HTMLElement>('[data-chat-composer-form="true"]');
      const mainSurface = form?.querySelector<HTMLElement>(
        '[data-chat-composer-main-surface="true"]',
      );
      const rect = (mainSurface ?? form ?? anchor).getBoundingClientRect();
      const rootFontSizePx =
        Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      const drawerInsetRem = Number.parseFloat(
        window.getComputedStyle(form ?? anchor).getPropertyValue("--chat-composer-drawer-inset"),
      );
      const drawerInset = drawerInsetRem * rootFontSizePx;
      // One extra pixel prevents fractional layout coordinates from exposing
      // the canvas between the drawer mask and the composer's foreground edge.
      // Mirrors --chat-composer-attachment-overlap: calc(1rem + 1px).
      const composerOverlap = rootFontSizePx + 1;
      const next = {
        bottom: window.innerHeight - rect.top - composerOverlap,
        left: rect.left + drawerInset,
        maxHeight: Math.max(96, rect.top - 24 + composerOverlap),
        width: Math.max(0, rect.width - drawerInset * 2),
      };
      setPosition((current) =>
        current && composerCommandMenuPositionsEqual(current, next) ? current : next,
      );
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updatePosition);
    if (observer) {
      // The composer is centered and capped at a max width, so opening a side
      // panel slides it sideways without ever resizing it. Watching the anchor
      // alone would leave the menu behind; the ancestors are what shrink, and
      // they resize on every frame of the panel animation.
      observer.observe(anchor);
      for (let element = anchor.parentElement; element; element = element.parentElement) {
        observer.observe(element);
      }
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [props.anchor]);

  if (!position) return null;

  return createPortal(
    <div
      className="pointer-events-auto fixed z-40 flex flex-col"
      data-composer-drawer-layer="true"
      style={{
        bottom: position.bottom,
        left: position.left,
        maxHeight: position.maxHeight,
        width: position.width,
      }}
    >
      {props.children}
    </div>,
    document.body,
  );
}
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectValue } from "../ui/select";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { toastManager } from "../ui/toast";
import {
  BotIcon,
  CircleAlertIcon,
  PencilRulerIcon,
  PlayIcon,
  ShieldIcon,
  XIcon,
} from "lucide-react";
import { proposedPlanTitle } from "../../proposedPlan";
import { hasProviderSetup } from "./ProviderStatusBanner";
import {
  deriveProviderInstanceEntries,
  NO_PROVIDER_MODEL_SELECTION,
  sortProviderInstanceEntries,
  type ProviderInstanceEntry,
} from "../../providerInstances";
import { type AppModelOption, getAppModelOptionsForInstance } from "../../modelSelection";
import type { UnifiedSettings } from "@afar-research/contracts/settings";
import {
  isVideoAttachment,
  type SessionPhase,
  type Thread,
  type ThreadShell,
  videoMimeType,
} from "../../types";
import {
  buildComposerPromptHistoryEntries,
  type ComposerPromptHistoryMessage,
  stepComposerPromptHistory,
  type ComposerPromptHistoryPosition,
} from "./composerPromptHistory";
import type { PendingUserInputDraftAnswer } from "../../pendingUserInput";
import type { PendingApproval, PendingUserInput } from "../../session-logic";
import type { ContextWindowSnapshot } from "../../lib/contextWindow";
import {
  formatProviderSkillDisplayName,
  getProviderSlashCommandsForSlashMenu,
  getProviderSkillsForSlashMenu,
} from "@afar-research/client-runtime/providerSkills";
import { searchProviderSkills } from "../../providerSkillSearch";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useEnvironmentManagedByAfar } from "../../hooks/useEnvironmentManagedByAfar";
import type { ReviewCommentContext } from "../../reviewCommentContext";

const extendReplacementRangeForTrailingSpace = (
  text: string,
  rangeEnd: number,
  replacement: string,
): number => {
  if (!replacement.endsWith(" ")) {
    return rangeEnd;
  }
  return text[rangeEnd] === " " ? rangeEnd + 1 : rangeEnd;
};

function useRestingComposerControlsLayout(host: HTMLDivElement | null) {
  const controlsRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef(host);
  hostRef.current = host;
  const [layout, setLayout] = useState({ hiddenCount: 0, visible: true });

  const measure = useCallback(() => {
    const currentHost = hostRef.current;
    const controls = controlsRef.current;
    // The controls only mount while the composer rests, so the expanded
    // composer pays no layout reads here despite the every-render effect.
    if (currentHost === null || !controls) return;

    const measurement = measureRestingComposerControls(controls);
    if (!measurement) return;
    const hostWidth = currentHost.clientWidth;

    setLayout((current) => {
      const next = resolveRestingComposerControlsLayout({
        ...measurement,
        hostWidth,
        previous: current,
      });
      return next.hiddenCount === current.hiddenCount && next.visible === current.visible
        ? current
        : next;
    });
  }, []);

  useLayoutEffect(measure);
  useEffect(() => {
    if (!host) return;
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    document.fonts.addEventListener("loadingdone", measure);
    return () => {
      observer.disconnect();
      document.fonts.removeEventListener("loadingdone", measure);
    };
  }, [host, measure]);

  return { controlsRef, hiddenBlockCount: layout.hiddenCount, controlsVisible: layout.visible };
}

const ComposerFooterModeControls = memo(function ComposerFooterModeControls(props: {
  showInteractionModeToggle: boolean;
  interactionMode: ProviderInteractionMode;
  runtimeMode: RuntimeMode;
  size?: "sm" | "xs";
  hidden?: boolean;
  onToggleInteractionMode: () => void;
  onRuntimeModeChange: (mode: RuntimeMode) => void;
}) {
  const size = props.size ?? "sm";
  const composerFloatingLayerProps = useComposerMenuProps();
  const [open, setOpen] = useComposerMenuState(props.hidden);
  const runtimeModeOption = runtimeModeConfig[props.runtimeMode];
  const RuntimeModeIcon = runtimeModeOption.icon;
  const interactionModeTooltip =
    props.interactionMode === "plan"
      ? "Plan mode — click to return to normal build mode"
      : "Default mode — click to enter plan mode";

  const interactionModeToggle = props.showInteractionModeToggle ? (
    <>
      <ComposerControlSeparator size={size} />
      <Tooltip>
        <TooltipTrigger
          render={
            <ComposerControl
              size={size}
              className={cn(
                "shrink-0 whitespace-nowrap",
                props.interactionMode === "plan"
                  ? "bg-accent text-accent-foreground hover:bg-accent/80"
                  : size === "xs"
                    ? undefined
                    : "text-secondary-label hover:text-foreground",
              )}
              type="button"
              onClick={props.onToggleInteractionMode}
              aria-label={interactionModeTooltip}
            />
          }
        >
          {props.interactionMode === "plan" ? (
            <ComposerControlIcon
              icon={PencilRulerIcon}
              size={size}
              className="text-current opacity-100"
            />
          ) : (
            <ComposerControlIcon
              icon={BotIcon}
              size={size}
              opticalSize={size === "xs" ? "default" : "large"}
            />
          )}
          <span className="sr-only sm:not-sr-only">
            {props.interactionMode === "plan" ? "Plan" : "Build"}
          </span>
        </TooltipTrigger>
        <TooltipPopup side="top">{interactionModeTooltip}</TooltipPopup>
      </Tooltip>
    </>
  ) : null;

  return (
    <>
      <ComposerControlSeparator size={size} />

      <Tooltip>
        <Select
          open={open}
          onOpenChange={setOpen}
          value={props.runtimeMode}
          onValueChange={(value) => props.onRuntimeModeChange(value!)}
        >
          <TooltipTrigger
            render={
              <ComposerSelectControl
                data-composer-shortcut="composer.mode"
                size={size}
                className={size === "xs" ? undefined : "font-medium"}
                aria-label="Runtime mode"
              />
            }
          >
            <ComposerControlIcon icon={RuntimeModeIcon} size={size} />
            <SelectValue>{runtimeModeOption.label}</SelectValue>
          </TooltipTrigger>
          <SelectPopup alignItemWithTrigger={false} {...composerFloatingLayerProps}>
            {runtimeModeOptions.map((mode) => {
              const option = runtimeModeConfig[mode];
              const OptionIcon = option.icon;
              return (
                <SelectItem key={mode} value={mode} hideIndicator className="min-w-64 py-2">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="grid min-w-0 flex-1 gap-0.5">
                      <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                        <OptionIcon className="size-3.5 shrink-0 text-muted-foreground" />
                        {option.label}
                      </span>
                      <span className="text-muted-foreground text-xs leading-4">
                        {option.description}
                      </span>
                    </div>
                  </div>
                </SelectItem>
              );
            })}
          </SelectPopup>
        </Select>
        <TooltipPopup side="top">{runtimeModeOption.description}</TooltipPopup>
      </Tooltip>

      {interactionModeToggle}
    </>
  );
});

const ComposerFooterPrimaryActions = memo(function ComposerFooterPrimaryActions(props: {
  compact: boolean;
  activeContextWindow: ContextWindowSnapshot | null;
  reserveContextWindowMeter: boolean;
  activeThreadModelDisplayName: string | null;
  isPreparingWorktree: boolean;
  pendingAction: {
    questionIndex: number;
    isLastQuestion: boolean;
    canAdvance: boolean;
    isResponding: boolean;
    isComplete: boolean;
  } | null;
  isRunning: boolean;
  showPlanFollowUpPrompt: boolean;
  promptHasText: boolean;
  isSendBusy: boolean;
  sendDisabledReason: string | null;
  isConnecting: boolean;
  isEnvironmentUnavailable: boolean;
  hasSendableContent: boolean;
  destructiveSendLabel: string | null;
  preserveComposerFocusOnPointerDown?: boolean;
  onPreviousPendingQuestion: () => void;
  onInterrupt: () => void;
  onImplementPlanInNewThread: () => void;
  onCompactContext?: (() => void) | undefined;
  compactDisabled: boolean;
  compactDisabledReason: string | null;
}) {
  return (
    <>
      {props.activeContextWindow ? (
        <ContextWindowMeter
          usage={props.activeContextWindow}
          providerDisplayName={props.activeThreadModelDisplayName}
        />
      ) : props.reserveContextWindowMeter ? (
        <ContextWindowMeterPlaceholder />
      ) : null}
      <ComposerPrimaryActions
        compact={props.compact}
        pendingAction={props.pendingAction}
        isRunning={props.isRunning}
        showPlanFollowUpPrompt={props.showPlanFollowUpPrompt}
        promptHasText={props.promptHasText}
        isSendBusy={props.isSendBusy}
        sendDisabledReason={props.sendDisabledReason}
        isConnecting={props.isConnecting}
        isEnvironmentUnavailable={props.isEnvironmentUnavailable}
        isPreparingWorktree={props.isPreparingWorktree}
        hasSendableContent={props.hasSendableContent}
        destructiveSendLabel={props.destructiveSendLabel}
        preserveComposerFocusOnPointerDown={props.preserveComposerFocusOnPointerDown ?? false}
        onPreviousPendingQuestion={props.onPreviousPendingQuestion}
        onInterrupt={props.onInterrupt}
        onImplementPlanInNewThread={props.onImplementPlanInNewThread}
      />
    </>
  );
});

// --------------------------------------------------------------------------
// Handle exposed to ChatView
// --------------------------------------------------------------------------

export interface ChatComposerHandle {
  focusAtEnd: () => void;
  focusAt: (cursor: number) => void;
  /** Expand the desktop composer at the timeline end without taking focus. */
  restoreAfterTimelineReachedEnd: () => void;
  collapseForTimelineScrollKey: (key: string) => void;
  hasPendingAttachments: () => boolean;
  insertTextAtEnd: (
    text: string,
    options?: { ensureLeadingBoundary?: boolean; clipboardData?: DataTransfer },
  ) => boolean;
  /** Apply large-paste folding for text redirected from a blurred composer. */
  pasteTextAtEnd: (text: string, options?: { bypassAutoAttachment?: boolean }) => boolean;
  citeAssistantText: (
    citation: AssistantCitation,
    sourceAnchor: AssistantCitationSourceAnchor,
  ) => boolean;
  openModelPicker: () => void;
  toggleModelPicker: () => void;
  openControl: (command: KeybindingCommand) => void;
  isModelPickerOpen: () => boolean;
  compactContext: () => void;
  readSnapshot: () => {
    value: string;
    cursor: number;
    expandedCursor: number;
    contextIds: string[];
  };
  /** Reset composer cursor/trigger/highlight after external prompt mutations (e.g. onSend). */
  resetCursorState: (options?: {
    cursor?: number;
    prompt?: string;
    detectTrigger?: boolean;
  }) => void;
  /** Insert a terminal context from the terminal drawer. */
  addTerminalContext: (selection: TerminalContextSelection) => void;
  /**
   * Empty the Brain chip row once its send is accepted. Text sources and
   * Delete targets are not draft content, so clearing the draft leaves them
   * behind and the next send would carry them a second time.
   */
  clearBrainComposerCollection: () => void;
  /** Empty Add Example's URL chip row once its send is accepted. */
  replaceDesignEvidenceUrls: (urls: ReadonlyArray<string>) => void;
  /** Empty Delete's chip row once its fan-out is accepted. */
  replaceDesignDeleteTargets: (targets: ReadonlyArray<DesignDeleteTarget>) => void;
  /**
   * What the active Design Composer Mode will submit. The composer holds both
   * halves — the mention chip and the URL chips — so it answers for them rather
   * than letting the send path reassemble a collection of its own.
   */
  getDesignCollection: () => DesignComposerCollection;
  /** Get the current prompt/effort/model state for use in send. */
  getSendContext: () => {
    prompt: string;
    images: ComposerImageAttachment[];
    files: ComposerFileAttachment[];
    terminalContexts: TerminalContextDraft[];
    previewAnnotations: PreviewAnnotationPayload[];
    reviewComments: ReviewCommentContext[];
    /** What a Brain Operation would be built from, were the rail set. */
    brainCollection: BrainComposerCollection;
    /** The Design Systems this send targets, as the composer holds them. */
    designSystemMentions: ReadonlyArray<DesignSystemMention>;
    selectedPromptEffort: string | null;
    selectedModelOptionsForDispatch: unknown;
    selectedModelSelection: ModelSelection;
    providerAvailable: boolean;
    selectedProvider: ProviderDriverKind;
    selectedModel: string;
    selectedProviderModels: ReadonlyArray<ServerProvider["models"][number]>;
    interactionMode: ProviderInteractionMode;
    interactionModeEnabled: boolean;
  };
  /** Validate the fully composed text immediately before a provider turn starts. */
  validateProviderInput: (providerInput: string) => boolean;
}

// --------------------------------------------------------------------------
// Props
// --------------------------------------------------------------------------

/** Files or folders waiting on the attach dialog's choice. */
type PendingAttach =
  | { readonly kind: "paths"; readonly attachments: ReadonlyArray<ComposerPathAttachment> }
  | { readonly kind: "files"; readonly files: File[] };

function pendingAttachCount(pending: PendingAttach): number {
  return pending.kind === "paths" ? pending.attachments.length : pending.files.length;
}

export interface ChatComposerProps {
  /**
   * The Action chip the next send carries (ADR 0142), or null for an ordinary
   * message. It lives in the composer draft and clears with the send.
   */
  composerAction: ThreadActionSelection | null;
  onComposerActionChange: (action: ThreadActionSelection | null) => void;
  /** What this thread offers the Actions that require something. */
  actionContext: ThreadActionContext;
  /** Project and user skills that declared themselves Actions. */
  customActionSkills: ReadonlyArray<ComposerCustomActionSkill>;
  /** The project's `alwaysIngestAttachments`: attached files and folders go to Ingest. */
  alwaysIngestAttachments: boolean;
  /** Turn `alwaysIngestAttachments` on for the project, from the attach dialog. */
  onEnableAlwaysIngestAttachments: () => void;
  /** The Next Action's id, suggested above an empty composer with no chip. */
  nextActionId: string | null;
  /** The checkout the Delete Raws picker lists capture folders from. */
  projectRoot: string | null;
  composerDraftTarget: ScopedThreadRef | DraftId;
  environmentId: EnvironmentId;
  attachmentUploadsCapabilityKnown: boolean;
  supportsAttachmentUploads: boolean;
  supportsQuestionAttachments: boolean;
  maxFileAttachmentBytes: number | null;
  routeKind: "server" | "draft";
  routeThreadRef: ScopedThreadRef;
  draftId: DraftId | null;

  // Thread context
  activeThreadId: ThreadId | null;
  activeThreadEnvironmentId: EnvironmentId | undefined;
  /** The routed thread's provider instance, for continuation-locked selection. */
  activeThreadProviderInstanceId: ProviderInstanceId | null;
  /** The routed server thread's shell, present before its detail loads. */
  activeThreadShell: ThreadShell | null;
  /** Timeline messages including optimistic sends, for ArrowUp prompt recall. */
  /** Sent user prompts for ArrowUp recall, narrowed so a delta does not re-render. */
  promptHistoryMessages: ReadonlyArray<ComposerPromptHistoryMessage>;
  isServerThread: boolean;
  isLocalDraftThread: boolean;
  forceExpandedOnMobile: boolean;
  projectSelectionRequired: boolean;

  // Session phase
  phase: SessionPhase;
  /** A turn is in progress on the thread, `starting` included; see `isTurnInProgress`. */
  turnInProgress: boolean;
  isConnecting: boolean;
  isSendBusy: boolean;
  isRevertingCheckpoint?: boolean;
  sendDisabledReason: string | null;
  isPreparingWorktree: boolean;
  bannerItems: readonly ComposerBannerStackItem[];
  environmentUnavailable: {
    readonly label: string;
    readonly connection: EnvironmentConnectionPresentation;
  } | null;

  // Pending approvals / inputs
  activePendingApproval: PendingApproval | null;
  pendingApprovals: PendingApproval[];
  pendingUserInputs: PendingUserInput[];
  activePendingProgress: {
    questionIndex: number;
    isLastQuestion: boolean;
    canAdvance: boolean;
    customAnswer: string;
    activeQuestion: {
      id: string;
      multiSelect?: boolean | undefined;
      allowCustomAnswer?: boolean | undefined;
    } | null;
  } | null;
  activePendingResolvedAnswers: Record<string, unknown> | null;
  activePendingIsResponding: boolean;
  activePendingDraftAnswers: Record<string, PendingUserInputDraftAnswer>;
  activePendingQuestionIndex: number;
  respondingRequestIds: ApprovalRequestId[];

  // Plan
  showPlanFollowUpPrompt: boolean;
  activeProposedPlan: Thread["proposedPlans"][number] | null;
  activeTasksProgress: ComposerTasksProgress | null;
  activeTaskSteps: readonly ComposerTaskStep[] | null;
  threadSyncPhase: ThreadSyncPhase | null;

  // Mode
  runtimeMode: RuntimeMode;
  interactionMode: ProviderInteractionMode;

  // Provider / model
  lockedProvider: ProviderDriverKind | null;
  providerStatuses: ServerProvider[];
  /** False until the environment's server config has arrived at least once. */
  providerCatalogKnown: boolean;
  activeProjectDefaultModelSelection: ModelSelection | null | undefined;
  activeThreadModelSelection: ModelSelection | null | undefined;

  // Context window
  activeContextWindow: ContextWindowSnapshot | null;
  compactThreadUnavailable: boolean;
  compactDisabled: boolean;
  compactDisabledReason: string | null;

  // Misc
  resolvedTheme: "light" | "dark";
  settings: UnifiedSettings;
  keybindings: ResolvedKeybindingsConfig;
  terminalOpen: boolean;
  gitCwd: string | null;
  pullRequestProjectId: ProjectId | null;
  pullRequestRepository: string | null;
  restingControlsHost: HTMLDivElement | null;
  restingControlsHaveLeadingContext: boolean;
  onRestingControlsVisibilityChange: (visible: boolean) => void;
  getTimelineScrollableNode: () => HTMLElement | null;
  isTimelineAtLogicalEnd: () => boolean;
  /** Whether the timeline has more content than fits above the composer. */
  timelineOverflows: boolean;
  onComposerOverlayHeightChange: (height: number) => void;
  /**
   * Whether the desktop resting layout is active. Reported from a layout
   * effect, so it is current before the chat view measures the overlay.
   */
  onRestingChange: (resting: boolean) => void;

  // Refs the parent needs kept in sync
  promptRef: React.RefObject<string>;
  composerImagesRef: React.RefObject<ComposerImageAttachment[]>;
  composerFilesRef: React.RefObject<ComposerFileAttachment[]>;
  composerTerminalContextsRef: React.RefObject<TerminalContextDraft[]>;
  composerRef: React.RefObject<ChatComposerHandle | null>;
  onPageScrollKeyDown: (key: "PageUp" | "PageDown") => void;
  onPageScrollKeyUp: (key: string) => void;
  onPageScrollRelease: () => void;

  // Callbacks
  onCompactContext: () => void;
  onSend: (e?: { preventDefault: () => void }, intent?: ComposerSubmissionIntent) => void;
  /**
   * The composer took focus, which is the earliest honest signal that a prompt
   * is coming. ChatView uses it to bring the provider session up before the
   * send needs it; it must stay cheap and side-effect-free from here.
   */
  onComposerFocus?: () => void;
  onInterrupt: () => void;
  onImplementPlanInNewThread: () => void;
  onRespondToApproval: (
    requestId: ApprovalRequestId,
    decision: ProviderApprovalDecision,
  ) => Promise<unknown>;
  onSelectActivePendingUserInputOption: (questionId: string, optionValue: string) => void;
  onAdvanceActivePendingUserInput: () => void;
  /** Absent where the Environment has no way to dismiss a question unanswered. */
  onDismissActivePendingUserInput?: ((requestId: ApprovalRequestId) => void) | undefined;
  onPreviousActivePendingUserInputQuestion: () => void;
  onChangeActivePendingUserInputCustomAnswer: (
    questionId: string,
    value: string,
    nextCursor: number,
    expandedCursor: number,
    cursorAdjacentToMention: boolean,
  ) => void;

  onProviderModelSelect: (instanceId: ProviderInstanceId, model: string) => void;
  onOpenProviderSetup: (instanceId: ProviderInstanceId) => void;
  getModelDisabledReason: (instanceId: ProviderInstanceId, model: string) => string | null;
  toggleInteractionMode: () => void;
  handleRuntimeModeChange: (mode: RuntimeMode) => void;
  handleInteractionModeChange: (mode: ProviderInteractionMode) => void;

  focusComposer: () => void;
  scheduleComposerFocus: () => void;
  setThreadError: (threadId: ThreadId | null, error: string | null) => void;
  onExpandImage: (preview: ExpandedImagePreview) => void;
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export const ChatComposer = memo(function ChatComposer(props: ChatComposerProps) {
  const {
    composerDraftTarget,
    environmentId,
    attachmentUploadsCapabilityKnown,
    supportsAttachmentUploads,
    supportsQuestionAttachments,
    maxFileAttachmentBytes,
    routeKind,
    routeThreadRef,
    draftId,
    activeThreadId,
    composerAction,
    onComposerActionChange,
    actionContext,
    customActionSkills,
    alwaysIngestAttachments,
    onEnableAlwaysIngestAttachments,
    nextActionId,
    projectRoot,
    activeThreadEnvironmentId: _activeThreadEnvironmentId,
    activeThreadProviderInstanceId,
    promptHistoryMessages,
    isServerThread: _isServerThread,
    isLocalDraftThread: _isLocalDraftThread,
    forceExpandedOnMobile,
    projectSelectionRequired,
    phase,
    turnInProgress,
    isConnecting,
    isSendBusy,
    isRevertingCheckpoint = false,
    sendDisabledReason: externalSendDisabledReason,
    isPreparingWorktree,
    environmentUnavailable,
    activePendingApproval,
    pendingApprovals,
    pendingUserInputs,
    activePendingProgress,
    activePendingResolvedAnswers,
    activePendingIsResponding,
    activePendingDraftAnswers,
    activePendingQuestionIndex,
    respondingRequestIds,
    showPlanFollowUpPrompt,
    activeProposedPlan,
    runtimeMode,
    interactionMode: requestedInteractionMode,
    lockedProvider,
    providerStatuses,
    providerCatalogKnown,
    activeProjectDefaultModelSelection,
    activeThreadModelSelection,
    activeContextWindow,
    compactThreadUnavailable,
    compactDisabled,
    compactDisabledReason,
    resolvedTheme,
    settings,
    keybindings,
    terminalOpen,
    gitCwd,
    pullRequestProjectId,
    pullRequestRepository,
    restingControlsHost,
    restingControlsHaveLeadingContext,
    onRestingControlsVisibilityChange,
    getTimelineScrollableNode,
    isTimelineAtLogicalEnd,
    timelineOverflows,
    onComposerOverlayHeightChange,
    onRestingChange,
    promptRef,
    composerRef,
    composerImagesRef,
    composerFilesRef,
    composerTerminalContextsRef,
    onPageScrollKeyDown,
    onPageScrollKeyUp,
    onPageScrollRelease,
    onCompactContext,
    onSend,
    onComposerFocus,
    onInterrupt,
    onImplementPlanInNewThread,
    onRespondToApproval,
    onSelectActivePendingUserInputOption,
    onAdvanceActivePendingUserInput,
    onDismissActivePendingUserInput,
    onPreviousActivePendingUserInputQuestion,
    onChangeActivePendingUserInputCustomAnswer,
    onProviderModelSelect,
    onOpenProviderSetup,
    getModelDisabledReason,
    toggleInteractionMode,
    handleRuntimeModeChange,
    handleInteractionModeChange,
    focusComposer,
    scheduleComposerFocus,
    setThreadError,
    onExpandImage,
  } = props;
  // The Brain work and the Design Operation the chip carries, if it carries one.
  const brainAction = brainActionOf(composerAction);
  const designOperation = designOperationOf(composerAction);
  // Every Action the `+`, `/` and the Next suggestion offer here, unavailable
  // ones included with their reason.
  const composerActionEntries = useMemo(
    () => listComposerActionEntries({ context: actionContext, customSkills: customActionSkills }),
    [actionContext, customActionSkills],
  );
  const composerActionChipModel = useMemo(
    () => composerActionChip(composerAction, actionContext, customActionSkills),
    [actionContext, composerAction, customActionSkills],
  );
  const composerActionUnavailable = composerActionChipModel?.disabledReason != null;
  // The `/` menu's select handler is built before the pick handler it calls.
  const pickComposerActionRef = useRef<(selection: ThreadActionSelection) => void>(() => {});
  // A Brain Action gathers material rather than writing about it, so what
  // it gathers belongs in its own row beside its text sources — not threaded
  // into the prompt as an inline reference the user then types around.
  const brainComposerOwnsAttachments = brainAction !== null;
  // An Ingest send lists every source by path, so it carries as many as the user adds,
  // each at any size and as the original file.
  const attachmentLimit = composerAttachmentLimit(brainAction);
  const takesSourcesAsIs = composerTakesSourcesAsIs(brainAction);
  const effectiveMaxFileAttachmentBytes = takesSourcesAsIs
    ? Number.POSITIVE_INFINITY
    : maxFileAttachmentBytes;
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const environmentMachine = useEnvironmentMachine(environmentId);
  const activeTasksProgress = props.threadSyncPhase === null ? props.activeTasksProgress : null;
  const activeTaskSteps = props.threadSyncPhase === null ? props.activeTaskSteps : null;
  // ------------------------------------------------------------------
  // Store subscriptions (prompt / images / terminal contexts)
  // ------------------------------------------------------------------
  const composerDraft = useComposerThreadDraft(composerDraftTarget);
  // Live target key, for async flows that must notice a thread switch that
  // happened while they awaited.
  const composerDraftTargetKeyRef = useRef("");
  composerDraftTargetKeyRef.current = composerTargetKey(composerDraftTarget);
  const questionAttachmentTarget =
    pendingUserInputs[0] && activePendingProgress?.activeQuestion
      ? questionAttachmentDraftId(
          environmentId,
          activeThreadId!,
          pendingUserInputs[0].requestId,
          activePendingProgress.activeQuestion.id,
        )
      : null;
  const attachmentDraftTarget = questionAttachmentTarget ?? composerDraftTarget;
  const attachmentDraft = useComposerThreadDraft(attachmentDraftTarget);
  const attachmentTargetKey = composerTargetKey(attachmentDraftTarget);
  // An import that finishes after a draft change must compare against the draft open *now*, not
  // the one captured in the closure that started it.
  const attachmentTargetKeyRef = useRef(attachmentTargetKey);
  attachmentTargetKeyRef.current = attachmentTargetKey;
  const questionPreparations = useQuestionAttachmentPreparation((state) => state.counts);
  const prompt = composerDraft.prompt;
  const composerImages = attachmentDraft.images;
  const composerFiles = attachmentDraft.files;
  // A question answer has no chips: its files live in the question draft and show in the
  // strip. Only the thread prompt's references decide which files leave the strip.
  const inlineFileIdSet = useMemo(() => {
    if (questionAttachmentTarget) return new Set<string>();
    const contextIds = new Set(collectInlineContextIds(prompt));
    return new Set(
      composerFiles
        .filter((file) => contextIds.has(toKindScopedComposerContextId("file", file.id)))
        .map((file) => file.id),
    );
  }, [composerFiles, prompt, questionAttachmentTarget]);
  const composerVideos = composerFiles.filter((file) =>
    isPreviewableComposerVideo(file, environmentId),
  );
  // Files with no chip in the prompt. Ingest shows them in its own row, so the
  // plain file list stands down rather than showing each of them twice.
  const composerUnreferencedFiles = composerOtherFilesForPresentation(
    composerFiles,
    environmentId,
    inlineFileIdSet,
  );
  const composerOtherFiles = brainComposerOwnsAttachments
    ? EMPTY_COMPOSER_FILES
    : composerUnreferencedFiles;
  const composerTerminalContexts = composerDraft.terminalContexts;
  const composerPreviewAnnotations = composerDraft.previewAnnotations;
  const composerDesignSystemMentions = composerDraft.designSystemMentions;
  const composerReviewComments = composerDraft.reviewComments;
  const pendingSnapShotAnimations = useSyncExternalStore(
    subscribeToPendingSnapShotAnimations,
    getPendingSnapShotAnimations,
    getPendingSnapShotAnimations,
  );
  const pendingSnapShotIds = useMemo(
    () => pendingSnapShotAnimationIdsForTarget(pendingSnapShotAnimations, composerDraftTarget),
    [composerDraftTarget, pendingSnapShotAnimations],
  );
  const pendingSnapShotIdSet = useMemo(() => new Set(pendingSnapShotIds), [pendingSnapShotIds]);
  const uncommittedSnapShotIds = pendingSnapShotIds.filter(
    (id) => !composerImages.some((image) => image.id === id),
  );
  const standaloneComposerImages = useMemo(() => {
    const previewAnnotationIds = new Set(
      composerPreviewAnnotations.map((annotation) => annotation.id),
    );
    return composerImages.filter((image) => !previewAnnotationIds.has(image.id));
  }, [composerImages, composerPreviewAnnotations]);
  const nonPersistedComposerImageIds = attachmentDraft.nonPersistedImageIds;
  const uploadsByImageId = useAttachmentUploadStore((state) => state.uploadsByImageId);
  const openPrLink = useOpenPrLink(routeThreadRef);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const previewFile = composerFiles.find((file) => file.id === previewFileId);
  const composerContextActions = useMemo(
    () => ({
      expandImage: (imageId: string) => {
        const preview = buildExpandedImagePreview(composerImages, imageId);
        if (preview) onExpandImage(preview);
      },
      openFile: setPreviewFileId,
      openMention: (path: string) => useRightPanelStore.getState().openFile(routeThreadRef, path),
      expandVideo: (fileId: string) => {
        const file = composerFiles.find((candidate) => candidate.id === fileId);
        if (!file || !isVideoAttachment(file)) return;
        const localPreview = buildExpandedImagePreview([file], file.id);
        if (localPreview) {
          onExpandImage(localPreview);
          return;
        }
      },
      openPullRequest: (event: React.MouseEvent<HTMLElement>, url: string) => {
        openPrLink(event, url);
      },
    }),
    [composerFiles, composerImages, environmentId, onExpandImage, openPrLink, routeThreadRef],
  );
  const composerContextRecords = useMemo(
    () =>
      composerContextRecordsFromDraft({
        terminalContexts: composerTerminalContexts,
        reviewComments: composerReviewComments,
        previewAnnotations: composerPreviewAnnotations,
        images: composerImages,
        files: composerFiles,
        uploadsByImageId,
      }),
    [
      composerFiles,
      composerImages,
      composerPreviewAnnotations,
      composerReviewComments,
      composerTerminalContexts,
      uploadsByImageId,
    ],
  );
  const needsReattachFileCount = composerFiles.filter(composerFileNeedsReattach).length;
  const fileStagingLimit = takesSourcesAsIs
    ? Number.POSITIVE_INFINITY
    : fileAttachmentStagingLimit({
        attachmentUploadsCapabilityKnown,
        supportsAttachmentUploads,
        maxFileAttachmentBytes,
      });
  const fileCapabilityBlockReason = takesSourcesAsIs
    ? null
    : fileAttachmentCapabilityBlockReason({
        files: composerFiles,
        attachmentUploadsCapabilityKnown,
        supportsAttachmentUploads,
        maxFileAttachmentBytes,
      });
  const attachmentBlockReason =
    (questionAttachmentTarget &&
    !supportsQuestionAttachments &&
    (composerImages.length > 0 || composerFiles.length > 0)
      ? "Update this server to send files with question answers"
      : null) ??
    fileCapabilityBlockReason ??
    (supportsAttachmentUploads
      ? needsReattachFileCount > 0
        ? needsReattachFileCount === 1
          ? "Attach the interrupted file again or remove it"
          : "Attach the interrupted files again or remove them"
        : attachmentUploadBlockReason({
            imageIds: composerAttachmentsAwaitingUpload(composerImages, composerFiles).map(
              (attachment) => attachment.id,
            ),
            uploadsByImageId,
            environmentId,
          })
      : null);
  const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
  const addComposerDraftImages = useComposerDraftStore((store) => store.addImages);
  const removeComposerDraftImage = useComposerDraftStore((store) => store.removeImage);
  const addComposerDraftFiles = useComposerDraftStore((store) => store.addFiles);
  const removeComposerDraftFile = useComposerDraftStore((store) => store.removeFile);
  const setComposerDraftFileUpload = useComposerDraftStore((store) => store.setFileUpload);
  const insertComposerDraftTerminalContext = useComposerDraftStore(
    (store) => store.insertTerminalContext,
  );
  const setComposerDraftTerminalContexts = useComposerDraftStore(
    (store) => store.setTerminalContexts,
  );
  const removeComposerDraftPreviewAnnotation = useComposerDraftStore(
    (store) => store.removePreviewAnnotation,
  );
  const removeComposerDraftReviewComment = useComposerDraftStore(
    (store) => store.removeReviewComment,
  );
  const clearComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.clearPersistedAttachments,
  );
  const clearComposerDraftTerminalContexts = useComposerDraftStore(
    (store) => store.clearTerminalContexts,
  );
  const clearComposerDraftPromptAndImages = useComposerDraftStore(
    (store) => store.clearComposerPromptAndImages,
  );
  const syncComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.syncPersistedAttachments,
  );
  const getComposerDraft = useComposerDraftStore((store) => store.getComposerDraft);

  useEffect(() => {
    if (!attachmentUploadsCapabilityKnown) {
      return;
    }
    if (!supportsAttachmentUploads) {
      // The capability can flap on reconnect or version skew. Deleting a
      // persisted hydrated upload here would make the next send fail
      // verification while the file still sits in the draft.
      for (const attachment of attachmentsToReleaseOnUploadCapabilityLoss([
        ...composerImages,
        ...composerFiles,
      ])) {
        releaseAttachmentUpload(attachment.id);
      }
      return;
    }
    const invalidFiles =
      effectiveMaxFileAttachmentBytes === null
        ? composerFiles
        : composerFiles.filter((file) => file.sizeBytes > effectiveMaxFileAttachmentBytes);
    for (const attachment of attachmentsToReleaseOnUploadCapabilityLoss(invalidFiles)) {
      releaseAttachmentUpload(attachment.id);
    }
    const uploadableFiles =
      effectiveMaxFileAttachmentBytes === null
        ? []
        : composerFiles.filter((file) => file.sizeBytes <= effectiveMaxFileAttachmentBytes);
    const uploadableAttachments = [...composerImages, ...uploadableFiles];
    for (const attachment of uploadableAttachments) {
      // A picked host path is read in place and a needs-reattach file has no
      // bytes at all, so neither has an upload to start or to verify.
      if (attachment.type === "file" && !composerFileUploadsBytes(attachment)) {
        continue;
      }
      startAttachmentUpload({
        environmentId,
        image: attachment,
        draftTarget: attachmentDraftTarget,
      });
    }
  }, [
    attachmentUploadsCapabilityKnown,
    attachmentDraftTarget,
    composerFiles,
    composerImages,
    effectiveMaxFileAttachmentBytes,
    environmentId,
    supportsAttachmentUploads,
  ]);

  useEffect(() => {
    for (const file of composerFiles) {
      if (
        !attachmentUploadsCapabilityKnown ||
        !supportsAttachmentUploads ||
        maxFileAttachmentBytes === null ||
        file.sizeBytes > maxFileAttachmentBytes
      ) {
        continue;
      }
      const upload = uploadsByImageId[file.id];
      if (upload?.status === "ready" && upload.environmentId === environmentId) {
        setComposerDraftFileUpload(
          attachmentDraftTarget,
          file.id,
          environmentId,
          upload.attachmentId,
        );
      }
    }
  }, [
    attachmentUploadsCapabilityKnown,
    attachmentDraftTarget,
    composerFiles,
    environmentId,
    maxFileAttachmentBytes,
    setComposerDraftFileUpload,
    supportsAttachmentUploads,
    uploadsByImageId,
  ]);
  const setComposerDraftDesignSystemMentions = useComposerDraftStore(
    (store) => store.setDesignSystemMentions,
  );
  const removeComposerDraftDesignSystemMention = useComposerDraftStore(
    (store) => store.removeDesignSystemMention,
  );

  // ------------------------------------------------------------------
  // Model state
  // ------------------------------------------------------------------
  // Instance-aware projection of the wire provider list. One entry per
  // configured instance (default built-in + any custom `providerInstances.*`),
  // sorted default-first per driver kind for a stable picker order.
  const providerInstanceEntries = useMemo<ReadonlyArray<ProviderInstanceEntry>>(
    () => sortProviderInstanceEntries(deriveProviderInstanceEntries(providerStatuses)),
    [providerStatuses, settings],
  );
  const selectedProviderByThreadId = composerDraft.activeProvider ?? null;
  const {
    selectedProviderEntry,
    requestedDriverKind,
    lockedContinuationGroupKey,
    unavailableProviderInstanceId,
  } = useMemo(
    () =>
      resolveComposerProviderSelection({
        entries: providerInstanceEntries,
        candidateInstanceIds: [
          selectedProviderByThreadId,
          activeThreadProviderInstanceId,
          activeThreadModelSelection?.instanceId,
          activeProjectDefaultModelSelection?.instanceId,
        ],
        lockedProvider,
        lockedInstanceId: activeThreadProviderInstanceId ?? activeThreadModelSelection?.instanceId,
      }),
    [
      activeProjectDefaultModelSelection?.instanceId,
      activeThreadProviderInstanceId,
      activeThreadModelSelection?.instanceId,
      selectedProviderByThreadId,
      lockedProvider,
      providerInstanceEntries,
    ],
  );
  const selectedInstanceId =
    selectedProviderEntry?.instanceId ?? NO_PROVIDER_MODEL_SELECTION.instanceId;
  const noProviderAvailable = selectedProviderEntry === undefined;
  // Before the catalog arrives, every thread resolves to "no provider". Send
  // stays blocked either way; only the chrome waits, keeping the picker with
  // the thread's own selection instead of swapping in the setup button and
  // back once the catalog lands.
  const providerCatalogPending = noProviderAvailable && !providerCatalogKnown;
  const showProviderUnavailable = noProviderAvailable && !providerCatalogPending;
  // A Cloud Workspace's provider is AFAR's to set up, so there is no settings page to send to.
  const providerSetupAvailable = !useEnvironmentManagedByAfar(environmentId);
  const providerSetupInstanceId =
    noProviderAvailable && providerSetupAvailable
      ? (unavailableProviderInstanceId ??
        (lockedProvider === null
          ? providerInstanceEntries.find((entry) => hasProviderSetup(entry.snapshot))?.instanceId
          : undefined))
      : undefined;
  const resolvedCompactDisabledReason =
    compactDisabledReason ?? (noProviderAvailable ? "Compacting is unavailable right now" : null);
  // The driver kind follows the instance that will actually run the turn,
  // which can differ from the persisted selection when that selection is
  // disabled.
  const selectedProvider: ProviderDriverKind =
    selectedProviderEntry?.driverKind ?? requestedDriverKind;

  const { modelOptions: composerModelOptions, selectedModel } = useEffectiveComposerModelState({
    threadRef: composerDraftTarget,
    providers: providerStatuses,
    selectedProvider,
    selectedInstanceId,
    threadModelSelection: activeThreadModelSelection,
    projectModelSelection: activeProjectDefaultModelSelection,
    settings,
  });
  const sendDisabledReason = externalSendDisabledReason ?? attachmentBlockReason;
  const isSendDisabled = sendDisabledReason !== null;
  const selectedProviderStatus = useMemo(
    () => selectedProviderEntry?.snapshot ?? null,
    [selectedProviderEntry],
  );
  const compactCommandAvailable = providerSupportsManualCompaction(selectedProviderEntry);
  const selectedProviderSkills = selectedProviderStatus?.skills ?? [];
  const selectedProviderSlashCommands = selectedProviderStatus?.slashCommands ?? [];
  // A provider reports one catalog of skills and slash commands, not one per
  // working directory, so there is no per-cwd snapshot to fetch or refresh.
  const selectedProviderModels = useMemo<ReadonlyArray<ServerProvider["models"][number]>>(
    () => selectedProviderEntry?.models ?? [],
    [selectedProviderEntry],
  );

  const composerPromptInjectionState = useMemo(
    () => getComposerPromptInjectionState(prompt),
    [prompt],
  );
  const composerProviderState = useMemo(
    () =>
      getComposerProviderState({
        provider: selectedProvider,
        model: selectedModel,
        models: selectedProviderModels,
        promptInjectionState: composerPromptInjectionState,
        modelOptions: composerModelOptions?.[selectedInstanceId],
        planModeEnabled: settings.planModeEnabled,
      }),
    [
      composerModelOptions,
      composerPromptInjectionState,
      selectedInstanceId,
      selectedModel,
      selectedProvider,
      selectedProviderModels,
      settings.planModeEnabled,
    ],
  );

  const selectedPromptEffort = composerProviderState.promptEffort;
  const selectedModelOptionsForDispatch = composerProviderState.modelOptionsForDispatch;
  const { enabled: planModeUiEnabled, interactionMode } = resolveComposerInteractionMode({
    planModeEnabled: settings.planModeEnabled,
    provider: selectedProviderStatus,
    interactionMode: requestedInteractionMode,
  });
  const selectedModelSelection = useMemo<ModelSelection>(
    () => createModelSelection(selectedInstanceId, selectedModel, selectedModelOptionsForDispatch),
    [selectedInstanceId, selectedModel, selectedModelOptionsForDispatch],
  );
  const selectedModelForPicker = selectedModel;
  // Instance-keyed option list so the picker can show each configured
  // instance (built-in + custom) as a first-class sidebar entry. The
  // options are server-reported models plus that exact instance's
  // configured custom models. A selection the catalog has not reported is
  // included as an unavailable row until it does.
  const modelOptionsByInstance = useMemo<
    ReadonlyMap<ProviderInstanceId, ReadonlyArray<AppModelOption>>
  >(() => {
    const out = new Map<ProviderInstanceId, ReadonlyArray<AppModelOption>>();
    for (const entry of providerInstanceEntries) {
      out.set(entry.instanceId, getAppModelOptionsForInstance(settings, entry));
    }
    return out;
  }, [providerInstanceEntries, selectedInstanceId, selectedModelForPicker, settings]);
  const selectedModelForPickerWithCustomFallback = useMemo(() => {
    const currentOptions = modelOptionsByInstance.get(selectedInstanceId) ?? [];
    return currentOptions.some((option) => option.slug === selectedModelForPicker)
      ? selectedModelForPicker
      : (normalizeModelSlug(selectedModelForPicker, selectedProvider) ?? selectedModelForPicker);
  }, [modelOptionsByInstance, selectedInstanceId, selectedModelForPicker, selectedProvider]);

  // ------------------------------------------------------------------
  // Context window
  // ------------------------------------------------------------------
  const activeThreadModelDisplayName = useMemo(
    () => resolveContextWindowModelDisplayName(activeThreadModelSelection, modelOptionsByInstance),
    [activeThreadModelSelection, modelOptionsByInstance],
  );
  const reserveContextWindowMeter = shouldReserveContextWindowMeter({
    meterEnabled: settings.contextWindowMeterEnabled,
    detailLoading: props.threadSyncPhase === "loading",
    threadStarted: threadShellHasStarted(props.activeThreadShell),
    providerReportsContextWindow: selectedProviderStatus
      ? selectedProviderStatus.reportsContextWindow === true
      : null,
  });

  // ------------------------------------------------------------------
  // Composer-local state
  // ------------------------------------------------------------------
  const [composerCursor, setComposerCursor] = useState(() =>
    collapseExpandedComposerCursor(prompt, prompt.length),
  );
  const [composerTrigger, setComposerTrigger] = useState<ComposerTrigger | null>(() =>
    detectComposerTrigger(prompt, prompt.length),
  );
  const [composerHighlightedItemId, setComposerHighlightedItemId] = useState<string | null>(null);
  // Active ArrowUp recall. Cleared on edit and on thread switch.
  const promptHistoryPositionRef = useRef<ComposerPromptHistoryPosition | null>(null);
  const [composerHighlightedSearchKey, setComposerHighlightedSearchKey] = useState<string | null>(
    null,
  );
  const [isDragOverComposer, setIsDragOverComposer] = useState(false);
  const [isComposerFooterCompact, setIsComposerFooterCompact] = useState(false);
  const [isComposerPrimaryActionsCompact, setIsComposerPrimaryActionsCompact] = useState(false);
  const [isComposerModelPickerOpen, setIsComposerModelPickerOpen] = useState(false);
  const isMobileViewport = useMediaQuery("max-sm");
  const {
    isComposerFocused,
    setIsComposerFocused,
    isComposerScrollCollapsed,
    setIsComposerScrollCollapsed,
    restoreAfterTimelineReachedEnd,
  } = useComposerFocusState();
  const [composerSubmissionError, setComposerSubmissionError] = useState<string | null>(null);
  const [providerInputSubmissionError, setProviderInputSubmissionError] = useState<string | null>(
    null,
  );
  const [isBrainDeleteRawsDialogOpen, setIsBrainDeleteRawsDialogOpen] = useState(false);
  const [isEnvironmentFilesDialogOpen, setIsEnvironmentFilesDialogOpen] = useState(false);
  const [brainDeleteTargets, setBrainDeleteTargets] = useState<ReadonlyArray<string>>([]);
  const [isBrainAddTextDialogOpen, setIsBrainAddTextDialogOpen] = useState(false);
  const [brainTextSources, setBrainTextSources] = useState<
    ReadonlyArray<{ readonly id: string; readonly text: string }>
  >([]);
  // Read at send time, so a send started from a stale closure still carries
  // what the chips show.
  const brainComposerCollectionRef = useRef<BrainComposerCollection>(
    EMPTY_BRAIN_COMPOSER_COLLECTION,
  );
  const designComposerCollectionRef = useRef<DesignComposerCollection>(
    EMPTY_DESIGN_COMPOSER_COLLECTION,
  );
  const [composerMenuAnchor, setComposerMenuAnchor] = useState<HTMLDivElement | null>(null);
  const hasWrappedPrompt = useComposerMultilinePrompt(composerMenuAnchor);
  const hasMultilinePrompt = prompt.includes("\n") || hasWrappedPrompt;
  const [isStashMenuOpen, setIsStashMenuOpen] = useState(false);
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = useState(false);
  const [stashPulse, setStashPulse] = useState<{ key: number; active: boolean }>({
    key: 0,
    active: false,
  });
  const { active: panelAnimationsActive, durationMs: panelAnimationDurationMs } =
    usePanelAnimationSettings();
  const isComposerCollapsedMobile =
    isMobileViewport && !forceExpandedOnMobile && !isComposerFocused && !hasMultilinePrompt;
  const [designDeleteTargets, setDesignDeleteTargets] = useState<ReadonlyArray<DesignDeleteTarget>>(
    [],
  );
  const [isDesignDeleteTargetsDialogOpen, setIsDesignDeleteTargetsDialogOpen] = useState(false);
  // Add Example's `Add ▸ URL` entries. Component state rather than draft state,
  // the way Brain's Ingest text sources are: a URL belongs to the send being
  // composed, and folds into its brief the moment that send happens.
  const [designEvidenceUrls, setDesignEvidenceUrls] = useState<
    ReadonlyArray<{ readonly id: string; readonly url: string }>
  >([]);
  const [isDesignAddUrlDialogOpen, setIsDesignAddUrlDialogOpen] = useState(false);

  // ------------------------------------------------------------------
  // Refs
  // ------------------------------------------------------------------
  const composerEditorRef = useRef<ComposerPromptEditorHandle>(null);
  const pasteAsTextShortcutUntilRef = useRef(0);
  const pastedTextFileNamesRef = useRef<{ targetKey: string; names: Set<string> }>({
    targetKey: "",
    names: new Set(),
  });
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  /** Read at send time: a text-free operation carries no prompt and no attachments. */
  const brainComposerCarriesInputRef = useRef(true);
  const composerFormRef = useRef<HTMLFormElement>(null);
  const composerFooterControlsRef = useRef<HTMLDivElement>(null);
  const composerSurfaceRef = useRef<HTMLDivElement>(null);
  const providerInputRejectedRef = useRef(false);
  const composerSelectLockRef = useRef(false);
  const composerMenuOpenRef = useRef(false);
  const composerMenuItemsRef = useRef<ComposerCommandItem[]>([]);
  const activeComposerMenuItemRef = useRef<ComposerCommandItem | null>(null);
  const composerBlurFrameRef = useRef<number | null>(null);
  const mobileComposerExpandFrameRef = useRef<number | null>(null);
  const mobileComposerExpandReleaseFrameRef = useRef<number | null>(null);
  const mobileComposerExpandInFlightRef = useRef(false);
  const composerScrollCollapseTimeoutRef = useRef<number | null>(null);
  const composerScrollCollapseEligibleRef = useRef(false);
  const windowRefocusInFlightRef = useRef(false);
  const composerScrollGestureRef = useRef(createComposerScrollGestureState());
  const stashPulseKeyRef = useRef(0);
  const stashPulseTimeoutRef = useRef<number | null>(null);
  /**
   * Snapshots currently being encoded, keyed by target+prompt+image ids.
   * Keyed rather than boolean so a genuinely different prompt (or a different
   * thread) can still be stashed while an earlier encode is running.
   */
  const stashInFlightRef = useRef<Set<string>>(new Set());
  /**
   * Count of pasted images still being compressed, per thread. Reserved
   * against the attachment limit so concurrent pastes can't overshoot it,
   * and checked before sending so an image cannot move into
   * the next draft.
   */
  const pendingImageCompressionsRef = useRef<Map<string, number>>(new Map());
  const isRevertingCheckpointRef = useRef(isRevertingCheckpoint);
  isRevertingCheckpointRef.current = isRevertingCheckpoint;

  useEffect(() => {
    const armPasteAsTextShortcut = () => {
      // Electron can deliver its native menu action just before the paste
      // event, while browsers normally deliver keydown first. A short deadline
      // bridges both event paths without leaving later pastes in bypass mode.
      pasteAsTextShortcutUntilRef.current = Date.now() + 1_000;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof Node &&
        composerFormRef.current?.contains(event.target) &&
        isPasteAsTextShortcut(event, isMacPlatform(navigator.platform))
      ) {
        armPasteAsTextShortcut();
      }
    };
    const onBlur = () => {
      pasteAsTextShortcutUntilRef.current = 0;
    };
    const onDesktopPasteAsText = () => {
      const activeElement = document.activeElement;
      const blocksPasteToFocus =
        activeElement instanceof Element &&
        activeElement.closest(
          'input, textarea, select, button, a[href], summary, [contenteditable="true"], [contenteditable="plaintext-only"], [role="textbox"], [role="button"], [role="menuitem"], [role="option"]',
        ) !== null;
      if (
        (activeElement instanceof Node && composerFormRef.current?.contains(activeElement)) ||
        !blocksPasteToFocus
      ) {
        armPasteAsTextShortcut();
      }
    };
    window.addEventListener(DESKTOP_PASTE_AS_TEXT_EVENT, onDesktopPasteAsText);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener(DESKTOP_PASTE_AS_TEXT_EVENT, onDesktopPasteAsText);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // ------------------------------------------------------------------
  // Derived: composer send state
  // ------------------------------------------------------------------
  // What an Ingest or Delete send carries. Without a Brain Action it stays empty,
  // so the chip row and its count cost nothing on the ordinary composer.
  const brainComposerCollection = useMemo<BrainComposerCollection>(
    () =>
      brainAction === null
        ? EMPTY_BRAIN_COMPOSER_COLLECTION
        : {
            // Turn order: the send path builds its attachment list
            // images-then-files, and `attachmentIndex` is a position in
            // exactly that list.
            attachments: [
              ...composerImages.map((image) => ({
                id: image.id,
                name: image.name,
                isFolder: false,
              })),
              ...composerFiles.map((file) => ({
                id: file.id,
                name: file.name,
                isFolder: file.isFolder === true,
                // A picked path ingests in place; only material without one
                // rides the turn as bytes for the agent to read (ADR 0109).
                ...(file.hostPath ? { hostPath: file.hostPath } : {}),
              })),
            ],
            textSources: brainTextSources,
            deleteTargets: brainDeleteTargets,
          },
    [brainAction, brainDeleteTargets, brainTextSources, composerFiles, composerImages],
  );
  // ------------------------------------------------------------------
  // Derived: composer trigger / menu
  // ------------------------------------------------------------------
  const composerTriggerKind = composerTrigger?.kind ?? null;
  const pathTriggerQuery = composerTrigger?.kind === "path" ? composerTrigger.query : "";
  const pullRequestTriggerQuery =
    composerTrigger?.kind === "pull-request" ? composerTrigger.query : "";
  const pullRequestTextQuery =
    composerTriggerKind === "pull-request" &&
    pullRequestTriggerQuery.length > 0 &&
    !/^\d+$/u.test(pullRequestTriggerQuery)
      ? pullRequestTriggerQuery
      : null;
  const debouncedPullRequestTextQuery = useDebouncedValue(pullRequestTextQuery, 180);
  const settledPullRequestTextQuery =
    pullRequestTextQuery === debouncedPullRequestTextQuery ? pullRequestTextQuery : null;
  const isPathTrigger = composerTriggerKind === "path";
  const workspaceEntries = useComposerPathSearch({
    environmentId,
    cwd: isPathTrigger ? gitCwd : null,
    query: isPathTrigger ? pathTriggerQuery : null,
  });

  const [isDesignTargetPickerOpen, setIsDesignTargetPickerOpen] = useState(false);
  // Discovery runs for the three surfaces that need the project's Design
  // Systems: the `@` menu, the footer's own target picker, and a composer
  // holding a Design System Mention — a held mention is the next send's target,
  // and the Design System under it may have been renamed or deleted since it
  // was picked.
  //
  // The footer picker has to be named here. Reading the listing without asking
  // for it is what left it opening on "this project has no Design System yet"
  // in exactly the state it exists for: a target mode with nothing picked, in
  // which neither of the other two reasons holds.
  const holdsDesignSystemMentions = composerDesignSystemMentions.length > 0;
  const needsDesignSystemListing =
    isPathTrigger || holdsDesignSystemMentions || isDesignTargetPickerOpen;
  const designSystemDiscovery = useDesignSystemMentionDiscovery({
    environmentId,
    cwd: needsDesignSystemListing ? projectRoot : null,
    enabled: needsDesignSystemListing,
  });
  // Opening either picker takes a fresh scan. The query stays mounted while the
  // composer holds a mention, so mounting is no longer what makes it re-read —
  // and a Design System renamed since the last open would otherwise be offered
  // under its old name at a path it has left.
  const aDesignSystemPickerIsOpen = isPathTrigger || isDesignTargetPickerOpen;
  useEffect(() => {
    if (!aDesignSystemPickerIsOpen) return;
    designSystemDiscovery.refresh();
    // The refresh belongs to a picker opening, not to every render of one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aDesignSystemPickerIsOpen]);

  /**
   * The held mentions against what is on disk now. Nothing is judged while
   * discovery is still answering, or when it failed: a chip that blinks
   * "missing" because a listing had not arrived is worse than a stale name.
   */
  const resolvedDesignSystemMentions = useMemo(
    () =>
      !holdsDesignSystemMentions ||
      designSystemDiscovery.isPending ||
      designSystemDiscovery.error !== null
        ? null
        : composerDesignSystemMentions.map((mention) =>
            resolveDesignSystemMention({ mention, targets: designSystemDiscovery.targets }),
          ),
    [
      composerDesignSystemMentions,
      designSystemDiscovery.error,
      designSystemDiscovery.isPending,
      designSystemDiscovery.targets,
      holdsDesignSystemMentions,
    ],
  );
  const missingDesignSystemMentionPaths = useMemo(
    () =>
      new Set(
        (resolvedDesignSystemMentions ?? [])
          .filter((entry) => entry.state === "missing")
          .map((entry) => entry.mention.path),
      ),
    [resolvedDesignSystemMentions],
  );
  // A rename moves the Design System's directory, so the held mention is
  // rewritten to what it now points at rather than left naming a path nothing
  // is behind. Sent messages keep the mention they were sent with (ADR 0116).
  useEffect(() => {
    if (resolvedDesignSystemMentions === null) return;
    if (!resolvedDesignSystemMentions.some((entry) => entry.state === "renamed")) return;
    setComposerDraftDesignSystemMentions(
      composerDraftTarget,
      resolvedDesignSystemMentions.map((entry) => entry.mention),
    );
  }, [composerDraftTarget, resolvedDesignSystemMentions, setComposerDraftDesignSystemMentions]);

  // What a Design send carries besides its attachments: the single mention the
  // target modes collect, Add Example's URLs, and Delete's own picked targets.
  const designComposerCollection = useMemo<DesignComposerCollection>(() => {
    if (designOperation === null) return EMPTY_DESIGN_COMPOSER_COLLECTION;
    return {
      // A target the App cannot find is not a target: an operation sent at it
      // would run against a path its Design System has left.
      target:
        composerDesignSystemMentions.find(
          (mention) => !missingDesignSystemMentionPaths.has(mention.path),
        ) ?? null,
      deleteTargets: designOperation === "delete-design-system" ? designDeleteTargets : [],
      evidenceUrls: designComposerOperationCollectsEvidenceUrls(designOperation)
        ? designEvidenceUrls.map((entry) => entry.url)
        : [],
      // Images and files in the order the send path builds them: Add Example
      // weighs a dropped file exactly as it weighs a pasted URL.
      attachmentCount: composerImages.length + composerFiles.length,
    };
  }, [
    composerDesignSystemMentions,
    composerFiles.length,
    composerImages.length,
    designOperation,
    designDeleteTargets,
    designEvidenceUrls,
    missingDesignSystemMentionPaths,
  ]);
  /**
   * Commits a Design System pick into the composer draft. Shared by the footer
   * picker and the `@` menu: the single-select rule for a target mode lives in
   * `selectDesignComposerMentions`, and two call sites reaching for it
   * separately is how they come to disagree about what a second pick does.
   */
  const pickDesignSystemTarget = useCallback(
    (target: DesignSystemMentionTarget) => {
      setComposerDraftDesignSystemMentions(
        composerDraftTarget,
        selectDesignComposerMentions({
          mode: designOperation,
          existing: composerDesignSystemMentions,
          picked: {
            type: "design-system",
            // The identity a rename preserves, so the held mention can follow
            // the Design System if it moves before this is sent.
            ...(target.id === undefined ? {} : { id: target.id }),
            name: target.name,
            path: target.path,
          },
        }),
      );
    },
    [
      composerDesignSystemMentions,
      composerDraftTarget,
      designOperation,
      setComposerDraftDesignSystemMentions,
    ],
  );
  const designTargetPicker = useMemo(
    () =>
      describeDesignComposerTargetPicker({
        mode: designOperation,
        collection: designComposerCollection,
      }),
    [designComposerCollection, designOperation],
  );
  const matchingDesignSystems = useMemo(
    () =>
      searchDesignSystemMentionTargets(
        designSystemDiscovery.targets,
        pathTriggerQuery,
        composerDesignSystemMentions.map((mention) => mention.path),
      ),
    [composerDesignSystemMentions, designSystemDiscovery.targets, pathTriggerQuery],
  );
  const designComposerCollectedCount =
    designOperation === null
      ? 0
      : countDesignComposerCollected(designOperation, designComposerCollection);
  const designComposerEvidenceCount = countDesignComposerEvidence(designComposerCollection);

  const composerSendState = useMemo(() => {
    const state = deriveComposerSendState({
      prompt,
      imageCount: composerImages.length + composerFiles.length,
      terminalContexts: composerTerminalContexts,
      elementContextCount: composerPreviewAnnotations.length + composerReviewComments.length,
    });
    // An Action that cannot run here does not send; its chip says why.
    if (composerActionUnavailable) return { ...state, hasSendableContent: false };
    if (brainAction !== null) {
      // Lint sends on its action alone and Delete on its picked targets, so a
      // Brain send is not gated on typed text or attachments.
      return {
        ...state,
        hasSendableContent: brainAwareComposerCanSend({
          action: brainAction,
          collection: brainComposerCollection,
          hasComposerContent: state.hasSendableContent,
        }),
      };
    }
    if (designOperation !== null) {
      // The target is whatever the picked mode collects — the Design System
      // Mention the composer is holding, or Delete's picked rows. It is a
      // precondition rather than a validation: burning a provider turn to learn
      // you forgot to type `@` is the worst version of this. An unpicked
      // composer collects nothing and sends on its text, like any other thread.
      return {
        ...state,
        hasSendableContent: designComposerOperationCanSend({
          mode: designOperation,
          collectedCount: designComposerCollectedCount,
          evidenceCount: designComposerEvidenceCount,
          hasComposerContent: state.hasSendableContent,
        }),
      };
    }
    return state;
  }, [
    brainComposerCollection,
    brainAction,
    composerFiles.length,
    composerImages.length,
    composerPreviewAnnotations.length,
    composerReviewComments.length,
    composerTerminalContexts,
    prompt,
    designComposerCollectedCount,
    designComposerEvidenceCount,
    designOperation,
    composerActionUnavailable,
  ]);
  const compactSlashCommandAvailable =
    composerTrigger?.kind === "slash-command" &&
    prompt.slice(0, composerTrigger.rangeStart).trim() === "" &&
    !compactThreadUnavailable &&
    prompt.slice(composerTrigger.rangeEnd).trim() === "" &&
    composerImages.length + composerFiles.length === 0 &&
    composerDraft.persistedAttachments.length === 0 &&
    composerTerminalContexts.length === 0 &&
    composerPreviewAnnotations.length === 0 &&
    composerReviewComments.length === 0;

  const pullRequestListTargets = useMemo(
    () =>
      composerTriggerKind !== "pull-request" ||
      pullRequestProjectId === null ||
      (pullRequestTextQuery !== null && settledPullRequestTextQuery === null)
        ? EMPTY_PULL_REQUEST_LIST_TARGETS
        : [
            {
              environmentId,
              input: {
                state: "all" as const,
                projectId: pullRequestProjectId,
                limit: COMPOSER_PULL_REQUEST_LIST_LIMIT,
                ...(settledPullRequestTextQuery === null
                  ? {}
                  : { query: settledPullRequestTextQuery }),
              },
            },
          ],
    [
      composerTriggerKind,
      environmentId,
      pullRequestProjectId,
      pullRequestTextQuery,
      settledPullRequestTextQuery,
    ],
  );
  const pullRequestLookup = usePullRequestList(pullRequestListTargets);
  const pullRequestTriggerNumber = useMemo(() => {
    if (composerTrigger?.kind !== "pull-request" || composerTrigger.query.length === 0) {
      return null;
    }
    const number = Number(composerTrigger.query);
    return Number.isSafeInteger(number) && number > 0 ? number : null;
  }, [composerTrigger]);
  const debouncedPullRequestNumber = useDebouncedValue(pullRequestTriggerNumber, 180);
  // Finding a pull request by exact number outside the recent list would need
  // an authority epoch the composer has no source for, so matches come from it.
  const composerMenuItems = useMemo<ComposerCommandItem[]>(() => {
    if (!composerTrigger) return [];
    if (composerTrigger.kind === "path") {
      const selectedPaths = new Set(composerDesignSystemMentions.map((mention) => mention.path));
      return [
        // Design Systems rank above files: with a Design Action the `@` menu is
        // how an operation gets its target, and a target is not a file.
        ...matchingDesignSystems.map((target) => ({
          id: `design-system:${target.path}`,
          type: "design-system" as const,
          target,
          label: target.name,
          description: target.description ?? "No description provided.",
          selected: selectedPaths.has(target.path),
        })),
        ...workspaceEntries.entries.map((entry) => ({
          id: `path:${entry.kind}:${entry.path}`,
          type: "path" as const,
          path: entry.path,
          pathKind: entry.kind,
          label: basenameOfPath(entry.path),
          description: entry.path.slice(0, Math.max(0, entry.path.lastIndexOf("/"))),
        })),
      ];
    }
    if (composerTrigger.kind === "slash-command") {
      const builtInSlashCommandItems = [
        {
          id: "slash:model",
          type: "slash-command",
          command: "model",
          label: "/model",
          description: "Switch response model for this thread",
        },
        ...(planModeUiEnabled
          ? ([
              {
                id: "slash:plan",
                type: "slash-command",
                command: "plan",
                label: "/plan",
                description: "Switch this thread into plan mode",
              },
              {
                id: "slash:default",
                type: "slash-command",
                command: "default",
                label: "/default",
                description: "Switch this thread back to normal build mode",
              },
            ] as const)
          : []),
      ] satisfies ReadonlyArray<Extract<ComposerCommandItem, { type: "slash-command" }>>;
      const slashMenuSkills = getProviderSkillsForSlashMenu(
        selectedProviderSkills,
        settings.showSkillsInSlashMenu,
      );
      const providerSlashCommandItems = getProviderSlashCommandsForSlashMenu(
        selectedProviderSlashCommands,
        slashMenuSkills,
      ).map((command) => ({
        id: `provider-slash-command:${selectedProvider}:${command.name}`,
        type: "provider-slash-command" as const,
        provider: selectedProvider,
        command,
        label: `/${command.name}`,
        description: command.description ?? command.input?.hint ?? "Run provider command",
      }));
      const query = composerTrigger.query.trim().toLowerCase();
      const skillItems = slashMenuSkills.map((skill) => ({
        id: `skill:${selectedProvider}:${skill.name}`,
        type: "skill" as const,
        provider: selectedProvider,
        skill,
        label: `/skill:${skill.name}`,
        description:
          skill.shortDescription ??
          skill.description ??
          (skill.scope ? `${skill.scope} skill` : ""),
      }));
      const visibleProviderSlashCommandItems = providerSlashCommandItems.filter(
        (item) => item.command.name !== "compact" || compactSlashCommandAvailable,
      );
      const slashCommandItems = slashCommandItemsForPromptPosition(
        [...builtInSlashCommandItems, ...visibleProviderSlashCommandItems, ...skillItems],
        composerTrigger.rangeStart === 0,
      );
      // The Actions lead `/` at the start of the message: picking one sets the
      // chip, so it has nothing to do in the middle of a sentence.
      const actionItems =
        composerTrigger.rangeStart === 0 && activePendingProgress === null
          ? matchComposerActionEntries(composerActionEntries, query).map(
              (entry): ComposerCommandItem => ({
                id: `action:${entry.selection.id}`,
                type: "action",
                entry,
                label: entry.label,
                description: entry.disabledReason ?? entry.description,
              }),
            )
          : [];
      return [...actionItems, ...searchSlashCommandItems(slashCommandItems, query)];
    }
    if (composerTrigger.kind === "skill") {
      return searchProviderSkills(selectedProviderSkills, composerTrigger.query).map((skill) => ({
        id: `skill:${selectedProvider}:${skill.name}`,
        type: "skill" as const,
        provider: selectedProvider,
        skill,
        label: formatProviderSkillDisplayName(skill),
        description:
          skill.shortDescription ??
          skill.description ??
          (skill.scope ? `${skill.scope} skill` : "Run provider skill"),
      }));
    }
    if (
      composerTrigger.kind === "pull-request" &&
      pullRequestProjectId !== null &&
      pullRequestRepository !== null
    ) {
      const matches = /^\d*$/u.test(composerTrigger.query)
        ? filterComposerPullRequestMatches({
            entries: pullRequestLookup.data?.entries ?? [],
            projectId: pullRequestProjectId,
            repository: pullRequestRepository,
            query: composerTrigger.query,
            limit: COMPOSER_PULL_REQUEST_RESULT_LIMIT,
          })
        : rankPullRequestMatches(
            (pullRequestLookup.data?.entries ?? []).filter(
              (entry) =>
                entry.projectId === pullRequestProjectId &&
                entry.repository.trim().toLowerCase() ===
                  pullRequestRepository.trim().toLowerCase() &&
                matchesPullRequestQuery(entry, composerTrigger.query),
            ),
            composerTrigger.query,
          ).slice(0, COMPOSER_PULL_REQUEST_RESULT_LIMIT);
      return matches.map((pullRequest) => ({
        id: `pull-request:${pullRequest.projectId}:${pullRequest.repository}:${pullRequest.number}`,
        type: "pull-request",
        pullRequest: {
          number: pullRequest.number,
          title: pullRequest.title,
          url: pullRequest.url,
          headBranch: pullRequest.headRefName,
          baseBranch: pullRequest.baseRefName,
          state: pullRequest.state,
          isDraft: pullRequest.isDraft,
        },
        label: `#${pullRequest.number}`,
        description: pullRequest.title,
      }));
    }
    return [];
  }, [
    activePendingProgress,
    compactSlashCommandAvailable,
    composerActionEntries,
    composerDesignSystemMentions,
    composerTrigger,
    matchingDesignSystems,
    planModeUiEnabled,
    pullRequestLookup.data,
    pullRequestProjectId,
    pullRequestRepository,
    pullRequestTriggerNumber,
    selectedProvider,
    selectedProviderSkills,
    selectedProviderSlashCommands,
    selectedProviderStatus,
    settings.showSkillsInSlashMenu,
    workspaceEntries.entries,
  ]);

  const composerMenuOpen = Boolean(composerTrigger);
  const composerMenuSearchKey = composerTrigger
    ? `${composerTrigger.kind}:${composerTrigger.query.trim().toLowerCase()}`
    : null;
  const activeComposerMenuItem = useMemo(() => {
    const activeItemId = resolveComposerMenuActiveItemId({
      items: composerMenuItems,
      highlightedItemId: composerHighlightedItemId,
      currentSearchKey: composerMenuSearchKey,
      highlightedSearchKey: composerHighlightedSearchKey,
    });
    return composerMenuItems.find((item) => item.id === activeItemId) ?? null;
  }, [
    composerHighlightedItemId,
    composerHighlightedSearchKey,
    composerMenuItems,
    composerMenuSearchKey,
  ]);

  composerMenuOpenRef.current = composerMenuOpen;
  composerMenuItemsRef.current = composerMenuItems;
  activeComposerMenuItemRef.current = activeComposerMenuItem;

  const nonPersistedComposerImageIdSet = useMemo(
    () => new Set(nonPersistedComposerImageIds),
    [nonPersistedComposerImageIds],
  );

  const isComposerApprovalState = activePendingApproval !== null;
  const activePendingUserInput = pendingUserInputs[0] ?? null;
  const isChoiceOnlyPendingQuestion =
    activePendingProgress?.activeQuestion?.allowCustomAnswer === false;
  const showComposerTopDrawer =
    isComposerApprovalState ||
    pendingUserInputs.length > 0 ||
    (!isComposerCollapsedMobile && showPlanFollowUpPrompt && activeProposedPlan !== null);
  const showCollapsedMobilePromptRow =
    isComposerCollapsedMobile && !isComposerApprovalState && pendingUserInputs.length === 0;
  const questionTakesAttachments =
    activePendingProgress !== null &&
    supportsQuestionAttachments &&
    activePendingProgress.activeQuestion?.allowCustomAnswer !== false;
  // What the `+` can add to this message. Lint, Delete Brain sources and
  // Delete design system carry no turn attachments, so their files items go.
  const composerTakesAttachments =
    fileStagingLimit !== null &&
    (designOperation === null || designComposerOperationTakesAttachments(designOperation)) &&
    brainActionCarriesComposerInput(brainAction) &&
    (activePendingProgress === null || questionTakesAttachments);
  // A provider question owns the composer while it is up, so only its
  // attachments are offered then — never an Action.
  const showComposerAttachAction = activePendingProgress === null || questionTakesAttachments;
  const composerFooterHasWideActions = showPlanFollowUpPrompt || activePendingProgress !== null;
  const composerFooterActionLayoutKey = useMemo(() => {
    if (activePendingProgress) {
      return `pending:${activePendingProgress.questionIndex}:${activePendingProgress.isLastQuestion}:${activePendingIsResponding}`;
    }
    if (turnInProgress) {
      return "running";
    }
    if (showPlanFollowUpPrompt) {
      return prompt.trim().length > 0 ? "plan:refine" : "plan:implement";
    }
    return `idle:${composerSendState.hasSendableContent}:${isSendBusy}:${isConnecting}:${isPreparingWorktree}`;
  }, [
    activePendingIsResponding,
    activePendingProgress,
    composerSendState.hasSendableContent,
    isConnecting,
    isPreparingWorktree,
    isSendBusy,
    turnInProgress,
    prompt,
    showPlanFollowUpPrompt,
  ]);

  const isComposerMenuLoading =
    (composerTriggerKind === "path" && pathTriggerQuery.length > 0 && workspaceEntries.isPending) ||
    (composerTriggerKind === "pull-request" &&
      pullRequestProjectId !== null &&
      pullRequestRepository !== null &&
      (pullRequestLookup.isPending ||
        pullRequestTextQuery !== debouncedPullRequestTextQuery ||
        pullRequestTriggerNumber !== debouncedPullRequestNumber));
  const composerMenuEmptyState = useMemo(() => {
    if (composerTriggerKind === "skill") {
      return "No skills found. Try / to browse provider commands.";
    }
    if (composerTriggerKind === "pull-request") {
      if (pullRequestProjectId === null || pullRequestRepository === null) {
        return "Pull requests are not available for this project.";
      }
      if (
        pullRequestLookup.error !== null ||
        pullRequestLookup.data?.unavailableProjectIds.includes(pullRequestProjectId)
      ) {
        return "Pull requests could not be read for this project.";
      }
      return composerTrigger?.query
        ? `No pull request matches ${composerTrigger.query}.`
        : "No pull requests found in this repository.";
    }
    return composerTriggerKind === "path"
      ? "No matching files or folders."
      : "No matching command.";
  }, [
    composerTrigger,
    composerTriggerKind,
    pullRequestLookup.data?.unavailableProjectIds,
    pullRequestLookup.error,
    pullRequestProjectId,
    pullRequestRepository,
  ]);

  // ------------------------------------------------------------------
  // Provider traits UI
  // ------------------------------------------------------------------
  const setPromptFromTraits = useCallback(
    (nextPrompt: string) => {
      if (nextPrompt === promptRef.current) {
        scheduleComposerFocus();
        return;
      }
      promptRef.current = nextPrompt;
      setComposerDraftPrompt(composerDraftTarget, nextPrompt);
      const nextCursor = collapseExpandedComposerCursor(nextPrompt, nextPrompt.length);
      setComposerCursor(nextCursor);
      setComposerTrigger(detectComposerTrigger(nextPrompt, nextPrompt.length));
      scheduleComposerFocus();
    },
    [composerDraftTarget, promptRef, scheduleComposerFocus, setComposerDraftPrompt],
  );

  const providerTraitsMenuContent = renderProviderTraitsMenuContent({
    provider: selectedProvider,
    instanceId: selectedInstanceId,
    ...(routeKind === "server" ? { threadRef: routeThreadRef } : {}),
    ...(routeKind === "draft" && draftId ? { draftId } : {}),
    model: selectedModel,
    models: selectedProviderModels,
    modelOptions: composerModelOptions?.[selectedInstanceId],
    prompt,
    onPromptChange: setPromptFromTraits,
    planModeEnabled: settings.planModeEnabled,
  });
  const providerTraitsPickerInput = {
    provider: selectedProvider,
    instanceId: selectedInstanceId,
    ...(routeKind === "server" ? { threadRef: routeThreadRef } : {}),
    ...(routeKind === "draft" && draftId ? { draftId } : {}),
    model: selectedModel,
    models: selectedProviderModels,
    modelOptions: composerModelOptions?.[selectedInstanceId],
    prompt,
    onPromptChange: setPromptFromTraits,
    planModeEnabled: settings.planModeEnabled,
    isComposerOwned: true,
  } satisfies Parameters<typeof renderProviderTraitsPicker>[0];
  const providerTraitsPicker = renderProviderTraitsPicker(providerTraitsPickerInput);
  // Without an external host the resting controls get their own row inside
  // the composer, so resting never takes the model or mode out of reach.
  const [inlineRestingControlsHost, setInlineRestingControlsHost] = useState<HTMLDivElement | null>(
    null,
  );
  const {
    controlsRef: restingComposerControlsRef,
    hiddenBlockCount: restingControlsHiddenBlockCount,
    controlsVisible: restingControlsVisible,
  } = useRestingComposerControlsLayout(restingControlsHost ?? inlineRestingControlsHost);
  const pendingPrimaryAction = useMemo(
    () =>
      activePendingProgress
        ? {
            questionIndex: activePendingProgress.questionIndex,
            isLastQuestion: activePendingProgress.isLastQuestion,
            canAdvance:
              activePendingProgress.canAdvance &&
              !attachmentBlockReason &&
              !(questionPreparations[attachmentTargetKey] ?? 0),
            isResponding: activePendingIsResponding,
            isComplete: Boolean(activePendingResolvedAnswers),
          }
        : null,
    [
      activePendingIsResponding,
      activePendingProgress,
      activePendingResolvedAnswers,
      attachmentBlockReason,
      questionPreparations,
      attachmentTargetKey,
    ],
  );
  const collapsedComposerPrimaryActionDisabled =
    turnInProgress ||
    isSendBusy ||
    isSendDisabled ||
    isConnecting ||
    noProviderAvailable ||
    projectSelectionRequired ||
    environmentUnavailable !== null ||
    !composerSendState.hasSendableContent;
  const collapsedComposerPrimaryActionLabel = "Send message";
  const showMobilePendingAnswerActions =
    isMobileViewport && !isComposerCollapsedMobile && pendingPrimaryAction !== null;

  // ------------------------------------------------------------------
  // Prompt helpers
  // ------------------------------------------------------------------
  const setPrompt = useCallback(
    (nextPrompt: string) => {
      setComposerDraftPrompt(composerDraftTarget, nextPrompt);
    },
    [composerDraftTarget, setComposerDraftPrompt],
  );

  const addComposerImage = useCallback(
    (image: ComposerImageAttachment) =>
      addComposerDraftImages(attachmentDraftTarget, [image], { attachmentLimit }),
    [attachmentDraftTarget, addComposerDraftImages, attachmentLimit],
  );

  const addComposerImagesToDraft = useCallback(
    (images: ComposerImageAttachment[]) =>
      addComposerDraftImages(attachmentDraftTarget, images, { attachmentLimit }),
    [attachmentDraftTarget, addComposerDraftImages, attachmentLimit],
  );

  const addComposerFilesToDraft = useCallback(
    (files: ComposerFileAttachment[]) =>
      addComposerDraftFiles(attachmentDraftTarget, files, {
        appendReference: questionAttachmentTarget === null && !brainComposerOwnsAttachments,
        attachmentLimit,
      }),
    [
      addComposerDraftFiles,
      attachmentDraftTarget,
      attachmentLimit,
      brainComposerOwnsAttachments,
      questionAttachmentTarget,
    ],
  );

  const removeComposerImageFromDraft = useCallback(
    (imageId: string) => {
      if (questionAttachmentTarget && activePendingIsResponding) return;
      releaseAttachmentUpload(imageId);
      removeComposerDraftImage(attachmentDraftTarget, imageId);
    },
    [
      attachmentDraftTarget,
      questionAttachmentTarget,
      activePendingIsResponding,
      removeComposerDraftImage,
    ],
  );

  const removeComposerFileFromDraft = useCallback(
    (fileId: string) => {
      if (questionAttachmentTarget && activePendingIsResponding) return;
      // Release by the draft attachment, not the bare queue key: a hydrated
      // file's upload lives server-side under its persisted attachment id.
      const file = composerFilesRef.current.find((candidate) => candidate.id === fileId);
      if (file) {
        releaseDraftAttachment(file);
      } else {
        releaseAttachmentUpload(fileId);
      }
      removeComposerDraftFile(attachmentDraftTarget, fileId);
    },
    [
      attachmentDraftTarget,
      questionAttachmentTarget,
      activePendingIsResponding,
      composerFilesRef,
      removeComposerDraftFile,
    ],
  );

  const addComposerDraftTerminalContexts = useComposerDraftStore(
    (store) => store.addTerminalContexts,
  );
  const addComposerDraftReviewComment = useComposerDraftStore((store) => store.addReviewComment);
  const addComposerDraftPreviewAnnotation = useComposerDraftStore(
    (store) => store.addPreviewAnnotation,
  );
  const buildContextClipboardFragment = useCallback(
    (contextIds: ReadonlyArray<string>): string | null => {
      const wanted = new Set(contextIds);
      // An annotation's screenshot is referenced by the annotation record, not by the copied
      // text. Pull it in so the round-trip keeps the image the annotation points at.
      for (const annotation of composerPreviewAnnotations) {
        if (
          wanted.has(previewAnnotationContextId(annotation.id)) &&
          composerImages.some((image) => image.id === annotation.id)
        ) {
          wanted.add(toKindScopedComposerContextId("image", annotation.id));
        }
      }
      const records: ComposerContextRecord[] = [
        ...composerTerminalContexts
          .filter((c) => wanted.has(terminalContextReference(c).contextId))
          .map(terminalContextRecord),
        ...composerReviewComments
          .filter((c) => wanted.has(reviewCommentContextId(c.id)))
          .map(reviewCommentContextRecord),
        ...composerPreviewAnnotations
          .filter((a) => wanted.has(previewAnnotationContextId(a.id)))
          .map((annotation) =>
            previewAnnotationContextRecord(annotation, {
              screenshotContextId: composerImages.some((image) => image.id === annotation.id)
                ? annotation.id
                : undefined,
            }),
          ),
        ...[...composerImages, ...composerFiles]
          .filter((attachment) =>
            wanted.has(toKindScopedComposerContextId(attachment.type, attachment.id)),
          )
          .flatMap((attachment) => {
            const record = uploadedAttachmentContextRecord(
              attachment,
              uploadsByImageId[attachment.id],
            );
            return record ? [record] : [];
          }),
      ];
      if (records.length === 0) return null;
      return encodeComposerContextFragment({
        version: 1,
        source: { environmentId, ...(activeThreadId ? { threadId: activeThreadId } : {}) },
        records,
      });
    },
    [
      activeThreadId,
      composerFiles,
      composerImages,
      composerPreviewAnnotations,
      composerReviewComments,
      composerTerminalContexts,
      environmentId,
      uploadsByImageId,
    ],
  );
  const createAssetUrl = useAtomQueryRunner(assetEnvironment.createUrl, { reportFailure: false });
  /**
   * Bytes for a pasted image or file come back through the source environment's asset URL
   * (the client is the only party that can reach both) and re-enter this draft as a normal
   * attachment under a fresh id. The pasted chip is rewritten to that id and reads as
   * unresolved until the bytes land; a failed transfer says so and leaves the chip to remove.
   */
  const runAttachmentImport = useCallback(
    async (
      record: Extract<ComposerContextRecord, { kind: "image" | "file" }>,
      localId: string,
      sourceEnvironmentId: EnvironmentId,
      importTargetKey: string,
    ) => {
      const fail = (reason: string) => {
        toastManager.add({
          type: "error",
          title: `Couldn't bring ${record.name} into this message`,
          description: `${reason} Remove the chip or attach the file again.`,
        });
      };
      const sourceConnection = readPreparedConnection(sourceEnvironmentId);
      if (!sourceConnection) {
        fail("The environment it came from is not connected.");
        return;
      }
      const result = await createAssetUrl({
        environmentId: sourceEnvironmentId,
        input: {
          resource: {
            _tag: "attachment",
            threadId: routeThreadRef.threadId,
            attachmentId: record.attachmentId,
          },
        },
      });
      const url =
        result._tag === "Success"
          ? resolveAssetUrl(sourceConnection.httpBaseUrl, result.value.relativeUrl)
          : null;
      if (!url) {
        fail("The original attachment is no longer available.");
        return;
      }
      let blob: Blob;
      try {
        const response = await fetch(url, { signal: AbortSignal.timeout(60_000) });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        blob = await response.blob();
      } catch {
        fail("Downloading it from the source failed.");
        return;
      }
      const file = new File([blob], record.name, { type: record.mimeType || blob.type });
      // The draft these bytes belong to may have been sent or switched away from while they
      // downloaded. Dropping them here keeps them out of whatever draft is open now.
      if (attachmentTargetKeyRef.current !== importTargetKey) return;
      if (record.kind === "image") {
        const accepted = addComposerImage({
          type: "image",
          id: localId,
          name: record.name,
          mimeType: file.type,
          sizeBytes: file.size,
          previewUrl: URL.createObjectURL(file),
          file,
        });
        if (!accepted.includes(localId))
          fail("The draft rejected this attachment (duplicate or attachment limit reached).");
      } else {
        const accepted = addComposerFilesToDraft([
          {
            type: "file",
            id: localId,
            name: record.name,
            mimeType: file.type,
            sizeBytes: file.size,
            file,
          },
        ]);
        if (!accepted.includes(localId))
          fail("The draft rejected this attachment (duplicate or attachment limit reached).");
      }
    },
    [addComposerFilesToDraft, addComposerImage, attachmentTargetKey, createAssetUrl],
  );
  const importAttachmentRecord = useCallback(
    async (
      record: Extract<ComposerContextRecord, { kind: "image" | "file" }>,
      localId: string,
      sourceEnvironmentId: EnvironmentId,
    ) => {
      // The chip lands in the draft immediately while these bytes are still downloading. Count
      // the transfer against its own draft so a send cannot snapshot a message whose chip has no
      // attachment behind it, and so bytes for an abandoned draft never enter the next one.
      const importTargetKey = attachmentTargetKey;
      pendingDraftWork.begin(importTargetKey);
      try {
        await runAttachmentImport(record, localId, sourceEnvironmentId, importTargetKey);
      } finally {
        pendingDraftWork.end(importTargetKey);
      }
    },
    [attachmentTargetKey, runAttachmentImport],
  );
  /**
   * Brings records into this draft (paste, stash restore). Binaries are transferred only
   * when `sourceEnvironmentId` is given; the stash restores its own images and files.
   */
  const importContextRecords = useCallback(
    (
      records: ReadonlyArray<ComposerContextRecord>,
      sourceEnvironmentId: EnvironmentId | null,
    ): ReadonlyMap<string, string> => {
      const rewritten = new Map<string, string>();
      const dependentAttachmentLocalIds = new Map<string, string>();
      const skippedDependentAttachmentIds = new Set<string>();
      // Resolve annotations before their dependent screenshot records even if a foreign
      // clipboard producer emitted the records in a different order.
      const orderedRecords = records.toSorted((left, right) =>
        left.kind === "preview-annotation" && right.kind !== "preview-annotation"
          ? -1
          : right.kind === "preview-annotation" && left.kind !== "preview-annotation"
            ? 1
            : 0,
      );
      for (const candidate of orderedRecords) {
        // Producer ids fold into context ids, so two different excerpts can collide. Only skip
        // when the draft already holds the same payload; a colliding but different record is
        // re-minted under a fresh id so both survive the paste.
        const record = asKnownContextRecord(candidate);
        if (!record) continue;
        const existing = composerContextImportLookupIds(record).flatMap((contextId) => {
          const found = composerContextRecords.get(contextId);
          return found ? [found] : [];
        })[0];
        const existingRecord =
          existing?.kind === "terminal"
            ? terminalContextRecord(existing.record)
            : existing?.kind === "review-comment"
              ? reviewCommentContextRecord(existing.record)
              : existing?.kind === "preview-annotation"
                ? previewAnnotationContextRecord(existing.record)
                : existing
                  ? (uploadedContextRecordFromDraft(existing) ?? undefined)
                  : undefined;
        if (existingRecord && isSameComposerContextPayload(existingRecord, record)) {
          if (record.kind === "preview-annotation" && record.screenshotContextId) {
            skippedDependentAttachmentIds.add(record.screenshotContextId);
          }
          continue;
        }
        const conflicts = existing !== undefined;
        switch (record.kind) {
          case "terminal": {
            const threadId = activeThreadId;
            if (!threadId) break;
            const imported = terminalContextDraftFromRecord(record, threadId);
            const draft = conflicts ? { ...imported, id: randomUUID() } : imported;
            addComposerDraftTerminalContexts(composerDraftTarget, [draft], {
              appendReference: false,
            });
            rewritten.set(record.contextId, terminalContextReference(draft).contextId);
            break;
          }
          case "review-comment": {
            const imported = reviewCommentFromRecord(record);
            const comment = conflicts ? { ...imported, id: randomUUID() } : imported;
            addComposerDraftReviewComment(composerDraftTarget, comment, {
              appendReference: false,
            });
            rewritten.set(record.contextId, reviewCommentContextId(comment.id));
            break;
          }
          case "element":
          case "preview-annotation": {
            const imported =
              record.kind === "element"
                ? elementContextToPreviewAnnotation(record, randomUUID(), new Date().toISOString())
                : previewAnnotationFromRecord(record);
            const annotation = conflicts ? { ...imported, id: randomUUID() } : imported;
            if (record.kind === "preview-annotation" && record.screenshotContextId) {
              dependentAttachmentLocalIds.set(record.screenshotContextId, annotation.id);
            }
            addComposerDraftPreviewAnnotation(composerDraftTarget, annotation, {
              appendReference: false,
            });
            rewritten.set(record.contextId, previewAnnotationContextId(annotation.id));
            break;
          }
          case "image":
          case "file": {
            if (skippedDependentAttachmentIds.has(record.contextId)) break;
            if (sourceEnvironmentId === null && !conflicts) {
              rewritten.set(record.contextId, record.contextId);
              break;
            }
            if (sourceEnvironmentId === null) break;
            const localId = dependentAttachmentLocalIds.get(record.contextId) ?? randomUUID();
            rewritten.set(record.contextId, toKindScopedComposerContextId(record.kind, localId));
            void importAttachmentRecord(record, localId, sourceEnvironmentId);
            break;
          }
          default:
            break;
        }
      }
      return rewritten;
    },
    [
      activeThreadId,
      activeThreadId,
      addComposerDraftPreviewAnnotation,
      addComposerDraftReviewComment,
      addComposerDraftTerminalContexts,
      composerContextRecords,
      composerDraftTarget,
      importAttachmentRecord,
    ],
  );
  const importContextFragment = useCallback(
    (fragment: ComposerContextClipboardFragment): ReadonlyMap<string, string> =>
      importContextRecords(fragment.records, fragment.source.environmentId),
    [importContextRecords],
  );

  // ------------------------------------------------------------------
  // Sync refs back to parent
  // ------------------------------------------------------------------
  useEffect(() => {
    promptRef.current = prompt;
    setComposerCursor((existing) => clampCollapsedComposerCursor(prompt, existing));
  }, [prompt, promptRef]);

  useEffect(() => {
    if (composerSubmissionError === null) return;
    const nextError = getComposerPromptLengthValidationMessage(prompt);
    if (nextError !== composerSubmissionError) {
      setComposerSubmissionError(nextError);
    }
  }, [composerSubmissionError, prompt]);

  useEffect(() => {
    setProviderInputSubmissionError(null);
  }, [
    composerPreviewAnnotations,
    composerReviewComments,
    composerTerminalContexts,
    prompt,
    selectedModel,
    selectedPromptEffort,
    selectedProvider,
  ]);

  useEffect(() => {
    composerImagesRef.current = composerImages;
  }, [composerImages, composerImagesRef]);

  useEffect(() => {
    composerFilesRef.current = composerFiles;
  }, [composerFiles, composerFilesRef]);

  useEffect(() => {
    brainComposerCollectionRef.current = brainComposerCollection;
  }, [brainComposerCollection]);

  useEffect(() => {
    designComposerCollectionRef.current = designComposerCollection;
  }, [designComposerCollection]);

  useEffect(() => {
    composerTerminalContextsRef.current = composerTerminalContexts;
  }, [composerTerminalContexts, composerTerminalContextsRef]);

  // ------------------------------------------------------------------
  // Composer menu highlight sync
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!composerMenuOpen) {
      setComposerHighlightedItemId(null);
      setComposerHighlightedSearchKey(null);
      return;
    }
    const nextActiveItemId = resolveComposerMenuActiveItemId({
      items: composerMenuItems,
      highlightedItemId: composerHighlightedItemId,
      currentSearchKey: composerMenuSearchKey,
      highlightedSearchKey: composerHighlightedSearchKey,
    });
    setComposerHighlightedItemId((existing) =>
      existing === nextActiveItemId ? existing : nextActiveItemId,
    );
    setComposerHighlightedSearchKey((existing) =>
      existing === composerMenuSearchKey ? existing : composerMenuSearchKey,
    );
  }, [
    composerHighlightedItemId,
    composerHighlightedSearchKey,
    composerMenuItems,
    composerMenuOpen,
    composerMenuSearchKey,
  ]);

  const lastSyncedPendingInputRef = useRef<{
    requestId: string | null;
    questionId: string | null;
  } | null>(null);

  useEffect(() => {
    const nextCustomAnswer = activePendingProgress?.customAnswer;
    if (typeof nextCustomAnswer !== "string") {
      lastSyncedPendingInputRef.current = null;
      return;
    }

    const nextRequestId = activePendingUserInput?.requestId ?? null;
    const nextQuestionId = activePendingProgress?.activeQuestion?.id ?? null;
    const questionChanged =
      lastSyncedPendingInputRef.current?.requestId !== nextRequestId ||
      lastSyncedPendingInputRef.current?.questionId !== nextQuestionId;
    const textChangedExternally = promptRef.current !== nextCustomAnswer;

    lastSyncedPendingInputRef.current = {
      requestId: nextRequestId,
      questionId: nextQuestionId,
    };

    if (!questionChanged && !textChangedExternally) {
      return;
    }

    promptRef.current = nextCustomAnswer;
    const nextCursor = collapseExpandedComposerCursor(nextCustomAnswer, nextCustomAnswer.length);
    setComposerCursor(nextCursor);
    setComposerTrigger(
      detectComposerTrigger(
        nextCustomAnswer,
        expandCollapsedComposerCursor(nextCustomAnswer, nextCursor),
      ),
    );
    setComposerHighlightedItemId(null);
  }, [
    activePendingProgress?.customAnswer,
    activePendingProgress?.activeQuestion?.id,
    activePendingUserInput?.requestId,
    promptRef,
  ]);

  // ------------------------------------------------------------------
  // Reset compositor state on thread/draft change
  // ------------------------------------------------------------------
  useEffect(() => {
    setComposerHighlightedItemId(null);
    setComposerSubmissionError(null);
    setProviderInputSubmissionError(null);
    setComposerCursor(collapseExpandedComposerCursor(promptRef.current, promptRef.current.length));
    setComposerTrigger(detectComposerTrigger(promptRef.current, promptRef.current.length));
    setIsDragOverComposer(false);
    setIsComposerScrollCollapsed(false);
  }, [draftId, activeThreadId, promptRef, setIsComposerScrollCollapsed]);

  // ------------------------------------------------------------------
  // Footer compact layout observation
  // ------------------------------------------------------------------
  useLayoutEffect(() => {
    const composerForm = composerFormRef.current;
    if (!composerForm) return;
    const measureComposerFormWidth = () => composerForm.clientWidth;
    const measureFooterCompactness = () => {
      const composerFormWidth = measureComposerFormWidth();
      const footerCompact = shouldUseCompactComposerFooter(composerFormWidth, {
        hasWideActions: composerFooterHasWideActions,
      });
      const primaryActionsCompact =
        footerCompact &&
        shouldUseCompactComposerPrimaryActions(composerFormWidth, {
          hasWideActions: composerFooterHasWideActions,
        });
      return { primaryActionsCompact, footerCompact };
    };

    const initialCompactness = measureFooterCompactness();
    setIsComposerPrimaryActionsCompact(initialCompactness.primaryActionsCompact);
    setIsComposerFooterCompact(initialCompactness.footerCompact);
    if (typeof ResizeObserver === "undefined") return;
    const footerControls = composerFooterControlsRef.current;
    const stopFooterControlsFade = footerControls
      ? observeResponsiveBreakpointFade({
          target: footerControls,
          container: composerForm,
          active: panelAnimationsActive,
          durationMs: panelAnimationDurationMs,
          breakpoint: {
            value: composerFooterHasWideActions
              ? COMPOSER_FOOTER_WIDE_ACTIONS_COMPACT_BREAKPOINT_PX
              : COMPOSER_FOOTER_COMPACT_BREAKPOINT_PX,
            unit: "px",
          },
        })
      : undefined;

    const observer = new ResizeObserver(() => {
      const nextCompactness = measureFooterCompactness();
      setIsComposerPrimaryActionsCompact((previous) =>
        previous === nextCompactness.primaryActionsCompact
          ? previous
          : nextCompactness.primaryActionsCompact,
      );
      setIsComposerFooterCompact((previous) =>
        previous === nextCompactness.footerCompact ? previous : nextCompactness.footerCompact,
      );
    });

    observer.observe(composerForm);
    return () => {
      observer.disconnect();
      stopFooterControlsFade?.();
    };
  }, [
    activeThreadId,
    composerFooterActionLayoutKey,
    composerFooterHasWideActions,
    isComposerApprovalState,
    isComposerCollapsedMobile,
    panelAnimationDurationMs,
    panelAnimationsActive,
  ]);

  // ------------------------------------------------------------------
  // Image persist effect
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (composerImages.length === 0) {
        clearComposerDraftPersistedAttachments(attachmentDraftTarget);
        return;
      }
      const getPersistedAttachmentsForThread = () =>
        getComposerDraft(attachmentDraftTarget)?.persistedAttachments ?? [];
      try {
        const currentPersistedAttachments = getPersistedAttachmentsForThread();
        const existingPersistedById = new Map(
          currentPersistedAttachments.map((attachment) => [attachment.id, attachment]),
        );
        const stagedAttachmentById = new Map<string, PersistedComposerImageAttachment>();
        await Promise.all(
          composerImages.map(async (image) => {
            try {
              const dataUrl = await readFileAsDataUrl(image.file);
              stagedAttachmentById.set(image.id, {
                id: image.id,
                name: image.name,
                mimeType: image.mimeType,
                sizeBytes: image.sizeBytes,
                dataUrl,
                ...(image.source ? { source: image.source } : {}),
              });
            } catch {
              const existingPersisted = existingPersistedById.get(image.id);
              if (existingPersisted) {
                stagedAttachmentById.set(image.id, existingPersisted);
              }
            }
          }),
        );
        const serialized = Array.from(stagedAttachmentById.values());
        if (cancelled) return;
        syncComposerDraftPersistedAttachments(attachmentDraftTarget, serialized);
      } catch {
        const currentImageIds = new Set(composerImages.map((image) => image.id));
        const fallbackPersistedAttachments = getPersistedAttachmentsForThread();
        const fallbackPersistedIds: Array<string> = [];
        for (const attachment of fallbackPersistedAttachments) {
          if (currentImageIds.has(attachment.id)) {
            fallbackPersistedIds.push(attachment.id);
          }
        }
        const fallbackPersistedIdSet = new Set(fallbackPersistedIds);
        const fallbackAttachments = fallbackPersistedAttachments.filter((attachment) =>
          fallbackPersistedIdSet.has(attachment.id),
        );
        if (cancelled) return;
        syncComposerDraftPersistedAttachments(attachmentDraftTarget, fallbackAttachments);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    attachmentDraftTarget,
    clearComposerDraftPersistedAttachments,
    composerImages,
    getComposerDraft,
    syncComposerDraftPersistedAttachments,
  ]);

  // ------------------------------------------------------------------
  // Callbacks: prompt change
  // ------------------------------------------------------------------
  const expandComposerForEditorChange = useCallback(() => {
    // Editor changes win over the momentum tail of the active scroll gesture.
    suppressActiveComposerScrollGesture(
      composerScrollGestureRef.current,
      window.performance.now(),
      COMPOSER_SCROLL_GESTURE_RESET_MS,
    );
    setIsComposerScrollCollapsed(false);
  }, [setIsComposerScrollCollapsed]);

  /**
   * Payloads for chips the prompt no longer references. History undo restores the
   * reference text but knows nothing about the draft records behind it, so a delete keeps its
   * payload here and an undo puts it back rather than leaving a dangling chip.
   */
  const removedContextPayloadsRef = useRef<{
    terminals: Map<string, TerminalContextDraft>;
    reviewComments: Map<string, ReviewCommentContext>;
  }>({ terminals: new Map(), reviewComments: new Map() });
  const removedAttachmentContextPayloadsRef = useRef<RetainedAttachmentContextPayloads>({
    files: new Map(),
    previewAnnotations: new Map(),
  });

  const onPromptChange = useCallback(
    (
      nextPrompt: string,
      nextCursor: number,
      expandedCursor: number,
      cursorAdjacentToMention: boolean,
      contextIds: string[],
    ) => {
      expandComposerForEditorChange();
      if (activePendingProgress?.activeQuestion && pendingUserInputs.length > 0) {
        if (activePendingProgress.activeQuestion.allowCustomAnswer === false) return;
        setComposerCursor(nextCursor);
        setComposerTrigger(
          cursorAdjacentToMention ? null : detectComposerTrigger(nextPrompt, expandedCursor),
        );
        onChangeActivePendingUserInputCustomAnswer(
          activePendingProgress.activeQuestion.id,
          nextPrompt,
          nextCursor,
          expandedCursor,
          cursorAdjacentToMention,
        );
        return;
      }
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      // Any edit ends browsing, even one later undone by hand: typing a
      // character and deleting it leaves the text equal to the recall, and
      // ArrowDown must move the caret then, not clear the composer.
      if (promptHistoryPositionRef.current?.recalled !== nextPrompt) {
        promptHistoryPositionRef.current = null;
      }
      const referenced = new Set(contextIds);
      const retained = removedContextPayloadsRef.current;

      // An undone delete brings the reference back; restore the payload it points at.
      const liveTerminalIds = new Set<string>(
        composerTerminalContexts.map((context) => terminalContextReference(context).contextId),
      );
      const restoredTerminals = [...referenced].flatMap((contextId) => {
        if (liveTerminalIds.has(contextId)) return [];
        const context = retained.terminals.get(contextId);
        return context ? [context] : [];
      });
      const nextTerminals = [
        ...composerTerminalContexts.filter((context) =>
          referenced.has(terminalContextReference(context).contextId),
        ),
        ...restoredTerminals,
      ];
      for (const context of composerTerminalContexts) {
        const contextId = terminalContextReference(context).contextId;
        if (!referenced.has(contextId)) retained.terminals.set(contextId, context);
      }
      if (
        nextTerminals.length !== composerTerminalContexts.length ||
        restoredTerminals.length > 0
      ) {
        setComposerDraftTerminalContexts(composerDraftTarget, nextTerminals);
      }

      for (const comment of composerReviewComments) {
        const contextId = reviewCommentContextId(comment.id);
        if (!referenced.has(contextId)) {
          retained.reviewComments.set(contextId, comment);
          removeComposerDraftReviewComment(composerDraftTarget, comment.id);
        }
      }
      const liveReviewIds = new Set<string>(
        composerReviewComments.map((comment) => reviewCommentContextId(comment.id)),
      );
      for (const contextId of referenced) {
        if (liveReviewIds.has(contextId)) continue;
        const comment = retained.reviewComments.get(contextId);
        if (comment) {
          addComposerDraftReviewComment(composerDraftTarget, comment, { appendReference: false });
        }
      }

      const attachmentChanges = reconcileAttachmentContextReferences({
        referencedContextIds: referenced,
        files: composerFiles,
        images: composerImages,
        previewAnnotations: composerPreviewAnnotations,
        retained: removedAttachmentContextPayloadsRef.current,
      });
      for (const annotationId of attachmentChanges.annotationIdsToRemove) {
        // Keep the upload queue entry alive: undo restores the image that owns it.
        removeComposerDraftPreviewAnnotation(composerDraftTarget, annotationId);
      }
      for (const restored of attachmentChanges.annotationsToRestore) {
        if (restored.image) addComposerDraftImages(attachmentDraftTarget, [restored.image]);
        addComposerDraftPreviewAnnotation(composerDraftTarget, restored.annotation, {
          appendReference: false,
        });
      }
      for (const fileId of attachmentChanges.filesToRemove) {
        // The retained File and upload are still sendable if the editor restores the chip.
        removeComposerDraftFile(attachmentDraftTarget, fileId);
      }
      if (attachmentChanges.filesToRestore.length > 0) {
        addComposerDraftFiles(attachmentDraftTarget, attachmentChanges.filesToRestore);
      }
      setComposerCursor(nextCursor);
      setComposerTrigger(
        cursorAdjacentToMention ? null : detectComposerTrigger(nextPrompt, expandedCursor),
      );
    },
    [
      activePendingProgress?.activeQuestion,
      expandComposerForEditorChange,
      pendingUserInputs.length,
      onChangeActivePendingUserInputCustomAnswer,
      promptRef,
      setPrompt,
      composerDraftTarget,
      composerTerminalContexts,
      setComposerDraftTerminalContexts,
      composerReviewComments,
      composerPreviewAnnotations,
      composerImages,
      composerFiles,
      removeComposerDraftReviewComment,
      removeComposerDraftPreviewAnnotation,
      removeComposerDraftFile,
      addComposerDraftReviewComment,
      addComposerDraftPreviewAnnotation,
      addComposerDraftImages,
      addComposerDraftFiles,
      attachmentDraftTarget,
    ],
  );

  // ------------------------------------------------------------------
  // Callbacks: prompt replacement / menu
  // ------------------------------------------------------------------
  const applyPromptReplacement = useCallback(
    (
      rangeStart: number,
      rangeEnd: number,
      replacement: string,
      options?: {
        expectedText?: string;
        expandedCursorAfterReplace?: number;
        focusEditorAfterReplace?: boolean;
        citationComment?: { start: number; sourceAnchor: AssistantCitationSourceAnchor };
      },
    ): boolean => {
      if (
        activePendingUserInput &&
        activePendingProgress?.activeQuestion?.allowCustomAnswer === false
      ) {
        return false;
      }
      const currentText = promptRef.current;
      const safeStart = Math.max(0, Math.min(currentText.length, rangeStart));
      const safeEnd = Math.max(safeStart, Math.min(currentText.length, rangeEnd));
      if (
        options?.expectedText !== undefined &&
        currentText.slice(safeStart, safeEnd) !== options.expectedText
      ) {
        return false;
      }
      const next = replaceTextRange(promptRef.current, rangeStart, rangeEnd, replacement);
      const nextCursor = collapseExpandedComposerCursor(
        next.text,
        options?.expandedCursorAfterReplace ?? next.cursor,
      );
      const nextExpandedCursor = expandCollapsedComposerCursor(next.text, nextCursor);
      if (options?.citationComment) {
        composerEditorRef.current?.requestCitationComment({
          previousValue: currentText,
          value: next.text,
          citationStart: options.citationComment.start,
          sourceAnchor: options.citationComment.sourceAnchor,
        });
      }
      promptRef.current = next.text;
      const activePendingQuestion = activePendingProgress?.activeQuestion;
      if (activePendingQuestion && activePendingUserInput) {
        onChangeActivePendingUserInputCustomAnswer(
          activePendingQuestion.id,
          next.text,
          nextCursor,
          nextExpandedCursor,
          false,
        );
      } else {
        setPrompt(next.text);
      }
      setComposerCursor(nextCursor);
      setComposerTrigger(detectComposerTrigger(next.text, nextExpandedCursor));
      if (options?.focusEditorAfterReplace !== false) {
        window.requestAnimationFrame(() => {
          // Type-to-focus routes only the first key through here; once the
          // controlled update focuses the editor, later keys land natively.
          // Skip the deferred caret placement when the draft has moved on,
          // or it drags the caret back behind what was typed since.
          if (promptRef.current !== next.text) return;
          composerEditorRef.current?.focusAt(nextCursor);
        });
      }
      return true;
    },
    [
      activePendingProgress?.activeQuestion,
      activePendingUserInput,
      onChangeActivePendingUserInputCustomAnswer,
      promptRef,
      setPrompt,
    ],
  );

  const readComposerSnapshot = useCallback((): {
    value: string;
    cursor: number;
    expandedCursor: number;
    contextIds: string[];
  } => {
    const editorSnapshot = composerEditorRef.current?.readSnapshot();
    if (editorSnapshot) {
      return editorSnapshot;
    }
    return {
      value: promptRef.current,
      cursor: composerCursor,
      expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
      contextIds: collectInlineContextIds(promptRef.current),
    };
  }, [composerCursor, promptRef]);

  const resolveActiveComposerTrigger = useCallback((): {
    snapshot: { value: string; cursor: number; expandedCursor: number };
    trigger: ComposerTrigger | null;
  } => {
    const snapshot = readComposerSnapshot();
    return {
      snapshot,
      trigger: detectComposerTrigger(snapshot.value, snapshot.expandedCursor),
    };
  }, [readComposerSnapshot]);

  const onSelectComposerItem = useCallback(
    (item: ComposerCommandItem) => {
      if (composerSelectLockRef.current) return;
      composerSelectLockRef.current = true;
      window.requestAnimationFrame(() => {
        composerSelectLockRef.current = false;
      });
      const { snapshot, trigger } = resolveActiveComposerTrigger();
      if (!trigger) return;
      if (item.type === "design-system") {
        const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
          expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
        });
        if (applied) {
          // A target mode takes exactly one Design System, so a second pick
          // replaces the first. Without a Design Action the mention stays the
          // ordinary multi-select context it is everywhere else.
          pickDesignSystemTarget(item.target);
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "path") {
        const replacement = `${serializeComposerFileLink(item.path)} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "slash-command") {
        if (item.command === "model") {
          const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
            expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
            focusEditorAfterReplace: false,
          });
          if (applied) {
            setComposerHighlightedItemId(null);
            setIsComposerModelPickerOpen(true);
          }
          return;
        }
        if (!planModeUiEnabled) return;
        void handleInteractionModeChange(item.command === "plan" ? "plan" : "default");
        const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
          expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
        });
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "provider-slash-command") {
        const replacement = `/${item.command.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "skill") {
        const replacement = `$${item.skill.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "action") {
        if (item.entry.disabledReason !== null) return;
        const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
          expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
        });
        if (applied) {
          setComposerHighlightedItemId(null);
          pickComposerActionRef.current(item.entry.selection);
        }
        return;
      }
      if (item.type === "pull-request") {
        if (
          trigger.kind !== "pull-request" ||
          !composerMenuItemsRef.current.some((candidate) => candidate.id === item.id)
        ) {
          return;
        }
        const comment = buildPullRequestReferenceContext(item.pullRequest);
        const replacement = `${formatInlineContextReference(
          reviewCommentContextReference(comment),
        )} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          addComposerDraftReviewComment(composerDraftTarget, comment, {
            appendReference: false,
          });
          setComposerHighlightedItemId(null);
        }
        return;
      }
    },
    [
      addComposerDraftReviewComment,
      setComposerDraftDesignSystemMentions,
      composerDesignSystemMentions,
      designOperation,
      applyPromptReplacement,
      composerDraftTarget,
      handleInteractionModeChange,
      planModeUiEnabled,
      resolveActiveComposerTrigger,
    ],
  );

  const onComposerMenuItemHighlighted = useCallback(
    (itemId: string | null) => {
      setComposerHighlightedItemId(itemId);
      setComposerHighlightedSearchKey(composerMenuSearchKey);
    },
    [composerMenuSearchKey],
  );

  const nudgeComposerMenuHighlight = useCallback(
    (key: "ArrowDown" | "ArrowUp") => {
      if (composerMenuItems.length === 0) return;
      const highlightedIndex = composerMenuItems.findIndex(
        (item) => item.id === composerHighlightedItemId,
      );
      const normalizedIndex =
        highlightedIndex >= 0 ? highlightedIndex : key === "ArrowDown" ? -1 : 0;
      const offset = key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        (normalizedIndex + offset + composerMenuItems.length) % composerMenuItems.length;
      const nextItem = composerMenuItems[nextIndex];
      setComposerHighlightedItemId(nextItem?.id ?? null);
    },
    [composerHighlightedItemId, composerMenuItems],
  );

  const blurMobileComposerAfterSend = useCallback(() => {
    if (!isMobileViewport) return;
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
      composerBlurFrameRef.current = null;
    }
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    setIsComposerFocused(false);
  }, [isMobileViewport, setIsComposerFocused]);

  const shouldBlurMobileComposerOnSubmit = useCallback(() => {
    if (!isMobileViewport) return false;
    if (
      isSendBusy ||
      isSendDisabled ||
      isConnecting ||
      noProviderAvailable ||
      environmentUnavailable !== null ||
      turnInProgress
    ) {
      return false;
    }
    if (activePendingProgress) {
      return activePendingProgress.isLastQuestion && Boolean(activePendingResolvedAnswers);
    }
    return showPlanFollowUpPrompt || composerSendState.hasSendableContent;
  }, [
    activePendingProgress,
    activePendingResolvedAnswers,
    composerSendState.hasSendableContent,
    environmentUnavailable,
    isConnecting,
    isMobileViewport,
    isSendBusy,
    isSendDisabled,
    noProviderAvailable,
    turnInProgress,
    showPlanFollowUpPrompt,
  ]);

  const submitComposer = useCallback(
    (event?: { preventDefault: () => void }, intent: ComposerSubmissionIntent = "foreground") => {
      if (noProviderAvailable || isSendDisabled) {
        event?.preventDefault();
        return;
      }
      // A send while a pasted image is still compressing would strand that
      // image: the turn snapshot wouldn't include it, and it would surface
      // in the *next* draft instead. Only oversized images hit this — small
      // files clear the pending counter within a microtask.
      if (
        activeThreadId &&
        (pendingImageCompressionsRef.current.get(attachmentTargetKey) ?? 0) > 0
      ) {
        event?.preventDefault();
        toastManager.add({
          type: "info",
          title: "Still compressing a pasted image.",
          description: "Send again once its thumbnail appears.",
        });
        return;
      }
      // A pasted chip's bytes arrive over the network, so the same hazard applies for longer:
      // sending now would snapshot a chip with no attachment behind it.
      if (pendingDraftWork.has(attachmentTargetKey)) {
        event?.preventDefault();
        toastManager.add({
          type: "info",
          title: "Still bringing a pasted attachment into this message.",
          description: "Send again once its chip resolves.",
        });
        return;
      }
      const submission = submitComposerDraft({
        prompt: promptRef.current,
        submissionTarget: activePendingProgress ? "pending-user-input" : "provider-turn",
        event,
        onSend: (sendEvent) => {
          // ChatView reports its final composed-input preflight through the
          // composer handle before its first asynchronous send step.
          providerInputRejectedRef.current = false;
          onSend(sendEvent, intent);
          return !providerInputRejectedRef.current;
        },
      });
      setComposerSubmissionError(submission.validationMessage);
      if (!submission.didDispatch) return;
      if (shouldBlurMobileComposerOnSubmit()) {
        blurMobileComposerAfterSend();
      }
    },
    [
      activeThreadId,
      activePendingProgress,
      attachmentTargetKey,
      blurMobileComposerAfterSend,
      isSendDisabled,
      noProviderAvailable,
      onSend,
      promptRef,
      shouldBlurMobileComposerOnSubmit,
    ],
  );
  const submitCitationAndSend = useCallback(() => {
    const intent = composerSubmissionIntentForEnter({
      isMobileViewport,
      shiftKey: false,
      modifierKey: true,
      isDraftThread: routeKind === "draft",
    });
    submitComposer(undefined, intent ?? "foreground");
  }, [isMobileViewport, routeKind, submitComposer]);
  const compactThreadContext = useCallback(() => {
    if (
      compactDisabled ||
      noProviderAvailable ||
      activePendingApproval !== null ||
      pendingUserInputs.length > 0 ||
      turnInProgress ||
      isSendBusy ||
      isConnecting ||
      !activeThreadId
    ) {
      return;
    }
    onCompactContext();
  }, [
    activePendingApproval,
    activeThreadId,
    compactDisabled,
    isConnecting,
    isSendBusy,
    noProviderAvailable,
    onCompactContext,
    pendingUserInputs.length,
    turnInProgress,
  ]);
  const expandMobileComposer = useCallback(() => {
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
      composerBlurFrameRef.current = null;
    }
    if (mobileComposerExpandFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileComposerExpandFrameRef.current);
    }
    if (mobileComposerExpandReleaseFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileComposerExpandReleaseFrameRef.current);
    }
    mobileComposerExpandInFlightRef.current = true;
    setIsComposerFocused(true);
    mobileComposerExpandFrameRef.current = window.requestAnimationFrame(() => {
      mobileComposerExpandFrameRef.current = null;
      composerEditorRef.current?.focusAtEnd();
      mobileComposerExpandReleaseFrameRef.current = window.requestAnimationFrame(() => {
        mobileComposerExpandReleaseFrameRef.current = null;
        mobileComposerExpandInFlightRef.current = false;
      });
    });
  }, [setIsComposerFocused]);

  // ------------------------------------------------------------------
  // Prompt history (ArrowUp / ArrowDown)
  // ------------------------------------------------------------------
  // Entries are built on the keypress, not per render: the timeline changes
  // on every streamed delta and ArrowUp is rare.
  const promptHistoryMessagesRef = useRef(promptHistoryMessages);
  promptHistoryMessagesRef.current = promptHistoryMessages;

  // The composer persists across threads. A recall from thread A must not
  // be treated as active in thread B, where the text-match fallback could
  // otherwise turn B's own draft into a browsing position.
  const promptHistoryTargetKey = composerTargetKey(composerDraftTarget);
  useEffect(() => {
    promptHistoryPositionRef.current = null;
  }, [promptHistoryTargetKey]);

  const replacePromptFromHistory = useCallback(
    (nextPrompt: string) => {
      promptRef.current = nextPrompt;
      setComposerDraftPrompt(composerDraftTarget, nextPrompt);
      setComposerCursor(collapseExpandedComposerCursor(nextPrompt, nextPrompt.length));
      setComposerTrigger(null);
      setComposerHighlightedItemId(null);
    },
    [composerDraftTarget, promptRef, setComposerDraftPrompt],
  );

  const navigatePromptHistory = useCallback(
    (direction: "backward" | "forward", event: KeyboardEvent): boolean => {
      if (event.shiftKey || event.altKey || event.metaKey || event.ctrlKey || event.isComposing) {
        return false;
      }
      if (isComposerApprovalState || pendingUserInputs.length > 0) return false;
      // A composer holding an image, file, picked element, preview
      // annotation, or review comment is not empty. Recalling text into it
      // would send the old prompt with the new context, which is never what
      // ArrowUp meant.
      if (
        composerImagesRef.current.length > 0 ||
        composerFilesRef.current.length > 0 ||
        composerTerminalContextsRef.current.length > 0 ||
        composerPreviewAnnotations.length > 0 ||
        composerReviewComments.length > 0
      ) {
        return false;
      }
      // A typed draft with no active recall can never step, so skip the
      // layout read and the entry build for that common case.
      if (promptHistoryPositionRef.current === null && promptRef.current.length > 0) {
        return false;
      }
      const editor = composerEditorRef.current;
      if (!editor?.isCaretOnVisualEdge(direction === "backward" ? "start" : "end")) {
        return false;
      }
      const step = stepComposerPromptHistory({
        direction,
        entries: buildComposerPromptHistoryEntries(promptHistoryMessagesRef.current),
        position: promptHistoryPositionRef.current,
        currentPrompt: promptRef.current,
      });
      if (!step) return false;
      promptHistoryPositionRef.current = step.position;
      replacePromptFromHistory(step.prompt);
      return true;
    },
    [
      composerTerminalContextsRef,
      composerFilesRef,
      composerImagesRef,
      composerPreviewAnnotations.length,
      composerReviewComments.length,
      isComposerApprovalState,
      pendingUserInputs.length,
      promptRef,
      replacePromptFromHistory,
    ],
  );

  // ------------------------------------------------------------------
  // Callbacks: command key
  // ------------------------------------------------------------------
  const onComposerCommandKey = (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
    isTaskItem = false,
  ) => {
    if (key === "Tab" && event.shiftKey) {
      if (!planModeUiEnabled) return false;
      toggleInteractionMode();
      return true;
    }
    const { trigger } = resolveActiveComposerTrigger();
    const menuIsActive = composerMenuOpenRef.current || trigger !== null;
    if (menuIsActive) {
      const currentItems = composerMenuItemsRef.current;
      const selectedItem = activeComposerMenuItemRef.current ?? currentItems[0];
      if (key === "ArrowDown" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowDown");
        return true;
      }
      if (key === "ArrowUp" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowUp");
        return true;
      }
      if ((key === "Enter" || key === "Tab") && selectedItem) {
        onSelectComposerItem(selectedItem);
        return true;
      }
    }
    if (key === "ArrowUp" || key === "ArrowDown") {
      return navigatePromptHistory(key === "ArrowUp" ? "backward" : "forward", event);
    }
    const submissionIntent =
      key === "Enter"
        ? composerSubmissionIntentForEnter({
            isMobileViewport,
            shiftKey: event.shiftKey,
            modifierKey: event.metaKey || event.ctrlKey,
            isDraftThread: routeKind === "draft",
            isRunning: turnInProgress,
            sendShortcut: settings.sendShortcut,
            prompt: promptRef.current,
          })
        : null;
    if (submissionIntent) {
      submitComposer(undefined, submissionIntent);
      return true;
    }
    // Native task splitting preserves marks and chips on both sides of the caret.
    if (key === "Enter" && isTaskItem) return false;
    if (!event.isComposing && (key === "Enter" || (key === "Tab" && !event.shiftKey))) {
      const selection = composerEditorRef.current?.readSelectionRange();
      const snapshot = readComposerSnapshot();
      if (selection && selection.start === selection.end && snapshot.value === promptRef.current) {
        const edit =
          key === "Enter"
            ? listContinuationForEnter(snapshot.value, selection.start)
            : listIndentForTab(snapshot.value, selection.start, selection.end);
        if (
          edit &&
          applyPromptReplacement(
            edit.start,
            edit.end,
            edit.replacement,
            key === "Tab"
              ? { expandedCursorAfterReplace: selection.start + edit.replacement.length }
              : undefined,
          )
        ) {
          return true;
        }
      }
    }
    return false;
  };

  // ------------------------------------------------------------------
  // Prompt stash (⌘S)
  // ------------------------------------------------------------------
  // Files remain tied to the environment that owns their uploaded bytes.
  const stashQueue = usePromptStashStore((state) => state.entries);
  const stashEntryToQueue = usePromptStashStore((state) => state.stashEntry);
  const takeStashEntry = usePromptStashStore((state) => state.takeEntry);
  const finalizeStashEntryImages = usePromptStashStore((state) => state.finalizeEntryImages);

  useEffect(() => {
    return () => {
      if (stashPulseTimeoutRef.current !== null) {
        window.clearTimeout(stashPulseTimeoutRef.current);
      }
    };
  }, []);

  /** Briefly highlight the badge so the save registers without a flourish. */
  const pulseStashBadge = useCallback(() => {
    stashPulseKeyRef.current += 1;
    setStashPulse({ key: stashPulseKeyRef.current, active: true });
    if (stashPulseTimeoutRef.current !== null) {
      window.clearTimeout(stashPulseTimeoutRef.current);
    }
    stashPulseTimeoutRef.current = window.setTimeout(() => {
      stashPulseTimeoutRef.current = null;
      setStashPulse((current) => ({ ...current, active: false }));
    }, 1200);
  }, []);

  const restoreStashEntry = useCallback(
    async (menuEntry: PromptStashEntry) => {
      const filesToVerify = menuEntry.files ?? [];
      if (filesToVerify.some((file) => file.environmentId !== environmentId)) {
        toastManager.add({
          type: "error",
          title: "Stashed files belong to another environment",
          description: "Restore this prompt in the environment that received its files.",
        });
        return;
      }
      setIsStashMenuOpen(false);

      // A thread switch during the verify await would mix the new thread's
      // prompt with this invocation's captured target. Nothing was taken yet,
      // so abort and leave the entry restorable where the user now is.
      if (
        isRevertingCheckpointRef.current ||
        composerTargetKey(composerDraftTarget) !== composerDraftTargetKeyRef.current
      ) {
        return;
      }

      // The take is also the double-activation guard (click + Enter): the
      // second caller finds the entry gone and stops here.
      const { entry, durable } = takeStashEntry(menuEntry.id);
      if (!entry) return;
      if (!durable) {
        toastManager.add({
          type: "warning",
          title: "Restored prompt may reappear in the stash",
          description:
            "Browser storage rejected the update, so this entry could still be there after a reload.",
          data: { hideCopyButton: true },
        });
      }

      const rewrittenContextIds = entry.records
        ? importContextRecords(entry.records, null)
        : new Map<string, string>();
      const restoredPrompt = replaceComposerContextReferences(entry.prompt, (reference) => {
        const contextId = rewrittenContextIds.get(reference.contextId);
        return contextId
          ? formatInlineContextReference({
              ...reference,
              contextId,
              kind: reference.kind === "element" ? "preview-annotation" : reference.kind,
            })
          : reference.source;
      });
      const currentPrompt = promptRef.current;
      // An image-only stash must not append blank lines to whatever is
      // already in the composer.
      const nextPrompt =
        restoredPrompt.length === 0
          ? currentPrompt
          : currentPrompt.trim().length
            ? `${currentPrompt.replace(/\s+$/, "")}\n\n${restoredPrompt}`
            : restoredPrompt;
      let promptChanged = nextPrompt !== currentPrompt;
      if (promptChanged) {
        promptRef.current = nextPrompt;
        setComposerDraftPrompt(composerDraftTarget, nextPrompt);
        setComposerCursor(collapseExpandedComposerCursor(nextPrompt, nextPrompt.length));
        setComposerTrigger(null);
      }

      let unrestoredFileNames: string[] = [];
      let restoredFileCount = 0;
      const stashedFiles = entry.files ?? [];
      if (stashedFiles.length > 0) {
        const composerFilesNow = composerFilesRef.current;
        const existingFileIds = new Set(composerFilesNow.map((file) => file.id));
        const retainedUploadIds = new Set(
          composerFilesNow.flatMap((file) =>
            file.uploadedAttachmentId ? [file.uploadedAttachmentId] : [],
          ),
        );
        const existingFileKeys = new Set(composerFilesNow.map(composerFileDedupKey));
        const reattachMarkers = composerFilesNow.filter(composerFileNeedsReattach);
        const restoredMarkerIds = new Set<string>();
        const duplicateFiles: PersistedComposerFileAttachment[] = [];
        const markerReplacements: ComposerFileAttachment[] = [];
        const appendedFiles: ComposerFileAttachment[] = [];
        for (const file of stashedFiles) {
          const key = composerFileDedupKey(file);
          const restored: ComposerFileAttachment = {
            type: "file",
            id: file.id,
            name: file.name,
            mimeType: file.mimeType,
            sizeBytes: file.sizeBytes,
            file: null,
            ...(file.source ? { source: file.source } : {}),
            uploadedAttachmentId: file.attachmentId,
            uploadEnvironmentId: environmentId,
          };
          if (existingFileIds.has(file.id)) {
            if (!retainedUploadIds.has(file.attachmentId)) {
              duplicateFiles.push(file);
            }
            continue;
          }
          const reattachMarker = reattachMarkers.find(
            (marker) =>
              !restoredMarkerIds.has(marker.id) && composerFileMatchesReattachMarker(marker, file),
          );
          if (reattachMarker) {
            restoredMarkerIds.add(reattachMarker.id);
            existingFileIds.add(file.id);
            existingFileKeys.add(key);
            retainedUploadIds.add(file.attachmentId);
            markerReplacements.push(restored);
            continue;
          }
          if (existingFileKeys.has(key)) {
            if (!retainedUploadIds.has(file.attachmentId)) {
              duplicateFiles.push(file);
            }
            continue;
          }
          existingFileIds.add(file.id);
          existingFileKeys.add(key);
          retainedUploadIds.add(file.attachmentId);
          appendedFiles.push(restored);
        }
        const capacity = Math.max(
          0,
          attachmentLimit - composerImagesRef.current.length - composerFilesNow.length,
        );
        // Marker replacements reuse their marker's slot; only appended files
        // consume capacity.
        const filesToAppend = appendedFiles.slice(0, capacity);
        const skippedFiles = appendedFiles.slice(capacity);
        unrestoredFileNames = skippedFiles.map((file) => file.name);
        // A non-durable take can resurrect the stash entry after a reload;
        // deleting these uploads would leave it pointing at nothing.
        if (durable) {
          for (const file of duplicateFiles) {
            releasePersistedAttachmentUpload({
              id: file.id,
              environmentId,
              attachmentId: file.attachmentId,
            });
          }
          for (const file of skippedFiles) {
            if (file.uploadedAttachmentId) {
              releasePersistedAttachmentUpload({
                id: file.id,
                environmentId,
                attachmentId: file.uploadedAttachmentId,
              });
            }
          }
        }
        const restoredFiles = [...markerReplacements, ...filesToAppend];
        if (restoredFiles.length > 0) {
          addComposerDraftFiles(composerDraftTarget, restoredFiles, { appendReference: true });
          const restoredFilePrompt = getComposerDraft(composerDraftTarget)?.prompt;
          if (restoredFilePrompt !== undefined && restoredFilePrompt !== promptRef.current) {
            promptRef.current = restoredFilePrompt;
            setComposerCursor(
              collapseExpandedComposerCursor(restoredFilePrompt, restoredFilePrompt.length),
            );
            setComposerTrigger(null);
            promptChanged = true;
          }
          restoredFileCount = filesToAppend.length;
        }
      }

      let unrestoredImageNames: string[] = [];
      if (entry.attachments.length > 0) {
        const existingIds = new Set(composerImagesRef.current.map((image) => image.id));
        // The draft store also dedupes by mimeType+sizeBytes+name, so filter
        // on the same key here. Counting a duplicate against capacity would
        // burn a slot the store then refuses to fill, pushing a genuinely
        // unique image into the overflow list for nothing.
        const existingDedupKeys = new Set(
          composerImagesRef.current.map(
            (image) => `${image.mimeType}\0${image.sizeBytes}\0${image.name}`,
          ),
        );
        const capacity = Math.max(
          0,
          attachmentLimit -
            composerImagesRef.current.length -
            composerFilesRef.current.length -
            restoredFileCount,
        );
        const pending = entry.attachments.filter(
          (attachment) =>
            !existingIds.has(attachment.id) &&
            !existingDedupKeys.has(
              `${attachment.mimeType}\0${attachment.sizeBytes}\0${attachment.name}`,
            ),
        );
        // Anything past the attachment limit cannot be restored. The entry is
        // already out of the queue, so report the overflow by name instead of
        // discarding it silently.
        unrestoredImageNames = pending.slice(capacity).map((attachment) => attachment.name);
        const restoredImages = hydrateImagesFromPersisted(pending.slice(0, capacity));
        if (restoredImages.length > 0) {
          addComposerDraftImages(composerDraftTarget, restoredImages);
        }
      }

      // Deliberately no model/provider restore: the stash exists to carry a
      // prompt across threads and providers, so whatever the composer has
      // selected right now stays selected.

      // Each cause gets its own sentence so "too large" is never blamed for a
      // file that actually failed to decode, or for one the composer simply
      // had no room to take back.
      const missingImageReasons: string[] = [];
      if (entry.droppedImageNames.length > 0) {
        missingImageReasons.push(
          `${entry.droppedImageNames.join(", ")} exceeded the stash size limit when this prompt was saved.`,
        );
      }
      if (entry.unreadableImageNames && entry.unreadableImageNames.length > 0) {
        missingImageReasons.push(
          `${entry.unreadableImageNames.join(", ")} could not be read when this prompt was saved.`,
        );
      }
      if (unrestoredImageNames.length > 0) {
        missingImageReasons.push(
          `${unrestoredImageNames.join(", ")} could not be restored: the composer is at its ${attachmentLimit}-attachment limit.`,
        );
      }
      if (unrestoredFileNames.length > 0) {
        missingImageReasons.push(
          `${unrestoredFileNames.join(", ")} could not be restored: the composer is at its ${attachmentLimit}-attachment limit.`,
        );
      }
      if (missingImageReasons.length > 0) {
        toastManager.add({
          type: "warning",
          title: "Some attachments were not restored",
          description: missingImageReasons.join(" "),
        });
      }

      // Only yank the caret to the end when text was actually inserted;
      // restoring images alone should leave the user where they were typing.
      if (promptChanged) {
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAtEnd();
        });
      }
    },
    [
      addComposerDraftFiles,
      addComposerDraftImages,
      composerDraftTarget,
      composerFilesRef,
      composerImagesRef,
      environmentId,
      promptRef,
      setComposerDraftPrompt,
      takeStashEntry,
      importContextRecords,
    ],
  );

  const deleteStashEntry = useCallback(
    (entry: PromptStashEntry) => {
      const { entry: removed, durable } = takeStashEntry(entry.id);
      if (!stashQueue.some((candidate) => candidate.id !== entry.id)) {
        setIsStashMenuOpen(false);
      }
      if (durable && removed) {
        for (const file of removed.files ?? []) {
          releasePersistedAttachmentUpload({
            id: file.id,
            environmentId: file.environmentId,
            attachmentId: file.attachmentId,
          });
        }
      }
      if (!durable) {
        toastManager.add({
          type: "warning",
          title: "Stash entry may come back",
          description:
            "Browser storage rejected the delete, so this prompt could reappear after a reload.",
          data: { hideCopyButton: true },
        });
      }
    },
    [stashQueue, takeStashEntry],
  );

  const stashCurrentPrompt = useCallback(async () => {
    // Stashing clears the draft. A pasted attachment still downloading would then land in the
    // emptied composer instead of travelling with the entry it belongs to.
    if (pendingDraftWork.has(attachmentTargetKeyRef.current)) {
      toastManager.add({
        type: "info",
        title: "Still bringing a pasted attachment into this message.",
        description: "Stash again once its chip resolves.",
      });
      return;
    }
    const prompt = promptRef.current.trim();
    const images = [...composerImagesRef.current];
    const files = [...composerFilesRef.current];
    // Context chips keep their links in the prompt; the payloads behind them travel as
    // records so the restore can resolve every chip.
    const stashedRecords: ComposerContextRecord[] = [
      ...composerTerminalContextsRef.current.map(terminalContextRecord),
      ...composerReviewComments.map(reviewCommentContextRecord),
      ...composerPreviewAnnotations.map((annotation) =>
        previewAnnotationContextRecord(annotation, {
          screenshotContextId: images.some((image) => image.id === annotation.id)
            ? annotation.id
            : undefined,
        }),
      ),
    ];
    if (prompt.length === 0 && images.length === 0 && files.length === 0) {
      const entries = usePromptStashStore.getState().entries;
      const entry = entries.length === 1 ? entries[0] : undefined;
      if (entry && !entry.pendingImageCount) {
        await restoreStashEntry(entry);
      } else {
        setIsStashMenuOpen((open) => !open);
      }
      return;
    }
    const stashedFiles: PersistedComposerFileAttachment[] = [];
    for (const file of files) {
      if (composerFileNeedsReattach(file)) {
        toastManager.add({
          type: "error",
          title: "Attach dropped files again or remove them before stashing",
        });
        return;
      }
      // A stash entry points at a finished upload. A picked host path has no
      // upload and never will, so say that rather than ask for a wait that
      // cannot end.
      if (!composerFileUploadsBytes(file)) {
        toastManager.add({
          type: "error",
          title: "Remove the picked files and folders before stashing this prompt",
          description: "A stash keeps uploaded attachments; a picked path is not one.",
        });
        return;
      }
      const upload = readAttachmentUpload(file.id);
      if (upload?.status !== "ready" || upload.environmentId !== environmentId) {
        toastManager.add({
          type: "error",
          title: "Wait for file uploads before stashing this prompt",
        });
        return;
      }
      stashedFiles.push({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        sizeBytes: file.sizeBytes,
        attachmentId: upload.attachmentId,
        environmentId,
        ...(file.source ? { source: file.source } : {}),
      });
    }
    // A repeat ⌘S on the *same* still-unencoded snapshot would stash it
    // twice. Guard on the snapshot itself rather than a bare boolean: once
    // the composer has been cleared the user can type something genuinely
    // new (or switch threads) while encoding continues, and that deserves its
    // own entry.
    const attachmentKey = images
      .map((image) => `image:${image.id}`)
      .concat(files.map((file) => `file:${file.id}`))
      .join(",");
    const snapshotKey = [String(composerDraftTarget), prompt, attachmentKey].join("\n");
    if (stashInFlightRef.current.has(snapshotKey)) return;
    stashInFlightRef.current.add(snapshotKey);

    const stashTarget = composerDraftTarget;
    const entryId = randomUUID();
    try {
      // Persist the text-only entry *first*, then clear. Ordering matters in
      // both directions: writing before clearing means a crash or closed tab
      // mid-encode still leaves the prompt recoverable, while clearing before
      // the async image work means edits typed during encoding are not wiped.
      // Images are appended to the stored entry as they finish encoding.
      const { evicted, written, durable } = stashEntryToQueue({
        id: entryId,
        createdAt: new Date().toISOString(),
        prompt,
        attachments: [],
        ...(stashedFiles.length > 0 ? { files: stashedFiles } : {}),
        droppedImageNames: [],
        unreadableImageNames: [],
        pendingImageCount: images.length,
        ...(stashedRecords.length > 0 ? { records: stashedRecords } : {}),
      });

      // Clearing the composer is only safe once the write actually landed.
      // If it was rejected (quota) the store has already rolled itself back,
      // so leave the composer untouched rather than making it the second
      // casualty of a reload.
      if (!written) {
        toastManager.add({
          type: "error",
          title: "Could not stash this prompt",
          description:
            "Browser storage rejected the write, so the composer was left as-is. Free up site data and try again.",
          data: { hideCopyButton: true },
        });
        return;
      }
      // Written but only into the in-memory fallback (localStorage blocked):
      // the entry is visible and restorable this session, so proceed with the
      // clear, but say it won't survive a reload.
      if (!durable) {
        toastManager.add({
          type: "warning",
          title: "Stashed prompt will not survive a reload",
          description:
            "Browser storage is unavailable, so this stash is kept in memory only for this session.",
          data: { hideCopyButton: true },
        });
      }

      // Everything the entry carries leaves the draft with it.
      promptRef.current = "";
      clearComposerDraftPromptAndImages(stashTarget);
      clearComposerDraftTerminalContexts(stashTarget);
      for (const comment of composerReviewComments) {
        removeComposerDraftReviewComment(stashTarget, comment.id);
      }
      for (const annotation of composerPreviewAnnotations) {
        releaseAttachmentUpload(annotation.id);
        removeComposerDraftPreviewAnnotation(stashTarget, annotation.id);
      }
      setComposerDraftPrompt(stashTarget, "");
      for (const image of images) {
        releaseAttachmentUpload(image.id);
      }
      setComposerCursor(0);
      setComposerTrigger(null);
      pulseStashBadge();

      if (evicted) {
        for (const file of evicted.files ?? []) {
          releasePersistedAttachmentUpload({
            id: file.id,
            environmentId: file.environmentId,
            attachmentId: file.attachmentId,
          });
        }
        toastManager.add({
          type: "warning",
          title: "Oldest stashed prompt discarded",
          description: `The stash holds ${MAX_STASH_ENTRIES} prompts; the oldest was removed to make room.`,
          data: { hideCopyButton: true },
        });
      }

      // Images are re-encoded for the stash rather than stored verbatim: the
      // composer allows up to 10MB per image, but localStorage gives the whole
      // origin ~5MB. Only the stashed copy shrinks; the live attachment (and
      // anything sent without stashing) keeps the original file.
      const candidateAttachments: PersistedComposerImageAttachment[] = [];
      const oversizedImageNames: string[] = [];
      const unreadableImageNames: string[] = [];
      for (const image of images) {
        const result = await compressImageForStash(image.file);
        if (!result.ok) {
          // "too large" and "could not be read" are distinct outcomes; the
          // menu and restore toast report them separately.
          (result.reason === "too-large" ? oversizedImageNames : unreadableImageNames).push(
            image.name,
          );
          continue;
        }
        candidateAttachments.push({
          id: image.id,
          name: image.name,
          mimeType: result.image.mimeType,
          sizeBytes: result.image.sizeBytes,
          dataUrl: result.image.dataUrl,
          ...(image.source
            ? { source: resizeSnapShotSource(image.source, result.image.imageSize) }
            : {}),
        });
      }
      const { kept, droppedNames } = partitionStashAttachments(candidateAttachments);

      const { attached, durable: imagesDurable } = finalizeStashEntryImages(entryId, {
        attachments: kept,
        droppedImageNames: [...oversizedImageNames, ...droppedNames],
        unreadableImageNames,
      });
      if (attached) {
        // The second phase can be rejected on its own: the text-only entry
        // fit, but adding image payloads pushed past the quota. Disk would
        // then still hold the phase-one entry with pendingImageCount set,
        // which reads as an orphan after reload — so say so now. Gated on the
        // entry write having been durable: on the in-memory fallback nothing
        // is ever durable, and the session-only warning already covered it.
        if (!imagesDurable && durable && images.length > 0) {
          toastManager.add({
            type: "warning",
            title: "Stashed images were not saved",
            description:
              "The prompt was stashed, but browser storage rejected its images. They will be missing if you reload.",
            data: { hideCopyButton: true },
          });
        }
      } else if (kept.length > 0) {
        // The entry was restored or deleted before its images finished
        // encoding, so they have nowhere to land. Say so rather than letting
        // them evaporate.
        toastManager.add({
          type: "warning",
          title: "Stashed images did not attach",
          description: `That prompt was restored or deleted before ${kept.length} image${kept.length === 1 ? "" : "s"} finished saving. Re-attach ${kept.length === 1 ? "it" : "them"} if you still need ${kept.length === 1 ? "it" : "them"}.`,
          data: { hideCopyButton: true },
        });
      }
    } finally {
      // Must clear on every path: a throw that left this set would wedge this
      // snapshot's ⌘S until the composer remounts.
      stashInFlightRef.current.delete(snapshotKey);
    }
  }, [
    clearComposerDraftPromptAndImages,
    clearComposerDraftTerminalContexts,
    setComposerDraftPrompt,
    composerDraftTarget,
    composerFilesRef,
    composerImagesRef,
    composerTerminalContextsRef,
    composerReviewComments,
    composerPreviewAnnotations,
    removeComposerDraftReviewComment,
    removeComposerDraftPreviewAnnotation,
    environmentId,
    finalizeStashEntryImages,
    promptRef,
    pulseStashBadge,
    restoreStashEntry,
    stashEntryToQueue,
  ]);

  const toggleStashMenu = useCallback(() => {
    if (isComposerCollapsedMobile) {
      expandMobileComposer();
      setIsStashMenuOpen(true);
      return;
    }
    setIsStashMenuOpen((open) => !open);
  }, [expandMobileComposer, isComposerCollapsedMobile]);
  const toggleTasksDrawer = useCallback(() => {
    setIsTasksDrawerOpen((open) => !open);
  }, []);
  const hasBannerItems = props.bannerItems.length > 0;
  const hasBlockingComposerTopDrawer =
    activePendingApproval !== null || pendingUserInputs.length > 0;
  const showInlineTasksBadge =
    activeTasksProgress !== null &&
    activeTaskSteps !== null &&
    !isTasksDrawerOpen &&
    !hasBlockingComposerTopDrawer &&
    (hasBannerItems || showComposerTopDrawer || isComposerCollapsedMobile);
  const inlineTasksBadge = showInlineTasksBadge ? (
    <ComposerTasksBadge
      expanded={false}
      onToggle={toggleTasksDrawer}
      placement="inline"
      progress={activeTasksProgress}
      steps={activeTaskSteps}
    />
  ) : null;
  const hasImageAttachmentAttention = standaloneComposerImages.some((image) => {
    const upload = uploadsByImageId[image.id];
    const failedInCurrentEnvironment =
      supportsAttachmentUploads &&
      upload?.status === "failed" &&
      upload.environmentId === environmentId;
    // A failed upload remains actionable in the expanded attachment tray, but
    // its collapsed thumbnail is enough to signal that the draft has images.
    return nonPersistedComposerImageIdSet.has(image.id) && !failedInCurrentEnvironment;
  });
  // A Brain operation only describes an ordinary turn. An approval or a pending
  // provider question owns the composer's input while it is up, so the
  // operation steps aside rather than replacing what answers it.
  const brainActionOwnsInput =
    brainAction !== null && !hasBlockingComposerTopDrawer && activePendingProgress === null;
  const brainActionDescriptor =
    brainActionOwnsInput && brainAction !== null ? describeBrainAction(brainAction) : null;
  // Lint and Delete take no typed input: the description stands in for the
  // editor, so the one-line resting layout has nothing to rest on.
  const brainActionTyping = brainActionDescriptor?.typing ?? null;
  const brainActionReplacesInput = !brainActionCarriesComposerInput(brainAction);
  brainComposerCarriesInputRef.current = !brainActionReplacesInput;
  // The mixed native panel depends on the client surface and on where the
  // thread's Environment runs. Without it the browser file input takes files
  // only, and a folder needs a pick of its own that not every browser has.
  const composerFilesPickTakesFolders = useMemo(
    () =>
      resolveRuntimeComposerFilesAndFoldersOpenStrategy(environmentMachine).kind !==
      "browser-file-input",
    [environmentMachine],
  );
  const composerFolderPickAvailable =
    !composerFilesPickTakesFolders && browserFolderPickSupported();
  const designOperationTyping =
    designOperation === null ? null : describeDesignComposerOperation(designOperation).typing;
  // The chip survives a reload with the draft, so a collapsed composer has to
  // say which Action it is about to send.
  const collapsedComposerActionMarker = composerActionChipModel?.label ?? null;
  useEffect(() => {
    // A URL collected for Add Example means nothing in any other mode, and the
    // chip row is the only place it is visible — so leaving the mode drops it
    // rather than parking evidence the next send would silently inherit.
    if (designOperation === null || !designComposerOperationCollectsEvidenceUrls(designOperation)) {
      setDesignEvidenceUrls((current) => (current.length === 0 ? current : []));
    }
  }, [designOperation]);
  // Banners and the tasks badge dock above the surface rather than inside
  // it, so they do not hold the composer open; only surface-internal chrome
  // does.
  const composerHasExpandedChrome =
    showComposerTopDrawer ||
    isTasksDrawerOpen ||
    composerMenuOpen ||
    isStashMenuOpen ||
    isDragOverComposer ||
    isPreparingWorktree ||
    showProviderUnavailable ||
    projectSelectionRequired ||
    environmentUnavailable !== null ||
    composerSubmissionError !== null ||
    providerInputSubmissionError !== null ||
    composerAction !== null ||
    brainActionReplacesInput ||
    hasImageAttachmentAttention;
  const isComposerResting = shouldUseRestingComposerLayout({
    isExistingThread: routeKind === "server" && activeThreadId !== null,
    isMobileViewport,
    isScrollCollapsed: isComposerScrollCollapsed,
    hasExpandedChrome: composerHasExpandedChrome,
    hasMultilinePrompt,
    timelineOverflows,
  });
  const expandedComposerImages = isComposerResting
    ? standaloneComposerImages.filter((image) => pendingSnapShotIdSet.has(image.id))
    : standaloneComposerImages;
  // The relocated controls live in the context strip whenever the composer is
  // collapsed for any reason, the desktop resting layout or the phone
  // collapse. Both leave the footer controls unrendered. Without a strip, a
  // resting composer keeps them in an inline row below the prompt; the phone
  // collapse shows none until the composer is focused.
  const composerControlsInStrip = isComposerResting || isComposerCollapsedMobile;
  const showInlineRestingControls = isComposerResting && restingControlsHost === null;
  const composerControlsVisibleInStrip = composerControlsInStrip && restingControlsVisible;
  const composerControlsHidden = composerControlsInStrip && !restingControlsVisible;
  if (composerControlsHidden && isComposerModelPickerOpen) {
    setIsComposerModelPickerOpen(false);
  }
  useLayoutEffect(() => {
    onRestingControlsVisibilityChange(composerControlsVisibleInStrip);
  }, [composerControlsVisibleInStrip, onRestingControlsVisibilityChange]);
  useLayoutEffect(() => {
    onRestingChange(isComposerResting);
  }, [isComposerResting, onRestingChange]);
  const restingImagePreviewCounts = getRestingComposerImagePreviewCounts(
    standaloneComposerImages.length,
  );
  const restingComposerImages = standaloneComposerImages.slice(
    0,
    restingImagePreviewCounts.visibleCount,
  );
  const collapsedComposerImagePreviews =
    restingComposerImages.length > 0 ? (
      <div
        data-chat-composer-resting-images="true"
        className="flex shrink-0 items-center gap-1 ps-1"
      >
        {restingComposerImages.map((image) => (
          <button
            key={image.id}
            type="button"
            className="relative size-7 shrink-0 cursor-zoom-in overflow-hidden rounded-md border border-border/70 bg-muted/60"
            aria-label={`Preview ${image.name}`}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => {
              const preview = buildExpandedImagePreview(composerImages, image.id);
              if (preview) onExpandImage(preview);
            }}
          >
            {image.previewUrl ? (
              <ComposerImageThumbnail
                file={image.file}
                alt=""
                className="size-full object-cover"
                fallback={
                  <PierreEntryIcon
                    pathValue={image.name}
                    kind="file"
                    theme={resolvedTheme}
                    className="m-auto size-3.5"
                  />
                }
              />
            ) : (
              <PierreEntryIcon
                pathValue={image.name}
                kind="file"
                theme={resolvedTheme}
                className="m-auto size-3.5"
              />
            )}
          </button>
        ))}
        {restingImagePreviewCounts.overflowCount > 0 ? (
          <button
            type="button"
            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border/70 bg-muted/60 font-medium text-secondary-label text-xs tabular-nums outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            aria-label={`Show ${String(restingImagePreviewCounts.overflowCount)} more image attachments`}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => {
              if (isComposerCollapsedMobile) {
                expandMobileComposer();
                return;
              }
              setIsComposerScrollCollapsed(false);
              setIsComposerFocused(true);
              scheduleComposerFocus();
            }}
          >
            +{restingImagePreviewCounts.overflowCount}
          </button>
        ) : null}
      </div>
    ) : null;
  const composerMainSurfaceRef = useComposerRestingTransition(
    composerControlsInStrip,
    isComposerResting,
    restingComposerControlsRef,
    onComposerOverlayHeightChange,
  );
  const canTrackComposerScrollGesture =
    routeKind === "server" && activeThreadId !== null && !isMobileViewport;
  const canScrollCollapseComposer =
    canTrackComposerScrollGesture &&
    settings.composerCollapseOnScroll &&
    !hasMultilinePrompt &&
    !composerHasExpandedChrome &&
    !showInlineTasksBadge;
  // Scrolling only has something to collapse while the composer is expanded,
  // focused or not, so the wheel handler keys off the resting state rather
  // than editor focus.
  composerScrollCollapseEligibleRef.current = canScrollCollapseComposer && !isComposerResting;

  useEffect(() => {
    if (!canScrollCollapseComposer) {
      setIsComposerScrollCollapsed(false);
    }
  }, [canScrollCollapseComposer, setIsComposerScrollCollapsed]);

  // Returning to the window re-fires focus on the element that already held
  // it. That focus arrives after the window's own event, so a window focus
  // marks the frame in which it should be ignored by the form's capture.
  useEffect(() => {
    if (!isComposerScrollCollapsed) return;
    let frame: number | null = null;
    const onWindowFocus = () => {
      windowRefocusInFlightRef.current = true;
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        windowRefocusInFlightRef.current = false;
      });
    };
    window.addEventListener("focus", onWindowFocus);
    return () => {
      window.removeEventListener("focus", onWindowFocus);
      if (frame !== null) window.cancelAnimationFrame(frame);
      windowRefocusInFlightRef.current = false;
    };
  }, [isComposerScrollCollapsed]);

  useEffect(() => {
    if (!canTrackComposerScrollGesture) return;

    const finishScrollGesture = () => {
      if (composerScrollCollapseTimeoutRef.current !== null) {
        window.clearTimeout(composerScrollCollapseTimeoutRef.current);
      }
      composerScrollCollapseTimeoutRef.current = null;
      resetComposerScrollGesture(composerScrollGestureRef.current);
    };
    const handleTimelineWheel = (event: WheelEvent) => {
      if (event.ctrlKey || !(event.target instanceof Element)) {
        return;
      }

      const scrollNode = getTimelineScrollableNode();
      if (!scrollNode) return;
      const insideTimeline = scrollNode.contains(event.target);
      if (!insideTimeline && !composerScrollGestureRef.current.collapseSuppressed) return;
      // A nested code block or tool result that can still scroll consumes the
      // wheel itself, so it never counts toward resting the composer.
      const targetsTimeline =
        insideTimeline && isTimelineScrollTarget(event.target, scrollNode, event.deltaY);

      if (composerScrollCollapseTimeoutRef.current !== null) {
        window.clearTimeout(composerScrollCollapseTimeoutRef.current);
      }
      composerScrollCollapseTimeoutRef.current = window.setTimeout(
        finishScrollGesture,
        COMPOSER_SCROLL_GESTURE_RESET_MS,
      );

      const canScrollInGestureDirection =
        targetsTimeline &&
        (event.deltaY < 0
          ? scrollNode.scrollTop > 0
          : scrollNode.scrollTop < scrollNode.scrollHeight - scrollNode.clientHeight);
      const deltaPx =
        Math.abs(event.deltaY) *
        (event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? scrollNode.clientHeight
            : 1);
      const shouldCollapse = recordComposerScrollGestureEvent(composerScrollGestureRef.current, {
        now: window.performance.now(),
        deltaPx,
        collapseThresholdPx: COMPOSER_SCROLL_COLLAPSE_THRESHOLD_PX,
        collapseEligible: targetsTimeline && composerScrollCollapseEligibleRef.current,
        canScrollInGestureDirection,
        scrollsTowardLogicalEnd: event.deltaY > 0 && isTimelineAtLogicalEnd(),
      });
      if (!shouldCollapse) {
        return;
      }

      setIsComposerScrollCollapsed(true);
    };

    document.addEventListener("wheel", handleTimelineWheel, { capture: true, passive: true });
    return () => {
      document.removeEventListener("wheel", handleTimelineWheel, true);
      finishScrollGesture();
    };
  }, [
    activeThreadId,
    canTrackComposerScrollGesture,
    getTimelineScrollableNode,
    isTimelineAtLogicalEnd,
    setIsComposerScrollCollapsed,
  ]);

  const restingHiddenBlockCount = composerControlsInStrip ? restingControlsHiddenBlockCount : 0;
  const composerControlsCompact = !composerControlsInStrip && isComposerFooterCompact;
  const restingProviderTraitsPicker = renderProviderTraitsPicker({
    ...providerTraitsPickerInput,
    size: "xs",
    hidden: composerControlsHidden || restingHiddenBlockCount > 1,
  });
  const restingBlockDefs = [
    ...(providerTraitsPicker
      ? [
          {
            id: "traits",
            content: (
              <>
                <ComposerControlSeparator size={composerControlsInStrip ? "xs" : "sm"} />
                {composerControlsInStrip ? restingProviderTraitsPicker : providerTraitsPicker}
              </>
            ),
          },
        ]
      : []),
    {
      id: "mode",
      content: (
        <ComposerFooterModeControls
          showInteractionModeToggle={planModeUiEnabled}
          interactionMode={interactionMode}
          runtimeMode={runtimeMode}
          size={composerControlsInStrip ? "xs" : "sm"}
          hidden={composerControlsHidden || restingHiddenBlockCount > 0}
          onToggleInteractionMode={toggleInteractionMode}
          onRuntimeModeChange={handleRuntimeModeChange}
        />
      ),
    },
  ];
  const hiddenRestingBlockIds = restingBlockDefs
    .slice(restingBlockDefs.length - restingHiddenBlockCount)
    .map((def) => def.id);
  const composerControls = showProviderUnavailable ? (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      disabled={!providerSetupInstanceId}
      onClick={() => {
        if (providerSetupInstanceId) {
          onOpenProviderSetup(providerSetupInstanceId);
        }
      }}
      data-chat-provider-unavailable="true"
      className="shrink-0 gap-2 px-2 text-secondary-label sm:px-3"
    >
      <CircleAlertIcon className="size-4" />
      {providerSetupInstanceId ? "Open provider settings" : "No provider available"}
    </Button>
  ) : (
    <>
      {composerControlsInStrip && restingControlsHaveLeadingContext ? (
        <ComposerControlSeparator
          size="xs"
          className="@max-[400px]/composer-surface:hidden"
          data-resting-controls-separator="true"
        />
      ) : null}
      <ProviderModelPicker
        disabled={providerCatalogPending}
        activeInstanceId={
          providerCatalogPending
            ? (activeThreadModelSelection?.instanceId ?? selectedInstanceId)
            : selectedInstanceId
        }
        model={
          providerCatalogPending
            ? (activeThreadModelSelection?.model ?? selectedModelForPickerWithCustomFallback)
            : selectedModelForPickerWithCustomFallback
        }
        lockedProvider={lockedProvider}
        lockedContinuationGroupKey={lockedContinuationGroupKey}
        instanceEntries={providerInstanceEntries}
        keybindings={keybindings}
        modelOptionsByInstance={modelOptionsByInstance}
        compact={composerControlsInStrip}
        triggerClassName={
          composerControlsInStrip
            ? cn(
                "min-w-13 shrink text-xs!",
                // The inline row is as wide as the composer, so its picker
                // keeps the label and shrinks only after the trailing blocks.
                !showInlineRestingControls &&
                  "@max-[640px]/composer-surface:[&_[data-chat-provider-model-picker-label]]:w-0 @max-[640px]/composer-surface:[&_[data-chat-provider-model-picker-label]]:flex-none",
              )
            : "-ms-2.5"
        }
        terminalOpen={terminalOpen}
        open={isComposerModelPickerOpen}
        {...(composerProviderState.modelPickerIconClassName || composerControlsInStrip
          ? {
              activeProviderIconClassName: cn(
                composerProviderState.modelPickerIconClassName,
                composerControlsInStrip &&
                  "fill-muted-foreground/70! text-muted-foreground/70! [&_path]:fill-muted-foreground/70! [&_rect]:fill-muted-foreground/70!",
              ),
            }
          : {})}
        onOpenChange={setIsComposerModelPickerOpen}
        getModelDisabledReason={getModelDisabledReason}
        onInstanceModelChange={onProviderModelSelect}
      />

      {composerControlsCompact ? (
        <CompactComposerControlsMenu
          interactionMode={interactionMode}
          runtimeMode={runtimeMode}
          showInteractionModeToggle={planModeUiEnabled}
          traitsMenuContent={providerTraitsMenuContent}
          onToggleInteractionMode={toggleInteractionMode}
          onRuntimeModeChange={handleRuntimeModeChange}
        />
      ) : (
        <>
          {restingBlockDefs.map((def, index) => {
            if (!composerControlsInStrip) {
              return <Fragment key={def.id}>{def.content}</Fragment>;
            }
            const hidden = index >= restingBlockDefs.length - restingHiddenBlockCount;
            return (
              <div
                key={def.id}
                data-resting-block={def.id}
                aria-hidden={hidden || undefined}
                inert={hidden || undefined}
                className={cn(
                  "flex w-max min-w-max shrink-0 items-center gap-1",
                  hidden && "pointer-events-none invisible absolute",
                )}
              >
                {def.content}
              </div>
            );
          })}
          {composerControlsInStrip ? (
            <div
              data-resting-controls-overflow
              aria-hidden={hiddenRestingBlockIds.length === 0 || undefined}
              inert={hiddenRestingBlockIds.length === 0 || undefined}
              className={cn(
                "min-w-0 shrink-0",
                hiddenRestingBlockIds.length === 0 && "pointer-events-none invisible absolute",
              )}
            >
              <CompactComposerControlsMenu
                interactionMode={interactionMode}
                runtimeMode={runtimeMode}
                size="xs"
                hidden={composerControlsHidden || hiddenRestingBlockIds.length === 0}
                showInteractionModeToggle={
                  planModeUiEnabled && hiddenRestingBlockIds.includes("mode")
                }
                traitsMenuContent={
                  hiddenRestingBlockIds.includes("traits") ? providerTraitsMenuContent : undefined
                }
                onToggleInteractionMode={toggleInteractionMode}
                onRuntimeModeChange={handleRuntimeModeChange}
              />
            </div>
          ) : null}
        </>
      )}
    </>
  );
  const showTasksTab =
    !hasBannerItems &&
    !showComposerTopDrawer &&
    !isTasksDrawerOpen &&
    !isComposerCollapsedMobile &&
    activeTasksProgress !== null &&
    activeTaskSteps !== null &&
    activeTasksProgress.totalSteps > 0;
  const activityStackContent = hasBannerItems ? (
    props.threadSyncPhase ? (
      <ComposerActivityRow phase={props.threadSyncPhase} />
    ) : !hasBlockingComposerTopDrawer && activeTasksProgress && activeTaskSteps ? (
      <ComposerTasksContent
        expanded={isTasksDrawerOpen}
        onToggle={toggleTasksDrawer}
        progress={activeTasksProgress}
        steps={activeTaskSteps}
      />
    ) : null
  ) : null;
  const activityStackItem: ComposerBannerStackContent | null = activityStackContent
    ? {
        id: "composer-activity",
        variant: "default",
        priority: "activity",
        content: activityStackContent,
      }
    : null;
  const bannerStackItems = activityStackItem
    ? [activityStackItem, ...props.bannerItems]
    : props.bannerItems;
  useEffect(() => {
    if (activeTasksProgress === null || activeTaskSteps === null) {
      setIsTasksDrawerOpen(false);
    }
  }, [activeTaskSteps, activeTasksProgress]);

  useEffect(() => {
    if (hasBlockingComposerTopDrawer) {
      setIsTasksDrawerOpen(false);
    }
  }, [hasBlockingComposerTopDrawer]);

  useEffect(() => {
    setIsTasksDrawerOpen(false);
  }, [activeThreadId]);

  // Close the stash menu whenever the trigger-driven command menu opens so
  // the two popovers never stack in the same layer, and when the user
  // resumes typing (the menu is a transient picker, not a panel).
  useEffect(() => {
    if (composerMenuOpen) {
      setIsStashMenuOpen(false);
    }
  }, [composerMenuOpen]);
  useEffect(() => {
    setIsStashMenuOpen(false);
  }, [prompt]);

  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      const command = resolveShortcutCommand(event, keybindings, {
        context: {
          terminalFocus: getTerminalFocusOwner() !== null,
          terminalOpen,
          modelPickerOpen: isComposerModelPickerOpen,
        },
      });
      if (command !== "composer.stash") return;
      // Always claim the shortcut so the browser save dialog never opens,
      // even when the composer is in a state that can't stash.
      event.preventDefault();
      event.stopPropagation();
      if (isCommandPaletteOpen() || isRevertingCheckpoint) {
        return;
      }
      if (pendingUserInputs.length > 0 && !isComposerApprovalState) {
        setIsStashMenuOpen((open) => !open);
        return;
      }
      if (isComposerApprovalState || projectSelectionRequired || activePendingProgress !== null) {
        return;
      }
      void stashCurrentPrompt();
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [
    activePendingProgress,
    isComposerApprovalState,
    isComposerModelPickerOpen,
    keybindings,
    pendingUserInputs.length,
    projectSelectionRequired,
    stashCurrentPrompt,
    isRevertingCheckpoint,
    terminalOpen,
  ]);

  // ------------------------------------------------------------------
  // Callbacks: attachments
  // ------------------------------------------------------------------
  const countReservedAttachments = () => {
    const questionRequest = pendingUserInputs[0];
    const otherQuestionKeys =
      questionAttachmentTarget && questionRequest && activeThreadId
        ? questionRequest.questions
            .map((question) =>
              questionAttachmentDraftId(
                environmentId,
                activeThreadId,
                questionRequest.requestId,
                question.id,
              ),
            )
            .filter((key) => key !== questionAttachmentTarget)
        : [];
    return (
      composerImagesRef.current.length +
      composerFilesRef.current.length +
      (pendingImageCompressionsRef.current.get(attachmentTargetKey) ?? 0) +
      countQuestionAttachments(otherQuestionKeys)
    );
  };
  /** Resolves true when at least one chip was inserted for the accepted attachments. */
  const addComposerAttachments = async (
    files: File[],
    options?: {
      readonly source?: ChatFileAttachment["source"];
      readonly selection?: { start: number; end: number };
    },
  ): Promise<boolean> => {
    if (!activeThreadId || files.length === 0 || isRevertingCheckpointRef.current) return false;
    if (brainActionReplacesInput) {
      toastManager.add({
        type: "error",
        title: `${brainActionDescriptor?.label ?? "This operation"} takes no attachments.`,
        description: "Remove the action first to attach anything to this message.",
      });
      return false;
    }
    if (
      pendingUserInputs.length > 0 &&
      (!supportsQuestionAttachments ||
        activePendingProgress?.activeQuestion?.allowCustomAnswer === false ||
        activePendingIsResponding)
    ) {
      toastManager.add({
        type: "error",
        title: "This question cannot accept attachments.",
      });
      return false;
    }
    // Captured before the awaits below: the user may switch threads while a
    // large image is being compressed, and the attachments and errors belong
    // to the thread the paste happened in.
    const threadId = activeThreadId;

    // Validation happens synchronously so concurrent pastes see each other:
    // accepted files reserve their attachment slots (via the pending counter)
    // before the first await, keeping the total under the limit.
    const pendingCount = pendingImageCompressionsRef.current.get(attachmentTargetKey) ?? 0;
    let reservedCount = countReservedAttachments();
    // A pick that matches a needs-reattach marker replaces it in the draft, so
    // it must not consume a slot; a draft full of markers would otherwise hit
    // the capacity error before the replacement path could run.
    const reattachMarkers = composerFilesRef.current.filter(composerFileNeedsReattach);
    const replacedReattachMarkerIds = new Set<string>();
    const acceptedImages: File[] = [];
    const acceptedFiles: ComposerFileAttachment[] = [];
    let error: string | null = null;
    for (const file of files) {
      // An Ingest source stays the original file, so an image is never downscaled.
      const attachmentKind = takesSourcesAsIs ? "file" : classifyComposerAttachmentFile(file);
      const fileMimeType =
        attachmentKind === "file"
          ? (videoMimeType({ name: file.name, mimeType: file.type }) ??
            (file.type || "application/octet-stream"))
          : file.type;
      const matchingReattachMarker =
        attachmentKind === "file"
          ? reattachMarkers.find(
              (marker) =>
                !replacedReattachMarkerIds.has(marker.id) &&
                composerFileMatchesReattachMarker(marker, {
                  name: file.name || "file",
                  mimeType: fileMimeType,
                  sizeBytes: file.size,
                }),
            )
          : undefined;
      if (matchingReattachMarker) {
        replacedReattachMarkerIds.add(matchingReattachMarker.id);
      }
      if (!matchingReattachMarker && reservedCount >= attachmentLimit) {
        error = `You can attach up to ${attachmentLimit} files per message.`;
        // Keep scanning: a later file in this batch can still replace a
        // needs-reattach marker without needing a free slot.
        continue;
      }
      if (attachmentKind === "unsupported-image") {
        error = `'${file.name}' is not a supported image type. Attach GIF, HEIC, HEIF, JPEG, PNG, or WebP images.`;
        continue;
      }
      if (attachmentKind === "image") {
        acceptedImages.push(normalizeComposerImageFileMimeType(file));
      } else {
        if (fileStagingLimit === null) {
          error = "This server does not support file attachments.";
          continue;
        }
        if (file.size <= 0) {
          error = `'${file.name}' is empty or could not be read.`;
          continue;
        }
        if (file.size > fileStagingLimit) {
          error = fileAttachmentTooLargeMessage(file.name, fileStagingLimit);
          continue;
        }
        const attachmentFile =
          file.type === fileMimeType
            ? file
            : new File([file], file.name, { type: fileMimeType, lastModified: file.lastModified });
        acceptedFiles.push({
          type: "file",
          id: randomUUID(),
          name: attachmentFile.name || "file",
          mimeType: fileMimeType,
          sizeBytes: attachmentFile.size,
          file: attachmentFile,
          ...(options?.source ? { source: options.source } : {}),
        });
      }
      if (!matchingReattachMarker) {
        reservedCount += 1;
      }
    }
    setThreadError(threadId, error);
    let insertedAny = false;
    if (acceptedFiles.length > 0) {
      // Only files the draft actually took get a chip; a duplicate is deduped by the store
      // and a chip for it would point at nothing.
      const storedIds = new Set(addComposerFilesToDraft(acceptedFiles));
      const storedFiles = acceptedFiles.filter((file) => storedIds.has(file.id));
      if (storedFiles.length > 0) {
        insertedAny = insertAttachmentReferences(
          storedFiles.map(fileContextReference),
          options?.selection,
        );
      }
      if (options?.source?._tag === "pasted-text" && storedFiles.length > 0) {
        const attached = storedFiles[0]!;
        toastManager.add({
          type: "info",
          title: `Large paste attached as ${attached.name}`,
          description: `${formatAttachmentSize(attached.sizeBytes)} · Use ${
            isMacPlatform(navigator.platform) ? "⌘⇧V" : "Ctrl+Shift+V"
          } to keep a large paste inline.`,
          data: { hideCopyButton: true },
        });
      }
    }
    if (acceptedImages.length === 0) return insertedAny;

    pendingImageCompressionsRef.current.set(
      attachmentTargetKey,
      pendingCount + acceptedImages.length,
    );
    if (questionAttachmentTarget)
      changeQuestionAttachmentPreparation(questionAttachmentTarget, acceptedImages.length);
    try {
      const nextImages: ComposerImageAttachment[] = [];
      let compressionError: string | null = null;
      for (const file of acceptedImages) {
        // Images over the wire cap are downscaled to fit rather than
        // refused; files already within it pass through byte-for-byte.
        const compressed = await prepareImageForAttachment(
          file,
          PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
        );
        if (!compressed.ok) {
          compressionError =
            compressed.reason === "unreadable"
              ? `'${file.name}' could not be read as an image.`
              : `'${file.name}' is too large to attach, even after compression.`;
          continue;
        }
        const attachmentFile = compressed.file;
        const previewUrl = URL.createObjectURL(attachmentFile);
        nextImages.push({
          type: "image",
          id: randomUUID(),
          name: attachmentFile.name || "image",
          mimeType: attachmentFile.type,
          sizeBytes: attachmentFile.size,
          previewUrl,
          file: attachmentFile,
        });
      }
      if (
        questionAttachmentTarget &&
        !useQuestionAttachmentPreparation.getState().counts[questionAttachmentTarget]
      ) {
        for (const image of nextImages) URL.revokeObjectURL(image.previewUrl);
        return false;
      }
      const storedImageIds = new Set(
        nextImages.length === 1 && nextImages[0]
          ? addComposerImage(nextImages[0])
          : nextImages.length > 1
            ? addComposerImagesToDraft(nextImages)
            : [],
      );
      const storedImages = nextImages.filter((image) => storedImageIds.has(image.id));
      if (storedImages.length > 0) {
        insertedAny =
          insertAttachmentReferences(storedImages.map(imageContextReference)) || insertedAny;
      }
      // Only failures are reported here. Success must not pass `null`: by
      // now other work (a failed send, an overlapping paste) may have set a
      // thread error this call knows nothing about, and clearing it would
      // swallow that message.
      if (compressionError !== null) {
        setThreadError(threadId, compressionError);
      }
    } finally {
      if (questionAttachmentTarget)
        changeQuestionAttachmentPreparation(questionAttachmentTarget, -acceptedImages.length);
      const remaining =
        (pendingImageCompressionsRef.current.get(attachmentTargetKey) ?? 0) - acceptedImages.length;
      if (remaining > 0) {
        pendingImageCompressionsRef.current.set(attachmentTargetKey, remaining);
      } else {
        pendingImageCompressionsRef.current.delete(attachmentTargetKey);
      }
    }
    return insertedAny;
  };

  /**
   * Chips for freshly attached files land at the caret; when the editor cannot take
   * input (approval, pending questions) they are appended so the file is never invisible.
   */
  const insertAttachmentReferences = (
    references: ReadonlyArray<ComposerContextReference>,
    selection?: { start: number; end: number },
  ): boolean => {
    if (references.length === 0) return false;
    // Question answers carry attachments beside the answer, never as chips. Falling back to
    // the thread prompt here would hide the file behind a reference the question never shows.
    if (questionAttachmentTarget) return false;
    // Ingest shows what it gathered in its own row, so a reference here would
    // be a second copy of the same source, sitting in the middle of the text.
    if (brainComposerOwnsAttachments) return false;
    if (selection) {
      const edit = inlineContextReferenceReplacement(promptRef.current, selection, references);
      return applyPromptReplacement(edit.start, edit.end, edit.text);
    }
    const text = references.map(formatInlineContextReference).join(" ");
    const inserted = insertComposerText(`${text} `, "cursor", { ensureLeadingBoundary: true });
    if (!inserted) {
      setPrompt(ensureInlineContextReferences(promptRef.current, references));
    }
    return true;
  };

  /**
   * Stage what a system picker returned. A picked path and a browser folder
   * are references the composer holds as-is; a browser file is bytes.
   */
  /** Pending plan questions take the composer, so nothing attaches until they are answered. */
  const attachingBlockedByPlanQuestions = (): boolean => {
    if (pendingUserInputs.length === 0) return false;
    toastManager.add({ type: "error", title: "Attach files after answering plan questions." });
    return true;
  };

  const addPickedPathAttachments = async (
    attachments: ReadonlyArray<ComposerPathAttachment>,
  ): Promise<void> => {
    if (attachments.length === 0 || brainActionReplacesInput) return;
    const staged = attachments.flatMap((attachment) => {
      const entry = composerFileFromPathAttachment(attachment);
      return entry === null ? [] : [entry];
    });
    // A reference stages straight into the draft: it is already complete, and
    // the ordinary attachment route would try to measure and upload bytes that
    // do not exist. Bytes take that route, which validates size and capacity.
    const referenceFiles = staged.flatMap((entry) =>
      "id" in entry ? [entry as ComposerFileAttachment] : [],
    );
    const byteFiles = staged.flatMap((entry) => ("id" in entry ? [] : [entry.file]));
    if (referenceFiles.length > 0) {
      const storedIds = new Set(addComposerFilesToDraft(referenceFiles));
      const storedFiles = referenceFiles.filter((file) => storedIds.has(file.id));
      if (storedFiles.length > 0) {
        insertAttachmentReferences(storedFiles.map(fileContextReference));
      }
    }
    if (byteFiles.length > 0) {
      await addComposerAttachments(byteFiles);
    }
  };

  /**
   * A browser without the mixed native panel can still pick one folder through
   * the File System Access API, which returns a live directory handle rather
   * than the "Upload N files" prompt a `webkitdirectory` input would raise.
   */
  const openFolderPicker = async (): Promise<void> => {
    if (pendingUserInputs.length > 0) {
      toastManager.add({ type: "error", title: "Attach a folder after answering plan questions." });
      return;
    }
    try {
      const folder = await pickBrowserFolderAttachment(randomUUID);
      if (folder === null) return;
      await attachWithIngestChoice({ kind: "paths", attachments: [folder] });
    } catch {
      toastManager.add({ type: "error", title: "Could not read that folder." });
    }
  };

  /**
   * The primary Files and folders pick. Desktop and Mac Host Web open a native
   * panel that returns real paths; every other surface falls back to the
   * browser file input, whose files carry bytes and no path.
   */
  const openFilesAndFoldersPicker = async (): Promise<void> => {
    if (attachingBlockedByPlanQuestions()) return;
    const strategy = resolveRuntimeComposerFilesAndFoldersOpenStrategy(environmentMachine);
    const openingToastId =
      strategy.kind === "host-native-mixed"
        ? toastManager.add({
            type: "loading",
            title: "Opening macOS file picker…",
            description: "If you do not see it, check behind this browser window.",
          })
        : null;
    try {
      const result = await openRuntimeComposerFilesAndFolders({
        makeId: randomUUID,
        strategy,
        openBrowserFileInput: () => {
          attachmentInputRef.current?.click();
        },
      });
      if (result.kind === "attachments") {
        await attachWithIngestChoice({ kind: "paths", attachments: result.attachments });
        return;
      }
      if (result.kind === "failed") {
        toastManager.add({
          type: "error",
          title: "Could not open the files and folders picker.",
          description: "Re-open Host Web from the app, then try again.",
        });
      }
    } finally {
      if (openingToastId !== null) toastManager.close(openingToastId);
    }
  };

  const removeComposerImage = (imageId: string) => {
    const image = composerImagesRef.current.find((candidate) => candidate.id === imageId);
    const referenced = collectInlineContextIds(promptRef.current).includes(
      image ? imageContextReference(image).contextId : "",
    );
    if (!referenced) {
      removeComposerImageFromDraft(imageId);
      return;
    }
    const confirmation = requestConfirmDialog(
      `Remove ${image?.name ?? "this image"} from the message?\nIt is referenced in your text; removing it also removes every reference.`,
      { variant: "destructive" },
    );
    if (!confirmation) {
      removeComposerImageFromDraft(imageId);
      return;
    }
    void confirmation.then((confirmed) => {
      if (confirmed) removeComposerImageFromDraft(imageId);
    });
  };

  // ------------------------------------------------------------------
  // Callbacks: paste / drag
  // ------------------------------------------------------------------
  const foldPastedText = (
    plainText: string,
    bypassAutoAttachment: boolean,
    selectionOverride?: { start: number; end: number },
  ): boolean => {
    const questionCanAttach =
      pendingUserInputs.length === 0 ||
      (supportsQuestionAttachments &&
        activePendingProgress?.activeQuestion?.allowCustomAnswer !== false &&
        !activePendingIsResponding);
    const hasAttachmentSlot = countReservedAttachments() < attachmentLimit;
    const selection = selectionOverride ?? composerEditorRef.current?.readSelectionRange();
    const wouldExceedInputLimit = wouldTextPasteExceedLimit({
      valueLength: promptRef.current.length,
      selection: selection ?? { start: 0, end: 0 },
      textLength: plainText.length,
      maxLength: PROVIDER_SEND_TURN_MAX_INPUT_CHARS,
    });
    const shouldFold =
      pastedTextDisposition({
        text: plainText,
        bypassAutoAttachment,
        wouldExceedInputLimit,
        canAttach: true,
      }) === "attachment";
    if (!shouldFold) {
      return false;
    }

    const canStageAttachment =
      Boolean(activeThreadId) &&
      !isRevertingCheckpointRef.current &&
      questionCanAttach &&
      hasAttachmentSlot;
    if (!canStageAttachment || fileStagingLimit === null) {
      if (!wouldExceedInputLimit) {
        return false;
      }
      toastManager.add({
        type: "error",
        title: "Pasted text is too large for this message",
        description: "Remove some text or an attachment, then paste again.",
        data: { hideCopyButton: true },
      });
      return true;
    }

    if (pastedTextFileNamesRef.current.targetKey !== attachmentTargetKey) {
      pastedTextFileNamesRef.current = { targetKey: attachmentTargetKey, names: new Set() };
    }
    const reservedNames = pastedTextFileNamesRef.current.names;
    for (const file of composerFilesRef.current) reservedNames.add(file.name);
    const foldedFileName = nextPastedTextFileName([...reservedNames]);
    reservedNames.add(foldedFileName);
    const foldedFile = new File([plainText], foldedFileName, {
      type: "text/plain;charset=utf-8",
    });
    if (foldedFile.size > fileStagingLimit) {
      reservedNames.delete(foldedFileName);
      if (!wouldExceedInputLimit) return false;
      toastManager.add({
        type: "error",
        title: "Pasted text is too large to attach",
        description: "Reduce the clipboard contents or save a smaller excerpt as a file.",
        data: { hideCopyButton: true },
      });
      return true;
    }

    void addComposerAttachments([foldedFile], {
      source: { _tag: "pasted-text" },
      ...(selection ? { selection } : {}),
    });
    return true;
  };

  const onComposerPaste = (event: React.ClipboardEvent<HTMLElement>) => {
    const files = Array.from(event.clipboardData.files);
    const plainText = event.clipboardData.getData("text/plain");
    const bypassAutoAttachment = Date.now() <= pasteAsTextShortcutUntilRef.current;
    pasteAsTextShortcutUntilRef.current = 0;
    // Claimable pastes go through even when agent questions are pending or the
    // composer is at its attachment limit: `addComposerAttachments` surfaces
    // those as a toast and a thread error. An early return here would swallow
    // the paste with no feedback.
    if (
      files.length > 0 &&
      activeThreadId &&
      shouldHandleComposerAttachmentPaste({ files, plainText })
    ) {
      event.preventDefault();
      event.stopPropagation();
      // A pasted screenshot is a picture for this message; a pasted file asks.
      void attachWithIngestChoice(
        { kind: "files", files },
        files.some((file) => !file.type.startsWith("image/")),
      );
      return;
    }

    // Copied T3 chips need the structured importer to bring their records and files along.
    if ((readPastedComposerContext(event.clipboardData)?.records.length ?? 0) > 0) return;
    if (!foldPastedText(plainText, bypassAutoAttachment)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const insertComposerText = useCallback(
    (
      text: string,
      position: "cursor" | "end",
      options?: {
        ensureLeadingBoundary?: boolean;
        citationCommentAnchor?: AssistantCitationSourceAnchor;
        clipboardData?: DataTransfer;
      },
    ): boolean => {
      if (
        text.length === 0 ||
        isConnecting ||
        isComposerApprovalState ||
        pendingUserInputs.length > 0 ||
        projectSelectionRequired ||
        (options?.citationCommentAnchor && !composerEditorRef.current)
      ) {
        return false;
      }
      if (options?.clipboardData) {
        text = importPastedComposerText(options.clipboardData, importContextFragment);
      }
      const prompt = promptRef.current;
      const cursor = position === "cursor" ? readComposerSnapshot().expandedCursor : prompt.length;
      const needsLeadingSpace =
        (options?.ensureLeadingBoundary ?? false) &&
        cursor > 0 &&
        !/\s/.test(prompt[cursor - 1] ?? "");
      const rangeEnd = extendReplacementRangeForTrailingSpace(prompt, cursor, text);
      return applyPromptReplacement(
        cursor,
        rangeEnd,
        needsLeadingSpace ? ` ${text}` : text,
        options?.citationCommentAnchor
          ? {
              citationComment: {
                start: cursor + (needsLeadingSpace ? 1 : 0),
                sourceAnchor: options.citationCommentAnchor,
              },
              focusEditorAfterReplace: false,
            }
          : undefined,
      );
    },
    [
      applyPromptReplacement,
      isComposerApprovalState,
      isConnecting,
      pendingUserInputs.length,
      projectSelectionRequired,
      promptRef,
      readComposerSnapshot,
      importContextFragment,
    ],
  );

  const insertComposerTextAtEnd = useCallback<ChatComposerHandle["insertTextAtEnd"]>(
    (text, options) => {
      const inserted = insertComposerText(text, "end", options);
      if (inserted && isComposerCollapsedMobile) {
        // The expanded editor is hidden at phone widths, so its scheduled
        // focus cannot expand the composer by itself. Reveal it before the
        // focus frame runs to preserve type-to-focus and external inserts.
        expandMobileComposer();
      }
      return inserted;
    },
    [expandMobileComposer, insertComposerText, isComposerCollapsedMobile],
  );

  // Context produced by other panels (diff comments, preview picks) asks the store to place
  // its chip; while this composer is mounted for the draft, that means the caret.
  const insertContextReferencesAtCaret = useCallback(
    (references: ReadonlyArray<ComposerContextReference>): boolean =>
      insertComposerText(`${references.map(formatInlineContextReference).join(" ")} `, "cursor", {
        ensureLeadingBoundary: true,
      }),
    [insertComposerText],
  );
  const setContextInsertionHandler = useComposerDraftStore(
    (store) => store.setContextInsertionHandler,
  );
  useEffect(() => {
    return setContextInsertionHandler(composerDraftTarget, insertContextReferencesAtCaret);
  }, [composerDraftTarget, insertContextReferencesAtCaret, setContextInsertionHandler]);

  // File-tree drags land as mentions and operating-system drags as
  // attachments. Handled in the capture phase so the editor never sees the
  // drop; the load-bearing rules (native stop, drop effect, no eager focus)
  // live in makeComposerDropHandlers.
  const composerDropHandlers = makeComposerDropHandlers<DataTransfer>({
    attachDroppedFiles: (transfer) => {
      if (attachingBlockedByPlanQuestions()) return;
      void composerAttachmentsFromDrop(transfer, randomUUID)
        .then(async (attachments) => {
          // Only a folder dropped with a live handle can be staged; other
          // browsers give its files one by one, which a draft cannot hold.
          const unstageableFolders = attachments.filter(
            (attachment) =>
              attachment.source === "browser-folder" && attachment.directoryHandle === undefined,
          );
          if (unstageableFolders.length > 0) {
            toastManager.add({
              type: "error",
              title: `Couldn't attach ${unstageableFolders.map((folder) => `"${folder.name}"`).join(", ")}`,
              description:
                "This browser can't attach a dropped folder. Use Attach → Folder instead.",
            });
          }
          await attachWithIngestChoice({ kind: "paths", attachments });
        })
        .catch(() => {
          toastManager.add({ type: "error", title: "Could not read the dropped files." });
        });
    },
    insertMentionAtEnd: (text) => insertComposerTextAtEnd(text, { ensureLeadingBoundary: true }),
    setDragActive: setIsDragOverComposer,
    onInsertRejected: () => {
      toastManager.add({
        type: "error",
        title: "Unable to add to chat",
        description: "The composer is busy; try again once it is ready.",
      });
    },
  });

  const onComposerMentionDragLeaveCapture = (event: React.DragEvent<HTMLFormElement>) => {
    if (!dataTransferIsComposerDrop(event.dataTransfer.types)) return;
    event.stopPropagation();
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    setIsDragOverComposer(false);
  };

  // A cancelled drag (Escape) can end without a dragleave on the hovered
  // target, which would leave the drop highlight stuck. dragend always fires
  // on the in-page drag source and bubbles to window, so it is the reset of
  // last resort while the highlight is up.
  useEffect(() => {
    if (!isDragOverComposer) return;
    const onWindowDragEnd = () => {
      setIsDragOverComposer(false);
    };
    window.addEventListener("dragend", onWindowDragEnd);
    return () => window.removeEventListener("dragend", onWindowDragEnd);
  }, [isDragOverComposer]);
  const handleInterruptPrimaryAction = useCallback(() => {
    void onInterrupt();
  }, [onInterrupt]);
  const handleImplementPlanInNewThreadPrimaryAction = useCallback(() => {
    void onImplementPlanInNewThread();
  }, [onImplementPlanInNewThread]);
  // The phone composer collapses when the editor loses focus. Desktop only
  // rests on a timeline scroll, so losing focus there changes nothing.
  const scheduleComposerCollapseCheck = useCallback(() => {
    if (!isMobileViewport || mobileComposerExpandInFlightRef.current) {
      return;
    }
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
    }
    composerBlurFrameRef.current = window.requestAnimationFrame(() => {
      composerBlurFrameRef.current = null;
      if (mobileComposerExpandInFlightRef.current) {
        return;
      }
      const composerSurface = composerSurfaceRef.current;
      const composerForm = composerFormRef.current;
      const activeElement = document.activeElement;
      if (isInsideRestingComposerControlScope(activeElement)) {
        return;
      }
      if (
        activeElement instanceof Node &&
        ((composerSurface && composerSurface.contains(activeElement)) ||
          (composerForm && composerForm.contains(activeElement)))
      ) {
        return;
      }
      setIsComposerFocused(false);
    });
  }, [isMobileViewport, setIsComposerFocused]);

  useEffect(() => {
    return () => {
      if (composerBlurFrameRef.current !== null) {
        window.cancelAnimationFrame(composerBlurFrameRef.current);
      }
      if (mobileComposerExpandFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileComposerExpandFrameRef.current);
      }
      if (mobileComposerExpandReleaseFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileComposerExpandReleaseFrameRef.current);
      }
    };
  }, []);

  // ------------------------------------------------------------------
  // Imperative handle
  // ------------------------------------------------------------------
  const openModelPicker = useCallback(() => {
    if (composerControlsHidden) {
      if (composerBlurFrameRef.current !== null) {
        window.cancelAnimationFrame(composerBlurFrameRef.current);
        composerBlurFrameRef.current = null;
      }
      setIsComposerScrollCollapsed(false);
      setIsComposerFocused(true);
    }
    setIsComposerModelPickerOpen(true);
  }, [composerControlsHidden, setIsComposerFocused, setIsComposerScrollCollapsed]);

  // ------------------------------------------------------------------
  // Actions (ADR 0142)
  // ------------------------------------------------------------------
  /**
   * Hold a different Action. Design System targets never cross from one Design
   * Operation to another: a system picked for Add template is not one picked
   * for Rename, and a chip nobody chose for this operation must not ride it.
   */
  const changeComposerAction = useCallback(
    (next: ThreadActionSelection | null) => {
      const nextDesignOperation = designOperationOf(next);
      if (designOperation !== null && nextDesignOperation !== designOperation) {
        setComposerDraftDesignSystemMentions(composerDraftTarget, []);
      }
      onComposerActionChange(next);
    },
    [
      composerDraftTarget,
      designOperation,
      onComposerActionChange,
      setComposerDraftDesignSystemMentions,
    ],
  );
  // An Action that runs at once, or once its dialog picked what it works on,
  // sends after the chip and what it collected have rendered: the send reads
  // them back through the composer handle.
  const [pendingActionSend, setPendingActionSend] = useState<string | null>(null);
  const pickComposerAction: (selection: ThreadActionSelection) => void = useCallback(
    (selection: ThreadActionSelection) => {
      const next = initialActionSelection(selection);
      changeComposerAction(next);
      const run = findAction(next.id)?.run ?? "chip";
      if (run === "immediate") {
        setPendingActionSend(next.id);
        return;
      }
      if (run === "dialog") {
        if (next.id === "brain-delete") setIsBrainDeleteRawsDialogOpen(true);
        else if (next.id === "design-delete") setIsDesignDeleteTargetsDialogOpen(true);
        else if (next.id === "design-rename") setIsDesignTargetPickerOpen(true);
        return;
      }
      scheduleComposerFocus();
    },
    [changeComposerAction, scheduleComposerFocus],
  );
  pickComposerActionRef.current = pickComposerAction;
  const changeComposerActionVariant = useCallback(
    (variant: string) => {
      if (composerAction === null) return;
      changeComposerAction({ id: composerAction.id, variant });
    },
    [changeComposerAction, composerAction],
  );
  const removeComposerAction = useCallback(() => {
    changeComposerAction(null);
    scheduleComposerFocus();
  }, [changeComposerAction, scheduleComposerFocus]);
  const submitComposerRef = useRef(submitComposer);
  submitComposerRef.current = submitComposer;
  useEffect(() => {
    if (pendingActionSend === null) return;
    setPendingActionSend(null);
    // The user took the chip off, or picked another, before the send landed.
    if (composerAction?.id !== pendingActionSend) return;
    submitComposerRef.current();
  }, [composerAction?.id, pendingActionSend]);

  // The Next Action, offered while the composer is empty and holds no chip.
  const nextActionSuggestion =
    nextActionId === null ||
    composerAction !== null ||
    activePendingProgress !== null ||
    isComposerApprovalState ||
    prompt.trim().length > 0 ||
    composerImages.length + composerFiles.length > 0
      ? null
      : (composerActionEntries.find((entry) => entry.selection.id === nextActionId) ?? null);

  // Attaching files or folders outside Ingest asks whether they are for the Brain.
  const [pendingIngestChoice, setPendingIngestChoice] = useState<PendingAttach | null>(null);
  const [queuedIngestAttach, setQueuedIngestAttach] = useState<PendingAttach | null>(null);
  const runPendingAttach = async (pending: PendingAttach): Promise<boolean> => {
    if (pending.kind === "paths") {
      await addPickedPathAttachments(pending.attachments);
      return true;
    }
    return addComposerAttachments(pending.files);
  };
  const attachWithIngestChoice = (
    pending: PendingAttach,
    attachesFilesOrFolders = true,
  ): Promise<boolean> => {
    // Native pickers return an empty selection when cancelled.
    if ((pending.kind === "paths" ? pending.attachments : pending.files).length === 0) {
      return Promise.resolve(false);
    }
    const decision = decideAttachIngest({
      action: composerAction,
      alwaysIngest: alwaysIngestAttachments,
      // A provider question takes its attachments as answers, never as sources.
      attachesFilesOrFolders: attachesFilesOrFolders && pendingUserInputs.length === 0,
    });
    if (decision === "attach") return runPendingAttach(pending);
    if (decision === "ingest") {
      changeComposerAction(INGEST_ACTION_SELECTION);
      setQueuedIngestAttach(pending);
    } else {
      setPendingIngestChoice(pending);
    }
    return Promise.resolve(true);
  };
  // Ingest takes its sources uncapped and as-is, which the attach path reads
  // from the chip; so the material lands only once the Ingest chip rendered.
  const runPendingAttachRef = useRef(runPendingAttach);
  runPendingAttachRef.current = runPendingAttach;
  useEffect(() => {
    if (queuedIngestAttach === null || brainAction !== "ingest") return;
    setQueuedIngestAttach(null);
    void runPendingAttachRef.current(queuedIngestAttach);
  }, [brainAction, queuedIngestAttach]);

  useImperativeHandle(
    composerRef,
    () => ({
      focusAtEnd: () => {
        composerEditorRef.current?.focusAtEnd();
      },
      focusAt: (cursor: number) => {
        composerEditorRef.current?.focusAt(cursor);
      },
      restoreAfterTimelineReachedEnd,
      collapseForTimelineScrollKey: (key) => {
        const scrollNode = getTimelineScrollableNode();
        if (
          composerScrollCollapseEligibleRef.current &&
          scrollNode &&
          shouldCollapseComposerForScrollKey({
            key,
            scrollTop: scrollNode.scrollTop,
            scrollHeight: scrollNode.scrollHeight,
            clientHeight: scrollNode.clientHeight,
            isAtLogicalEnd: isTimelineAtLogicalEnd(),
          })
        ) {
          setIsComposerScrollCollapsed(true);
        }
      },
      hasPendingAttachments: () =>
        (pendingImageCompressionsRef.current.get(attachmentTargetKey) ?? 0) > 0,
      insertTextAtEnd: insertComposerTextAtEnd,
      pasteTextAtEnd: (text: string, options) => {
        const bypassAutoAttachment =
          options?.bypassAutoAttachment === true ||
          Date.now() <= pasteAsTextShortcutUntilRef.current;
        pasteAsTextShortcutUntilRef.current = 0;
        const promptLength = promptRef.current.length;
        if (
          !foldPastedText(text, bypassAutoAttachment, {
            start: promptLength,
            end: promptLength,
          })
        ) {
          return false;
        }
        focusComposer();
        return true;
      },
      citeAssistantText: (citation, sourceAnchor) =>
        insertComposerText(
          formatAssistantCitationForComposer(citation, citation.comment),
          "cursor",
          { ensureLeadingBoundary: true, citationCommentAnchor: sourceAnchor },
        ),
      openModelPicker,
      toggleModelPicker: () => {
        if (isComposerModelPickerOpen) {
          setIsComposerModelPickerOpen(false);
        } else {
          openModelPicker();
        }
      },
      openControl: (command) => {
        if (composerBlurFrameRef.current !== null) {
          window.cancelAnimationFrame(composerBlurFrameRef.current);
          composerBlurFrameRef.current = null;
        }
        flushSync(() => {
          setIsComposerScrollCollapsed(false);
          setIsComposerFocused(true);
        });
        const shell = composerFormRef.current?.closest('[data-slot="composer-shell"]');
        const trigger = Array.from(
          shell?.querySelectorAll<HTMLButtonElement>(
            `button[data-composer-shortcut~="${command}"]:not(:disabled)`,
          ) ?? [],
        ).find(
          (element) =>
            !element.closest("[inert]") && element.checkVisibility({ visibilityProperty: true }),
        );
        if (!trigger) return;
        trigger.focus({ preventScroll: true });
        trigger.click();
      },
      compactContext: compactThreadContext,
      isModelPickerOpen: () => isComposerModelPickerOpen,
      readSnapshot: () => {
        return readComposerSnapshot();
      },
      resetCursorState: (options?: {
        cursor?: number;
        prompt?: string;
        detectTrigger?: boolean;
      }) => {
        const promptForState = options?.prompt ?? promptRef.current;
        const cursor = clampCollapsedComposerCursor(promptForState, options?.cursor ?? 0);
        setComposerHighlightedItemId(null);
        setComposerCursor(cursor);
        setComposerTrigger(
          options?.detectTrigger
            ? detectComposerTrigger(
                promptForState,
                expandCollapsedComposerCursor(promptForState, cursor),
              )
            : null,
        );
      },
      addTerminalContext: (selection: TerminalContextSelection) => {
        if (!activeThreadId || isChoiceOnlyPendingQuestion) return;
        const snapshot = readComposerSnapshot();
        const context = {
          id: randomUUID(),
          threadId: activeThreadId,
          createdAt: new Date().toISOString(),
          ...selection,
        };
        const insertion = insertInlineContextReference(
          snapshot.value,
          snapshot.expandedCursor,
          terminalContextReference(context),
        );
        const nextCollapsedCursor = collapseExpandedComposerCursor(
          insertion.prompt,
          insertion.cursor,
        );
        const inserted = insertComposerDraftTerminalContext(
          composerDraftTarget,
          insertion.prompt,
          context,
          composerTerminalContexts.length,
        );
        if (!inserted) return;
        promptRef.current = insertion.prompt;
        setComposerCursor(nextCollapsedCursor);
        setComposerTrigger(detectComposerTrigger(insertion.prompt, insertion.cursor));
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAt(nextCollapsedCursor);
        });
      },
      clearBrainComposerCollection: () => {
        brainComposerCollectionRef.current = EMPTY_BRAIN_COMPOSER_COLLECTION;
        setBrainTextSources([]);
        setBrainDeleteTargets([]);
      },
      replaceDesignEvidenceUrls: (urls: ReadonlyArray<string>) => {
        setDesignEvidenceUrls(urls.map((url) => ({ id: randomUUID(), url })));
      },
      replaceDesignDeleteTargets: (targets: ReadonlyArray<DesignDeleteTarget>) => {
        setDesignDeleteTargets(targets);
      },
      getDesignCollection: () => designComposerCollectionRef.current,
      getSendContext: () => ({
        // Lint and Delete take no typed input and no attachments: the composer
        // shows neither, so the turn must carry neither. Their whole
        // submission is the operation the rail picked.
        prompt: brainComposerCarriesInputRef.current ? promptRef.current : "",
        images: brainComposerCarriesInputRef.current ? composerImagesRef.current : EMPTY_IMAGES,
        files: brainComposerCarriesInputRef.current
          ? composerFilesRef.current
          : EMPTY_COMPOSER_FILES,
        // Images first, because the send path builds its attachments
        // images-then-files, and the index is what the agent is told.
        brainCollection: brainComposerCollectionRef.current,
        // The Design Systems the send targets, handed over rather than read
        // from the draft by the caller: subscribing ChatView to them would
        // re-render the timeline on every keystroke.
        designSystemMentions: composerDesignSystemMentions,
        terminalContexts: composerTerminalContextsRef.current,
        previewAnnotations: composerPreviewAnnotations,
        reviewComments: composerReviewComments,
        selectedPromptEffort,
        selectedModelOptionsForDispatch,
        selectedModelSelection,
        providerAvailable: !noProviderAvailable,
        selectedProvider,
        selectedModel,
        selectedProviderModels,
        interactionMode,
        interactionModeEnabled: planModeUiEnabled,
      }),
      validateProviderInput: (providerInput: string) => {
        const validationMessage = getComposerSubmissionValidationMessage({
          prompt: promptRef.current,
          providerInput,
          submissionTarget: "provider-turn",
        });
        providerInputRejectedRef.current = validationMessage !== null;
        setProviderInputSubmissionError(validationMessage);
        return validationMessage === null;
      },
    }),
    [
      activeThreadId,
      addComposerAttachments,
      foldPastedText,
      composerDraftTarget,
      composerCursor,
      composerTerminalContexts,
      insertComposerDraftTerminalContext,
      insertComposerText,
      insertComposerTextAtEnd,
      promptRef,
      composerImagesRef,
      composerFilesRef,
      composerTerminalContextsRef,
      composerPreviewAnnotations,
      composerReviewComments,
      focusComposer,
      environmentId,
      primaryEnvironmentId,
      isConnecting,
      isComposerApprovalState,
      isChoiceOnlyPendingQuestion,
      pendingUserInputs.length,
      projectSelectionRequired,
      applyPromptReplacement,
      isComposerModelPickerOpen,
      openModelPicker,
      readComposerSnapshot,
      selectedModel,
      selectedModelOptionsForDispatch,
      selectedModelSelection,
      noProviderAvailable,
      selectedPromptEffort,
      selectedProvider,
      selectedProviderModels,
      interactionMode,
      planModeUiEnabled,
      compactThreadContext,
      restoreAfterTimelineReachedEnd,
      getTimelineScrollableNode,
      isTimelineAtLogicalEnd,
      setIsComposerScrollCollapsed,
    ],
  );

  // Render
  // ------------------------------------------------------------------
  return (
    <form
      ref={composerFormRef}
      onSubmit={submitComposer}
      onPointerDownCapture={(event) => {
        const target = event.target;
        if (isInsideRestingComposerControlScope(target)) return;
        if (isInsideCollapsedComposerControls(target)) return;
        if (!(target instanceof Element)) return;
        const isInteractive = Boolean(
          target.closest('button, a, input, select, [role="button"], [role="menuitem"]'),
        );
        if (isInteractive) return;

        setIsComposerScrollCollapsed(false);
        if (isComposerResting && !target.closest('[data-testid="composer-editor"]')) {
          // Clicking resting-surface padding would otherwise blur the editor.
          // Treat that padding like the editor without stealing native caret
          // placement from text.
          event.preventDefault();
          setIsComposerFocused(true);
          scheduleComposerFocus();
        }
      }}
      onFocusCapture={(event) => {
        const activeElement = event.target;
        if (composerControlsInStrip && isInsideRestingComposerControlScope(activeElement)) {
          return;
        }
        if (isInsideCollapsedComposerControls(activeElement)) {
          return;
        }
        // Focus returning from another window or tab lands on the element
        // that already held it, which is not a request to expand a
        // scroll-collapsed composer.
        if (!windowRefocusInFlightRef.current) {
          setIsComposerScrollCollapsed(false);
        }
        if (composerBlurFrameRef.current !== null) {
          window.cancelAnimationFrame(composerBlurFrameRef.current);
          composerBlurFrameRef.current = null;
        }
        setIsComposerFocused(true);
        onComposerFocus?.();
      }}
      onBlurCapture={() => {
        scheduleComposerCollapseCheck();
      }}
      onDragEnterCapture={(event) => {
        if (isInsideRestingComposerControlScope(event.target)) return;
        composerDropHandlers.onDragEnter(event);
      }}
      onDragOverCapture={(event) => {
        if (isInsideRestingComposerControlScope(event.target)) return;
        composerDropHandlers.onDragOver(event);
      }}
      onDragLeaveCapture={(event) => {
        if (isInsideRestingComposerControlScope(event.target)) return;
        onComposerMentionDragLeaveCapture(event);
      }}
      onDropCapture={(event) => {
        if (isInsideRestingComposerControlScope(event.target)) return;
        composerDropHandlers.onDrop(event);
      }}
      className="mx-auto w-full min-w-0 max-w-3xl"
      data-chat-composer-form="true"
    >
      {composerControlsInStrip && restingControlsHost
        ? createPortal(
            <div
              ref={restingComposerControlsRef}
              data-chat-composer-resting-controls="true"
              aria-hidden={restingControlsVisible ? undefined : true}
              inert={restingControlsVisible ? undefined : true}
              className={cn(
                "relative flex w-max min-w-0 max-w-full items-center gap-1 font-normal text-muted-foreground/70 [&_button]:text-xs!",
                !restingControlsVisible && "invisible",
              )}
            >
              {composerControls}
            </div>,
            restingControlsHost,
          )
        : null}
      <ComposerBanner.Dock>
        <ComposerBanner.Column>
          <ComposerBannerStack
            key={activeThreadId}
            className="relative z-0"
            items={bannerStackItems}
          />
          {!activityStackItem && (props.threadSyncPhase || inlineTasksBadge) ? (
            <ComposerBanner.Attachment>
              <ComposerBanner.Root data-chat-composer-activity-strip="true">
                {props.threadSyncPhase ? (
                  <ComposerActivityRow phase={props.threadSyncPhase} />
                ) : (
                  inlineTasksBadge
                )}
              </ComposerBanner.Root>
            </ComposerBanner.Attachment>
          ) : null}
          {showComposerTopDrawer && (!isTasksDrawerOpen || hasBlockingComposerTopDrawer) ? (
            <ComposerBanner.Attachment>
              <ComposerBanner.Root
                data-chat-composer-top-drawer="true"
                variant={activePendingApproval ? "warning" : "info"}
                density={activePendingApproval ? "spacious" : "default"}
              >
                {activePendingApproval ? (
                  <ComposerBanner.Row
                    layout="approval"
                    data-chat-composer-collapsed-controls="true"
                  >
                    <ComposerBanner.Icon>
                      <ShieldIcon />
                    </ComposerBanner.Icon>
                    <ComposerBanner.Content>
                      <ComposerPendingApprovalPanel
                        approval={activePendingApproval}
                        pendingCount={pendingApprovals.length}
                      />
                    </ComposerBanner.Content>
                    <ComposerBanner.Actions>
                      <ComposerPendingApprovalActions
                        requestId={activePendingApproval.requestId}
                        isResponding={respondingRequestIds.includes(
                          activePendingApproval.requestId,
                        )}
                        options={
                          activePendingApproval.options.length > 0
                            ? activePendingApproval.options
                            : undefined
                        }
                        onRespondToApproval={onRespondToApproval}
                      />
                    </ComposerBanner.Actions>
                  </ComposerBanner.Row>
                ) : !isComposerCollapsedMobile && pendingUserInputs.length > 0 ? (
                  <ComposerPendingUserInputPanel
                    pendingUserInputs={pendingUserInputs}
                    respondingRequestIds={
                      activePendingIsResponding && activePendingUserInput
                        ? [...respondingRequestIds, activePendingUserInput.requestId]
                        : respondingRequestIds
                    }
                    answers={activePendingDraftAnswers}
                    questionIndex={activePendingQuestionIndex}
                    onToggleOption={onSelectActivePendingUserInputOption}
                    onAdvance={onAdvanceActivePendingUserInput}
                    onDismiss={onDismissActivePendingUserInput}
                  />
                ) : !isComposerCollapsedMobile && showPlanFollowUpPrompt && activeProposedPlan ? (
                  <ComposerPlanFollowUpBanner
                    key={activeProposedPlan.id}
                    planTitle={proposedPlanTitle(activeProposedPlan.planMarkdown) ?? null}
                  />
                ) : isComposerCollapsedMobile && pendingUserInputs.length > 0 ? (
                  <div data-chat-composer-collapsed-controls="true">
                    <ComposerPendingUserInputPanel
                      pendingUserInputs={pendingUserInputs}
                      respondingRequestIds={
                        activePendingIsResponding && activePendingUserInput
                          ? [...respondingRequestIds, activePendingUserInput.requestId]
                          : respondingRequestIds
                      }
                      answers={activePendingDraftAnswers}
                      questionIndex={activePendingQuestionIndex}
                      onToggleOption={onSelectActivePendingUserInputOption}
                      onAdvance={onAdvanceActivePendingUserInput}
                      onDismiss={onDismissActivePendingUserInput}
                    />
                    {!isChoiceOnlyPendingQuestion ||
                    activePendingProgress?.activeQuestion?.multiSelect ? (
                      <ComposerBanner.Body>
                        <div
                          data-chat-composer-mobile-pending-compact="true"
                          className={cn(
                            "flex min-w-0 items-center gap-2 rounded-lg border border-border/55 bg-background/55 p-1.5 pl-3 transition-colors hover:bg-background/80",
                            !activePendingProgress?.activeQuestion?.multiSelect && "p-0",
                          )}
                        >
                          {!isChoiceOnlyPendingQuestion ? (
                            <button
                              type="button"
                              className={cn(
                                "min-w-0 flex-1 truncate bg-transparent py-1.5 text-left text-sm",
                                activePendingProgress?.customAnswer
                                  ? "text-foreground"
                                  : "text-placeholder",
                                !activePendingProgress?.activeQuestion?.multiSelect && "px-3 py-2",
                              )}
                              onPointerDown={(event) => event.preventDefault()}
                              onClick={expandMobileComposer}
                              aria-label="Write custom answer"
                            >
                              {activePendingProgress?.customAnswer || "Write custom answer"}
                            </button>
                          ) : null}
                          {activePendingProgress?.activeQuestion?.multiSelect ? (
                            <ComposerPrimaryActions
                              compact
                              pendingAction={pendingPrimaryAction}
                              isRunning={false}
                              showPlanFollowUpPrompt={false}
                              promptHasText={false}
                              isSendBusy={isSendBusy}
                              sendDisabledReason={sendDisabledReason}
                              isConnecting={isConnecting}
                              isEnvironmentUnavailable={
                                environmentUnavailable !== null ||
                                noProviderAvailable ||
                                projectSelectionRequired
                              }
                              isPreparingWorktree={false}
                              hasSendableContent={false}
                              preserveComposerFocusOnPointerDown
                              onPreviousPendingQuestion={onPreviousActivePendingUserInputQuestion}
                              onInterrupt={handleInterruptPrimaryAction}
                              onImplementPlanInNewThread={
                                handleImplementPlanInNewThreadPrimaryAction
                              }
                            />
                          ) : null}
                        </div>
                      </ComposerBanner.Body>
                    ) : null}
                  </div>
                ) : null}
              </ComposerBanner.Root>
            </ComposerBanner.Attachment>
          ) : null}
          {!activityStackItem &&
          isTasksDrawerOpen &&
          !hasBlockingComposerTopDrawer &&
          activeTasksProgress &&
          activeTaskSteps ? (
            <ComposerTasksDrawer
              onCollapse={toggleTasksDrawer}
              progress={activeTasksProgress}
              steps={activeTaskSteps}
            />
          ) : null}
          {showTasksTab ? (
            <ComposerBanner.Attachment>
              <ComposerTasksBadge
                expanded={false}
                onToggle={toggleTasksDrawer}
                progress={activeTasksProgress}
                steps={activeTaskSteps}
              />
            </ComposerBanner.Attachment>
          ) : null}
        </ComposerBanner.Column>
        {!isComposerApprovalState ? (
          <ComposerStashBadge
            count={stashQueue.length}
            menuOpen={isStashMenuOpen}
            pulseKey={stashPulse.key}
            pulsing={stashPulse.active}
            onToggleMenu={toggleStashMenu}
          />
        ) : null}
      </ComposerBanner.Dock>
      <div className="relative">
        {brainAction === null ? null : (
          <BrainAddTextDialog
            open={isBrainAddTextDialogOpen}
            onOpenChange={setIsBrainAddTextDialogOpen}
            onAdd={(text) =>
              setBrainTextSources((current) => [...current, { id: randomUUID(), text }])
            }
          />
        )}
        {gitCwd === null ? null : (
          <ComposerEnvironmentFilesDialog
            open={isEnvironmentFilesDialogOpen}
            environmentId={environmentId}
            workspaceRoot={gitCwd}
            onOpenChange={setIsEnvironmentFilesDialogOpen}
            onAttach={(attachments) => void attachWithIngestChoice({ kind: "paths", attachments })}
          />
        )}
        {projectRoot === null ? null : (
          <BrainDeleteRawsDialog
            open={isBrainDeleteRawsDialogOpen}
            environmentId={environmentId}
            projectRoot={projectRoot}
            selected={brainDeleteTargets}
            onOpenChange={setIsBrainDeleteRawsDialogOpen}
            onConfirm={(targets) => {
              setBrainDeleteTargets(targets);
              // Picking the sources is the confirmation; the Action then sends.
              if (targets.length > 0 && composerAction?.id === "brain-delete") {
                setPendingActionSend("brain-delete");
              }
            }}
          />
        )}
        <AttachIngestDialog
          open={pendingIngestChoice !== null}
          count={pendingIngestChoice === null ? 0 : pendingAttachCount(pendingIngestChoice)}
          onCancel={() => setPendingIngestChoice(null)}
          onChoose={({ ingest, alwaysIngest }) => {
            const pending = pendingIngestChoice;
            setPendingIngestChoice(null);
            if (pending === null) return;
            if (alwaysIngest) onEnableAlwaysIngestAttachments();
            if (!ingest) {
              void runPendingAttach(pending);
              return;
            }
            changeComposerAction(INGEST_ACTION_SELECTION);
            setQueuedIngestAttach(pending);
          }}
        />
        <DesignAddEvidenceUrlDialog
          open={isDesignAddUrlDialogOpen}
          onOpenChange={setIsDesignAddUrlDialogOpen}
          onAdd={(url) =>
            setDesignEvidenceUrls((current) => [...current, { id: randomUUID(), url }])
          }
        />
        {projectRoot === null ? null : (
          <DesignDeleteTargetsDialog
            open={isDesignDeleteTargetsDialogOpen}
            environmentId={environmentId}
            projectRoot={projectRoot}
            selected={designDeleteTargets}
            onOpenChange={setIsDesignDeleteTargetsDialogOpen}
            onConfirm={(targets) => {
              setDesignDeleteTargets(targets);
              // Picking what to remove is the confirmation; the host then applies it.
              if (targets.length > 0 && composerAction?.id === "design-delete") {
                setPendingActionSend("design-delete");
              }
            }}
          />
        )}
        <ComposerSurface.Main
          ref={composerMainSurfaceRef}
          data-inline-resting-controls={restingControlsHost === null ? "true" : undefined}
          className={cn(composerProviderState.composerFrameClassName)}
        >
          <div
            ref={composerSurfaceRef}
            data-chat-composer-surface="true"
            data-chat-composer-mobile-collapsed={isComposerCollapsedMobile ? "true" : "false"}
            className={cn(
              "rounded-[20px] transition-[background-color] duration-200",
              isDragOverComposer ? "bg-accent/45 ring-1 ring-primary/70" : null,
              projectSelectionRequired ? "opacity-75" : null,
              composerProviderState.composerSurfaceClassName,
            )}
          >
            {composerActionChipModel !== null && !showCollapsedMobilePromptRow ? (
              <ComposerActionChip
                chip={composerActionChipModel}
                onVariantChange={changeComposerActionVariant}
                onRemove={removeComposerAction}
              />
            ) : nextActionSuggestion !== null && !showCollapsedMobilePromptRow ? (
              <NextActionSuggestion entry={nextActionSuggestion} onPick={pickComposerAction} />
            ) : null}
            {showCollapsedMobilePromptRow ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                {collapsedComposerActionMarker === null ? null : (
                  <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {collapsedComposerActionMarker}
                  </span>
                )}
                <button
                  type="button"
                  data-chat-composer-transition-prompt="true"
                  className={cn(
                    "min-w-0 flex-1 truncate bg-transparent p-0 text-left text-[14px] focus:outline-none",
                    (activePendingProgress ? activePendingProgress.customAnswer : prompt.trim())
                      ? "text-foreground"
                      : "text-placeholder",
                  )}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={isChoiceOnlyPendingQuestion ? undefined : expandMobileComposer}
                  disabled={isChoiceOnlyPendingQuestion}
                  aria-label="Expand composer"
                >
                  {activePendingProgress
                    ? isChoiceOnlyPendingQuestion
                      ? "Choose an option above"
                      : activePendingProgress.customAnswer ||
                        "Type your own answer, or leave this blank to use the selected option"
                    : prompt.trim() ||
                      (showProviderUnavailable
                        ? "Enable a provider in Settings"
                        : "Ask anything...")}
                </button>
                {collapsedComposerImagePreviews}
                <button
                  type="button"
                  data-chat-composer-transition-actions="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-message-action text-message-action-foreground hover:bg-message-action-hover disabled:opacity-30"
                  disabled={collapsedComposerPrimaryActionDisabled}
                  aria-label={collapsedComposerPrimaryActionLabel}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    submitComposer();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M8 3L8 13M8 3L4 7M8 3L12 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ) : null}

            <div
              ref={setComposerMenuAnchor}
              data-chat-composer-body="true"
              className={cn(
                "relative px-3 pb-2 sm:px-4",
                "pt-3.5 sm:pt-4",
                isComposerApprovalState && "pb-3 sm:pb-4",
                isComposerCollapsedMobile && "hidden",
                isComposerResting && "py-2 sm:py-2",
              )}
            >
              {isStashMenuOpen && !composerMenuOpen && !isComposerApprovalState && (
                <ComposerCommandMenuLayer anchor={composerMenuAnchor}>
                  <ComposerStashMenu
                    entries={stashQueue}
                    stashShortcutLabel={shortcutLabelForCommand(keybindings, "composer.stash", {
                      context: {
                        terminalFocus: false,
                        terminalOpen,
                        modelPickerOpen: false,
                      },
                    })}
                    onRestore={restoreStashEntry}
                    onDelete={deleteStashEntry}
                    onClose={() => setIsStashMenuOpen(false)}
                  />
                </ComposerCommandMenuLayer>
              )}

              {composerMenuOpen && !isComposerApprovalState && (
                <ComposerCommandMenuLayer anchor={composerMenuAnchor}>
                  <ComposerCommandMenu
                    items={composerMenuItems}
                    resolvedTheme={resolvedTheme}
                    isLoading={isComposerMenuLoading}
                    triggerKind={composerTriggerKind}
                    emptyStateText={composerMenuEmptyState}
                    activeItemId={activeComposerMenuItem?.id ?? null}
                    onHighlightedItemChange={onComposerMenuItemHighlighted}
                    onSelect={onSelectComposerItem}
                  />
                </ComposerCommandMenuLayer>
              )}

              {!isComposerCollapsedMobile &&
                !isComposerApprovalState &&
                (uncommittedSnapShotIds.length > 0 ||
                  composerVideos.length > 0 ||
                  expandedComposerImages.length > 0) && (
                  <div
                    className={cn(
                      "mb-3 flex max-w-full gap-2",
                      pendingSnapShotIds.length > 0 ||
                        expandedComposerImages.some((image) => image.source?.kind === "snap-shot")
                        ? "snap-x snap-proximity overflow-x-auto overscroll-x-contain pb-1 [scrollbar-color:color-mix(in_srgb,var(--contrast-foreground)_18%,transparent)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-3 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-[color-mix(in_srgb,var(--contrast-foreground)_18%,transparent)] [&::-webkit-scrollbar-thumb]:bg-clip-content [&::-webkit-scrollbar-thumb:hover]:bg-[color-mix(in_srgb,var(--contrast-foreground)_28%,transparent)] [&::-webkit-scrollbar-track]:mx-1 [&::-webkit-scrollbar-track]:bg-transparent"
                        : "flex-wrap",
                    )}
                  >
                    {expandedComposerImages
                      .map((image) => {
                        const upload = supportsAttachmentUploads
                          ? uploadsByImageId[image.id]
                          : undefined;
                        const snapShotAnimationPending =
                          image.source?.kind === "snap-shot" && pendingSnapShotIdSet.has(image.id);
                        const snapShotArrival =
                          image.source?.kind === "snap-shot" &&
                          shouldAnimateSnapShotArrival(image.source.capturedAt);
                        return (
                          <SnapShotAttachmentFrame
                            key={image.id}
                            data-chat-composer-expanded-image="true"
                            aria-hidden={snapShotAnimationPending || undefined}
                            inert={snapShotAnimationPending || undefined}
                            arrival={snapShotArrival}
                            animateArrival={
                              settings.snapShotAnimations &&
                              !snapShotAnimationPending &&
                              snapShotArrival
                            }
                            animationId={snapShotAnimationPending ? image.id : undefined}
                            animationSource={snapShotAnimationPending ? image.source : undefined}
                            className={cn(
                              "group/attachment shrink-0 snap-start bg-background",
                              image.source?.kind === "snap-shot"
                                ? SNAP_SHOT_ATTACHMENT_FRAME_CLASS
                                : "relative h-16 w-16 overflow-hidden rounded-lg border border-border/80",
                              snapShotAnimationPending && "invisible",
                            )}
                          >
                            {image.previewUrl ? (
                              <button
                                type="button"
                                className="h-full w-full cursor-zoom-in"
                                aria-label={`Preview ${image.name}`}
                                onClick={() => {
                                  const preview = buildExpandedImagePreview(
                                    composerImages,
                                    image.id,
                                  );
                                  if (!preview) return;
                                  onExpandImage(preview);
                                }}
                              >
                                <ComposerImageThumbnail
                                  file={image.file}
                                  alt={image.name}
                                  className="h-full w-full object-cover"
                                  fallback={
                                    <span className="flex h-full items-center justify-center px-1 text-[10px] text-secondary-label">
                                      {image.name}
                                    </span>
                                  }
                                />
                              </button>
                            ) : (
                              <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-secondary-label">
                                {image.name}
                              </div>
                            )}
                            {image.source?.kind === "snap-shot" ? (
                              <SnapShotAttachmentDetails
                                source={image.source}
                                className={cn(
                                  upload?.status === "uploading" && "bottom-4",
                                  upload?.status === "failed" && "bottom-8",
                                )}
                              />
                            ) : null}
                            {nonPersistedComposerImageIdSet.has(image.id) && (
                              <Tooltip>
                                <TooltipTrigger
                                  render={
                                    <span
                                      role="img"
                                      aria-label="Draft attachment may not persist"
                                      className="absolute left-1 top-1 inline-flex items-center justify-center rounded bg-background/85 p-0.5 text-amber-600"
                                    >
                                      <CircleAlertIcon className="size-3" />
                                    </span>
                                  }
                                />
                                <TooltipPopup
                                  side="top"
                                  className="max-w-64 whitespace-normal leading-tight"
                                >
                                  Draft attachment could not be saved locally and may be lost on
                                  navigation.
                                </TooltipPopup>
                              </Tooltip>
                            )}
                            {upload?.status === "uploading" && (
                              <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-background/85 px-1 text-center text-[10px] text-foreground">
                                {formatAttachmentUploadProgress(upload.progress)}
                              </span>
                            )}
                            {upload?.status === "failed" && (
                              <Tooltip>
                                <TooltipTrigger
                                  render={
                                    <Button
                                      variant="ghost"
                                      size="icon-xs"
                                      className="absolute bottom-1 left-1 bg-background/85 hover:bg-background/95"
                                      onClick={() =>
                                        retryAttachmentUpload({
                                          environmentId,
                                          image,
                                          draftTarget: attachmentDraftTarget,
                                        })
                                      }
                                      aria-label={`Retry upload for ${image.name}`}
                                    />
                                  }
                                >
                                  <RefreshIcon />
                                </TooltipTrigger>
                                <TooltipPopup
                                  side="top"
                                  className="max-w-64 whitespace-normal leading-tight"
                                >
                                  {upload.reason}
                                </TooltipPopup>
                              </Tooltip>
                            )}
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              className={cn(
                                "absolute right-1 top-1 bg-background/80 hover:bg-background/90",
                                image.source?.kind === "snap-shot" &&
                                  "opacity-0 transition-opacity pointer-coarse:opacity-100 focus-visible:opacity-100 group-hover/attachment:opacity-100 group-focus-within/attachment:opacity-100",
                              )}
                              onClick={() => removeComposerImage(image.id)}
                              aria-label={`Remove ${image.name}`}
                            >
                              <XIcon />
                            </Button>
                          </SnapShotAttachmentFrame>
                        );
                      })
                      .concat(
                        uncommittedSnapShotIds.map((captureId) => (
                          <SnapShotAttachmentFrame
                            key={captureId}
                            aria-hidden="true"
                            animationId={captureId}
                            animationSource={
                              pendingSnapShotAnimations.find((capture) => capture.id === captureId)
                                ?.source
                            }
                            className={cn(
                              SNAP_SHOT_ATTACHMENT_FRAME_CLASS,
                              "invisible shrink-0 snap-start bg-background",
                            )}
                          />
                        )),
                      )}
                    {composerVideos.map((file) => {
                      const fileCanUpload =
                        supportsAttachmentUploads &&
                        maxFileAttachmentBytes !== null &&
                        file.sizeBytes <= maxFileAttachmentBytes;
                      const upload = fileCanUpload ? uploadsByImageId[file.id] : undefined;
                      return (
                        <div
                          key={file.id}
                          className="relative h-16 w-16 shrink-0 snap-start overflow-hidden rounded-lg border border-border/80 bg-black"
                        >
                          {/* A hydrated draft can hold a video whose bytes did not
                              survive the reload. There is nothing to play, so the tile
                              shows its needs-reattach state instead of a dead control. */}
                          {file.file === null ? (
                            <span
                              className="flex h-full w-full flex-col items-center justify-center gap-1 px-1 text-center text-[10px] text-white/80"
                              aria-label={`${file.name} needs to be attached again`}
                            >
                              <PlayIcon className="size-4 fill-current opacity-40" />
                              Reattach
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="flex h-full w-full cursor-zoom-in flex-col items-center justify-center gap-1 px-1 text-white"
                              aria-label={`Play ${file.name}`}
                              onClick={() => {
                                const preview = buildExpandedImagePreview([file], file.id);
                                if (preview) onExpandImage(preview);
                              }}
                            >
                              <ComposerVideoThumbnail file={file.file} />
                              <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/10" />
                              <PlayIcon className="relative z-10 size-4 fill-current drop-shadow-md" />
                            </button>
                          )}
                          {upload?.status === "uploading" && (
                            <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-background/85 px-1 text-center text-[10px] text-foreground">
                              {formatAttachmentUploadProgress(upload.progress)}
                            </span>
                          )}
                          {upload?.status === "failed" && (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <Button
                                    variant="ghost"
                                    size="icon-xs"
                                    className="absolute bottom-1 left-1 bg-background/85 hover:bg-background/95"
                                    onClick={() =>
                                      retryAttachmentUpload({
                                        environmentId,
                                        image: file,
                                        draftTarget: attachmentDraftTarget,
                                      })
                                    }
                                    aria-label={`Retry upload for ${file.name}`}
                                  />
                                }
                              >
                                <RefreshIcon />
                              </TooltipTrigger>
                              <TooltipPopup
                                side="top"
                                className="max-w-64 whitespace-normal leading-tight"
                              >
                                {upload.reason}
                              </TooltipPopup>
                            </Tooltip>
                          )}
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            className="absolute right-1 top-1 bg-background/80 hover:bg-background/90"
                            onClick={() => removeComposerFileFromDraft(file.id)}
                            aria-label={`Remove ${file.name}`}
                          >
                            <XIcon />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

              {!isComposerCollapsedMobile &&
                !isComposerApprovalState &&
                composerOtherFiles.length > 0 && (
                  <div className="mb-3 flex flex-col gap-1">
                    {composerOtherFiles.map((file) => {
                      const fileCanUpload =
                        supportsAttachmentUploads &&
                        maxFileAttachmentBytes !== null &&
                        file.sizeBytes <= maxFileAttachmentBytes;
                      const upload = fileCanUpload ? uploadsByImageId[file.id] : undefined;
                      const needsReattach = composerFileNeedsReattach(file);
                      const canReattachFile =
                        fileStagingLimit !== null && file.sizeBytes <= fileStagingLimit;
                      // A picked path is a reference, not a copy: there are no
                      // bytes here to preview and no size to report, so the row
                      // shows where it points instead.
                      const hostPath = file.hostPath;
                      return (
                        <div
                          key={file.id}
                          className="flex min-w-0 items-center gap-2 py-1 text-sm text-foreground"
                        >
                          <PierreEntryIcon
                            pathValue={file.name}
                            kind={file.isFolder === true ? "directory" : "file"}
                            theme={resolvedTheme}
                          />
                          <button
                            type="button"
                            disabled={needsReattach || hostPath !== undefined}
                            className="min-w-0 flex-1 truncate text-left hover:underline focus-visible:outline-2 disabled:hover:no-underline"
                            onClick={() => setPreviewFileId(file.id)}
                            {...(hostPath !== undefined ? { title: hostPath } : {})}
                          >
                            {file.name}
                          </button>
                          <span className="shrink-0 text-xs text-secondary-label">
                            {needsReattach
                              ? canReattachFile
                                ? "Attach again"
                                : "Remove to send"
                              : upload?.status === "uploading"
                                ? formatAttachmentUploadProgress(upload.progress)
                                : composerFileSizeLabel(file)}
                          </span>
                          {!needsReattach && upload?.status === "failed" ? (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <Button
                                    variant="ghost"
                                    size="icon-xs"
                                    onClick={() =>
                                      retryAttachmentUpload({
                                        environmentId,
                                        image: file,
                                        draftTarget: attachmentDraftTarget,
                                      })
                                    }
                                    aria-label={`Retry upload for ${file.name}`}
                                  />
                                }
                              >
                                <RefreshIcon />
                              </TooltipTrigger>
                              <TooltipPopup
                                side="top"
                                className="max-w-64 whitespace-normal leading-tight"
                              >
                                {upload.reason}
                              </TooltipPopup>
                            </Tooltip>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            onClick={() => removeComposerFileFromDraft(file.id)}
                            aria-label={`Remove ${file.name}`}
                          >
                            <XIcon />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

              <div
                className={cn(
                  "relative",
                  isComposerResting && "flex min-w-0 items-center gap-1",
                  isComposerResting &&
                    ((settings.contextWindowMeterEnabled && activeContextWindow) ||
                    reserveContextWindowMeter
                      ? "pr-28"
                      : showComposerAttachAction
                        ? "pr-20"
                        : "pr-12"),
                )}
              >
                {previewFile ? (
                  <Dialog
                    open
                    onOpenChange={(open) => {
                      if (!open) setPreviewFileId(null);
                    }}
                  >
                    <DialogPopup
                      {...composerFloatingLayerProps}
                      className="h-[min(85vh,52rem)] max-w-4xl overflow-hidden"
                      showCloseButton={false}
                    >
                      <DialogTitle className="sr-only">{previewFile.name}</DialogTitle>
                      <AttachmentFilePreview
                        key={previewFile.id}
                        name={previewFile.name}
                        mimeType={previewFile.mimeType ?? "application/octet-stream"}
                        sizeBytes={previewFile.sizeBytes}
                        file={previewFile.file}
                        origin="Draft"
                        {...(previewFile.uploadedAttachmentId && previewFile.uploadEnvironmentId
                          ? {
                              asset: {
                                environmentId: previewFile.uploadEnvironmentId,
                                attachmentId: previewFile.uploadedAttachmentId,
                              },
                            }
                          : {})}
                        onRemove={() => {
                          removeComposerFileFromDraft(previewFile.id);
                          setPreviewFileId(null);
                        }}
                        onClose={() => setPreviewFileId(null)}
                      />
                    </DialogPopup>
                  </Dialog>
                ) : null}
                {brainComposerOwnsAttachments ? (
                  <BrainComposerChipRow
                    label={brainAction === "ingest" ? "Sources to ingest" : "Attachments"}
                    tone="text"
                    chips={[
                      // Gathered material reads in one row, in one dress: an
                      // attached folder is a source exactly as a pasted note
                      // is, so neither is threaded into the prompt.
                      ...composerUnreferencedFiles.map((file) => ({
                        id: file.id,
                        icon: file.isFolder === true ? ("folder" as const) : ("file" as const),
                        label: file.name,
                        kind: composerFileSizeLabel(file),
                        title: file.hostPath ?? file.name,
                        removeLabel: `Remove ${file.name}`,
                        onRemove: () => removeComposerFileFromDraft(file.id),
                      })),
                      ...brainTextSources.map((source, index) => ({
                        id: source.id,
                        icon: "text" as const,
                        label: source.text,
                        kind: "Text",
                        title: source.text,
                        removeLabel: `Remove text source ${index + 1}`,
                        onRemove: () =>
                          setBrainTextSources((current) =>
                            current.filter((candidate) => candidate.id !== source.id),
                          ),
                      })),
                    ]}
                  />
                ) : null}
                {brainAction === "delete" ? (
                  <BrainComposerChipRow
                    label="Raw sources to delete"
                    tone="delete"
                    chips={brainDeleteTargets.map((target) => ({
                      id: target,
                      icon: "folder" as const,
                      label: target,
                      kind: "Raw",
                      title: brainDeleteTargetPath(target),
                      removeLabel: `Keep ${target}`,
                      onRemove: () =>
                        setBrainDeleteTargets((current) =>
                          current.filter((candidate) => candidate !== target),
                        ),
                    }))}
                  />
                ) : null}
                {designOperation !== null && composerDesignSystemMentions.length > 0 ? (
                  <BrainComposerChipRow
                    label="Design Systems"
                    tone="design"
                    chips={composerDesignSystemMentions.map((mention) => ({
                      id: mention.path,
                      icon: "palette" as const,
                      label: mention.name,
                      kind: missingDesignSystemMentionPaths.has(mention.path)
                        ? "Not found"
                        : "Design System",
                      title: missingDesignSystemMentionPaths.has(mention.path)
                        ? `${mention.path} — this Design System is no longer there. Pick another one below.`
                        : mention.path,
                      removeLabel: `Remove Design System ${mention.name}`,
                      onRemove: () =>
                        removeComposerDraftDesignSystemMention(composerDraftTarget, mention.path),
                    }))}
                  />
                ) : null}
                {designOperation !== null &&
                designComposerOperationCollectsEvidenceUrls(designOperation) &&
                designEvidenceUrls.length > 0 ? (
                  <BrainComposerChipRow
                    label="Evidence"
                    tone="text"
                    chips={designEvidenceUrls.map((entry, index) => ({
                      id: entry.id,
                      icon: "link" as const,
                      label: entry.url,
                      kind: "URL",
                      title: entry.url,
                      removeLabel: `Remove evidence URL ${index + 1}`,
                      onRemove: () =>
                        setDesignEvidenceUrls((current) =>
                          current.filter((candidate) => candidate.id !== entry.id),
                        ),
                    }))}
                  />
                ) : null}
                {designOperation === "delete-design-system" ? (
                  <BrainComposerChipRow
                    label="Design targets to delete"
                    tone="delete"
                    chips={designDeleteTargets.map((target) => {
                      const key = designDeleteTargetKey(target);
                      const label = designDeleteTargetLabel(target);
                      return {
                        id: key,
                        icon: "folder" as const,
                        label,
                        kind: target.kind === "artifact-group" ? "Artifact group" : "Design System",
                        title: label,
                        removeLabel: `Keep ${label}`,
                        onRemove: () =>
                          setDesignDeleteTargets((current) =>
                            current.filter((candidate) => designDeleteTargetKey(candidate) !== key),
                          ),
                      };
                    })}
                  />
                ) : null}
                {brainActionDescriptor !== null &&
                !isComposerResting &&
                !isComposerFooterCompact ? (
                  // Narrow, this line is the only redundant one above the input:
                  // Ingest restates it in the placeholder, Lint and Delete
                  // restate it louder in the description block below.
                  <p className="mb-3 text-[12px] text-muted-foreground">
                    {brainActionDescriptor.blurb}
                  </p>
                ) : null}
                {brainAction !== null && brainActionTyping?.kind === "disabled" ? (
                  <ComposerActionDescription
                    description={brainActionTyping.description}
                    icon={composerActionChipModel?.icon ?? null}
                    destructive={brainAction === "delete"}
                  />
                ) : designOperation !== null && designOperationTyping?.kind === "disabled" ? (
                  <ComposerActionDescription
                    description={designOperationTyping.description}
                    icon={composerActionChipModel?.icon ?? null}
                    destructive={designOperation === "delete-design-system"}
                  />
                ) : (
                  <ComposerContextActionsContext value={composerContextActions}>
                    <ComposerPromptEditor
                      editorRef={composerEditorRef}
                      richTextEnabled={settings.composerRichTextEnabled}
                      value={
                        isComposerApprovalState
                          ? ""
                          : activePendingProgress
                            ? activePendingProgress.customAnswer
                            : prompt
                      }
                      cursor={composerCursor}
                      contextRecords={composerContextRecords}
                      buildContextClipboardFragment={buildContextClipboardFragment}
                      importContextFragment={importContextFragment}
                      skills={selectedProviderSkills}
                      containerClassName={cn(isComposerResting && "min-w-0 flex-1")}
                      className={cn(
                        showMobilePendingAnswerActions && "max-sm:pb-11",
                        isComposerResting &&
                          "max-h-8 min-h-8 overflow-hidden whitespace-pre! leading-8",
                        isComposerApprovalState && "min-h-8",
                      )}
                      placeholderClassName={cn(
                        isComposerResting &&
                          "flex items-center overflow-hidden whitespace-nowrap leading-8",
                      )}
                      onChange={onPromptChange}
                      onVisibleSelectionChange={expandComposerForEditorChange}
                      onCommandKeyDown={onComposerCommandKey}
                      onPageScrollKeyDown={onPageScrollKeyDown}
                      onPageScrollKeyUp={onPageScrollKeyUp}
                      onPageScrollRelease={onPageScrollRelease}
                      onCitationSubmitAndSend={submitCitationAndSend}
                      onPaste={onComposerPaste}
                      placeholder={
                        isComposerApprovalState
                          ? "Resolve this approval request to continue"
                          : activePendingProgress
                            ? isChoiceOnlyPendingQuestion
                              ? "Choose an option above"
                              : "Type your own answer, or leave this blank to use the selected option"
                            : showPlanFollowUpPrompt && activeProposedPlan
                              ? "Add feedback to refine the plan, or leave this blank to implement it"
                              : projectSelectionRequired
                                ? "Choose a project above to start a thread"
                                : showProviderUnavailable
                                  ? "Enable a provider in Settings to send a message"
                                  : phase === "disconnected"
                                    ? DISCONNECTED_COMPOSER_PLACEHOLDER
                                    : brainActionTyping?.kind === "enabled"
                                      ? brainActionTyping.placeholder
                                      : (designComposerPlaceholder(designOperation) ??
                                        "Ask anything, @tag files/folders, $use skills, or / for commands")
                      }
                      disabled={
                        isConnecting ||
                        isComposerApprovalState ||
                        projectSelectionRequired ||
                        isChoiceOnlyPendingQuestion ||
                        activePendingIsResponding
                      }
                    />
                  </ComposerContextActionsContext>
                )}
                {isComposerResting ? collapsedComposerImagePreviews : null}
                {showMobilePendingAnswerActions ? (
                  <div
                    data-chat-composer-mobile-pending-actions="true"
                    className="absolute bottom-0 right-0 flex items-center justify-end gap-1"
                  >
                    <ComposerPrimaryActions
                      compact
                      pendingAction={pendingPrimaryAction}
                      isRunning={false}
                      showPlanFollowUpPrompt={false}
                      promptHasText={false}
                      isSendBusy={isSendBusy}
                      sendDisabledReason={sendDisabledReason}
                      isConnecting={isConnecting}
                      isEnvironmentUnavailable={
                        environmentUnavailable !== null ||
                        noProviderAvailable ||
                        projectSelectionRequired
                      }
                      isPreparingWorktree={false}
                      hasSendableContent={false}
                      destructiveSendLabel={null}
                      preserveComposerFocusOnPointerDown
                      onPreviousPendingQuestion={onPreviousActivePendingUserInputQuestion}
                      onInterrupt={handleInterruptPrimaryAction}
                      onImplementPlanInNewThread={handleImplementPlanInNewThreadPrimaryAction}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <ComposerPromptLengthValidation
              message={providerInputSubmissionError ?? composerSubmissionError}
            />

            {/* Bottom toolbar */}
            {isComposerCollapsedMobile || isComposerApprovalState ? null : (
              <div
                data-chat-composer-footer="true"
                data-chat-composer-footer-compact={isComposerFooterCompact ? "true" : "false"}
                className={cn(
                  "flex min-w-0 flex-nowrap items-center justify-between gap-2 overflow-visible px-3 pb-3 sm:px-4 sm:pb-4",
                  pendingUserInputs.length > 0 && "pt-2",
                  isComposerFooterCompact ? "gap-1.5" : "gap-2 sm:gap-0",
                  showMobilePendingAnswerActions && "hidden sm:flex",
                  isComposerResting &&
                    "absolute right-px z-10 h-12 w-auto gap-0 py-0 sm:gap-0 sm:py-0",
                  isComposerResting &&
                    (showInlineRestingControls ? "bottom-[calc(2rem+1px)]" : "bottom-px"),
                )}
              >
                <div
                  ref={composerFooterControlsRef}
                  data-chat-composer-controls="left"
                  data-chat-composer-footer-controls="true"
                  className={cn(
                    "-m-1 -ms-3.5 flex min-w-0 flex-1 items-center gap-1 overflow-x-auto p-1 ps-3.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                    isComposerResting && "hidden",
                  )}
                >
                  {composerControlsInStrip ? null : composerControls}
                </div>

                {/* Right side: send / stop button */}
                <div
                  data-chat-composer-actions="right"
                  data-chat-composer-transition-actions="true"
                  data-chat-composer-primary-actions-compact={
                    isComposerPrimaryActionsCompact ? "true" : "false"
                  }
                  className="flex shrink-0 flex-nowrap items-center justify-end gap-2"
                >
                  {/* The target the operation acts on, next to the material it
                      carries. Renders nothing without a Design Action, in Create,
                      and in an unpicked composer. */}
                  {designOperation === null ? null : (
                    <DesignTargetPicker
                      picker={designTargetPicker}
                      mode={designOperation}
                      targets={designSystemDiscovery.targets}
                      selectedPath={designComposerCollection.target?.path ?? null}
                      isLoading={designSystemDiscovery.isPending}
                      open={isDesignTargetPickerOpen}
                      onOpenChange={setIsDesignTargetPickerOpen}
                      disabled={isConnecting || projectSelectionRequired}
                      onPickTarget={pickDesignSystemTarget}
                      onOpenDeleteTargets={() => setIsDesignDeleteTargetsDialogOpen(true)}
                    />
                  )}
                  {showComposerAttachAction ? (
                    <>
                      <input
                        ref={attachmentInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(event) => {
                          const files = Array.from(event.currentTarget.files ?? []);
                          event.currentTarget.value = "";
                          // Inserting a chip refocuses the editor after the draft renders;
                          // focusing synchronously here would report the editor's stale text
                          // over the prompt that was just written.
                          void attachWithIngestChoice({ kind: "files", files }).then((inserted) => {
                            if (!inserted) focusComposer();
                          });
                        }}
                      />
                      <ComposerActionMenu
                        entries={activePendingProgress === null ? composerActionEntries : []}
                        brainAction={brainAction}
                        designOperation={designOperation}
                        takesAttachments={composerTakesAttachments}
                        disabled={isConnecting || projectSelectionRequired}
                        onPickAction={pickComposerAction}
                        filesPickTakesFolders={composerFilesPickTakesFolders}
                        environmentIsElsewhere={environmentMachine === "elsewhere"}
                        onPickFilesAndFolders={() => void openFilesAndFoldersPicker()}
                        onPickEnvironmentFiles={
                          gitCwd === null
                            ? null
                            : () => {
                                if (!attachingBlockedByPlanQuestions()) {
                                  setIsEnvironmentFilesDialogOpen(true);
                                }
                              }
                        }
                        onPickFolder={
                          composerFolderPickAvailable ? () => void openFolderPicker() : null
                        }
                        onPickRawSources={() => setIsBrainDeleteRawsDialogOpen(true)}
                        onAddText={() => setIsBrainAddTextDialogOpen(true)}
                        onAddEvidenceUrl={() => setIsDesignAddUrlDialogOpen(true)}
                      />
                    </>
                  ) : null}
                  <ComposerFooterPrimaryActions
                    compact={isComposerResting || isComposerPrimaryActionsCompact}
                    activeContextWindow={
                      settings.contextWindowMeterEnabled ? activeContextWindow : null
                    }
                    reserveContextWindowMeter={reserveContextWindowMeter}
                    activeThreadModelDisplayName={activeThreadModelDisplayName}
                    pendingAction={pendingPrimaryAction}
                    isRunning={turnInProgress}
                    showPlanFollowUpPrompt={
                      pendingUserInputs.length === 0 && showPlanFollowUpPrompt
                    }
                    promptHasText={prompt.trim().length > 0}
                    isSendBusy={isSendBusy}
                    sendDisabledReason={sendDisabledReason}
                    isConnecting={isConnecting}
                    isEnvironmentUnavailable={
                      environmentUnavailable !== null ||
                      noProviderAvailable ||
                      projectSelectionRequired
                    }
                    isPreparingWorktree={isPreparingWorktree}
                    destructiveSendLabel={
                      designOperation === "delete-design-system"
                        ? designDeleteSendLabel(designDeleteTargets)
                        : null
                    }
                    hasSendableContent={composerSendState.hasSendableContent}
                    preserveComposerFocusOnPointerDown={isMobileViewport || isComposerResting}
                    onPreviousPendingQuestion={onPreviousActivePendingUserInputQuestion}
                    onInterrupt={handleInterruptPrimaryAction}
                    onImplementPlanInNewThread={handleImplementPlanInNewThreadPrimaryAction}
                    compactDisabled={
                      compactDisabled || noProviderAvailable || isSendBusy || isConnecting
                    }
                    compactDisabledReason={resolvedCompactDisabledReason}
                    {...(compactCommandAvailable ? { onCompactContext: compactThreadContext } : {})}
                  />
                </div>
              </div>
            )}
            {showInlineRestingControls ? (
              <div data-chat-composer-inline-resting-row="true" className="h-8">
                <div
                  ref={setInlineRestingControlsHost}
                  className="absolute inset-x-4 bottom-2 min-w-0"
                >
                  <div
                    ref={restingComposerControlsRef}
                    data-chat-composer-resting-controls="true"
                    aria-hidden={restingControlsVisible ? undefined : true}
                    inert={restingControlsVisible ? undefined : true}
                    className={cn(
                      "relative flex w-max min-w-0 max-w-full items-center gap-1 font-normal text-muted-foreground/70 [&_button]:text-xs!",
                      !restingControlsVisible && "invisible",
                    )}
                  >
                    {composerControls}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </ComposerSurface.Main>
      </div>
    </form>
  );
});
